'use strict';

// external imports
import React, {
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import {keys} from 'ts-transformer-keys';
import {enumerate} from 'ts-transformer-enumerate';
import _ from 'lodash';
import parse from 'html-react-parser';
import omniclone from 'omniclone';

// prime react imports
import {Card} from 'primereact/card';
import {
    TabPanel,
    TabView,
    TabViewTabChangeEvent
} from 'primereact/tabview';

// shared imports
import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import ItemType from '~/shared/src/mm/ItemType';
import PropertySet, {
    isPropertyValueType,
    PropertyValueType
} from '~/shared/src/mm/PropertySet';
import ReferenceSet from '~/shared/src/mm/ReferenceSet';
import ReferencePropertySet from '~/shared/src/mm/ReferencePropertySet';
import Item, {
    ItemAttributes,
    ItemAttributesForCreation,
    ItemAttributesSettable,
    ItemCollection,
    itemOnlyAttributesSettable
} from '~/shared/src/ci/Item';
import Property, {
    propertyAttributesSettable
} from '~/shared/src/ci/Property';
import EnumProperty from '~/shared/src/ci/EnumProperty';
import Reference, {
    isReferenceAttribute,
    ReferenceAttributes,
    ReferenceAttributesForCreation,
    ReferenceCollection,
    referenceOnlyAttributesSettable
} from '~/shared/src/ci/Reference';
import ReferenceProperty, {
    referencePropertyAttributesSettable
} from '~/shared/src/ci/ReferenceProperty';
import EnumReferenceProperty from '~/shared/src/ci/EnumReferenceProperty';
import {
    BaseRequestParameters,
    defaultBaseRequestParameters
} from '~/shared/src/utilities/modules/common';
import {
    DataDescriptor,
    DataDescriptors,
    DataStructure,
    ObjectDataDescriptor,
    ReferenceDataDescriptor,
    buildDataDescriptor,
    extractDataObjectForItem,
    getParentDataObject,
    extractDataObjectsFromDataStructure
} from '~/shared/src/utilities/dataStructure';
import dataUtils, {
    EnumUpdateValue,
    EnumValue,
    ItemData,
    ReferenceData,
    ReferenceValue,
    ResolvedDataObjectChain
} from '~/shared/src/utilities/dataUtils';
import objectUtils from '~/shared/src/utilities/objectUtils';
import {convertValueToString} from '~/shared/src/utilities/valueConverters';
import {
    ContainerCommonInheritedProperties,
    ContainerLayoutStructure,
    ContainerProperties,
    ContainerToContainerInheritedProperties,
    EnumFieldProperties,
    FormFieldData,
    FormFieldInheritedProperties,
    FormFieldLayoutStructureAndDataBinding,
    FormFieldProperties,
    FormInheritedProperties,
    FormLayoutStructure,
    FormProperties,
    LayoutElements,
    LoadConfigResult,
    LoadContainerData,
    LoadContainerProperties,
    LoadDataForDataStructureResult,
    LoadFormData,
    LoadReferenceFieldItemsParameters,
    LoadTabContainerData,
    TabContainerLayoutStructure,
    TabContainerProperties,
    VariableFieldProperties,
    WidthUnit,
    booleanFieldMessages,
    buttonFieldImages,
    buttonFieldMessages,
    calendarViewMessages,
    chartViewMessages,
    commonFormFieldImages,
    commonFormFieldMessages,
    commonFormFieldStyles,
    containerImages,
    containerInheritedProperties,
    containerMessages,
    containerStyles,
    fieldTypeToItemTypeMapping,
    formFieldInheritedProperties,
    formImages,
    formInheritedProperties,
    formMessages,
    graphViewMessages,
    layoutElementTypeToItemTypeMapping,
    mapViewMessages,
    overviewViewMessages,
    passwordFieldMessages,
    referenceFieldMessages,
    staticContentFieldImages,
    staticContentFieldMessages,
    useHookForm,
    variableFieldVariableTypes,
    widthUnitMapping,
    ValueSetFieldProperties
} from '~/shared/src/utilities/modules/form';
import {Condition} from '~/shared/src/utilities/condition';
import {Message} from '~/shared/src/utilities/message';
import {getIconCssClassName} from '~/shared/src/utilities/getCssClassName';
import {dataExtraction} from '~/shared/src/utilities/dataExtraction';
import messageUtils from '~/shared/src/utilities/messageUtils';
import {formatValue} from '~/shared/src/utilities/valueFormaters';

// frontend imports
import {Props as FieldWrapperProps} from '~/frontend/src/components/base/shared/field/FieldWrapper';
import BooleanField from '~/frontend/src/components/base/shared/field/valueTypeFields/BooleanField';
import ButtonField from '~/frontend/src/components/base/shared/field/ButtonField';
import CalendarView from '~/frontend/src/components/base/shared/view/CalendarView';
import ChartView from '~/frontend/src/components/base/shared/view/ChartView';
import ColorField from '~/frontend/src/components/base/shared/field/valueTypeFields/ColorField';
import CoordinateField from '~/frontend/src/components/base/shared/field/valueTypeFields/CoordinateField';
import CurrencyField from '~/frontend/src/components/base/shared/field/valueTypeFields/CurrencyField';
import DateField from '~/frontend/src/components/base/shared/field/valueTypeFields/DateField';
import DoubleField from '~/frontend/src/components/base/shared/field/valueTypeFields/DoubleField';
import DurationField from '~/frontend/src/components/base/shared/field/valueTypeFields/DurationField';
import EmailField from '~/frontend/src/components/base/shared/field/valueTypeFields/EmailField';
import GraphView from '~/frontend/src/components/base/shared/view/GraphView';
import IbanField from '~/frontend/src/components/base/shared/field/valueTypeFields/IbanField';
import ImageDataField from '~/frontend/src/components/base/shared/field/valueTypeFields/ImageDataField';
import IntegerField from '~/frontend/src/components/base/shared/field/valueTypeFields/IntegerField';
import JsonField from '~/frontend/src/components/base/shared/field/valueTypeFields/JsonField';
import LinkField from '~/frontend/src/components/base/shared/field/valueTypeFields/LinkField';
import MapView from '~/frontend/src/components/base/shared/view/MapView';
import OverviewView from '~/frontend/src/components/base/shared/view/OverviewView';
import PasswordField from '~/frontend/src/components/base/shared/field/PasswordField';
import PhoneField from '~/frontend/src/components/base/shared/field/valueTypeFields/PhoneField';
import StaticContentField from '~/frontend/src/components/base/shared/field/StaticContentField';
import StringField from '~/frontend/src/components/base/shared/field/valueTypeFields/StringField';
import TextField from '~/frontend/src/components/base/shared/field/valueTypeFields/TextField';
import TimeField from '~/frontend/src/components/base/shared/field/valueTypeFields/TimeField';
import TimestampField from '~/frontend/src/components/base/shared/field/valueTypeFields/TimestampField';
import ValueSetField, {
    componentProps as valueSetFieldComponentProps,
    ComponentProps as ValueSetFieldComponentProps
} from '~/frontend/src/components/base/shared/field/ValueSetField';
import EnumField, {
    componentProps as enumFieldComponentProps,
    ComponentProps as EnumFieldComponentProps
} from '~/frontend/src/components/base/shared/field/EnumField';
import ReferenceField, {
    componentProps as referenceFieldComponentProps,
    ComponentProps as ReferenceFieldComponentProps
} from '~/frontend/src/components/base/shared/field/ReferenceField';
import FileField, {
    componentProps as fileFieldComponentProps,
    ComponentProps as FileFieldComponentProps
} from '~/frontend/src/components/base/shared/field/FileField';

import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import {
    getIconStringSpan,
    getItemKeyDisplayName,
    getItemNameDisplayName,
    getItemTypeDisplayName,
    getPropertySetDisplayName,
    getReferencePropertySetDisplayName,
    getStatusColorStyleObject,
    nl2br
} from '~/frontend/src/utilities/renderHelpers';
import {
    formatRenderer,
    isInvalidHTML,
    renderValue,
    resolveStyle,
    resolveStyleToClassNames
} from '~/frontend/src/utilities/valueRenderers';
import {fieldUtils} from '~/frontend/src/utilities/fieldUtils';

// action imports
import origSetParam, {
    SetParamAction
} from '~/frontend/src/actions/panel/common/setParam';
import origSetParams, {
    SetParamsAction
} from '~/frontend/src/actions/panel/common/setParams';
import origUnsetParam, {
    UnsetParamAction
} from '~/frontend/src/actions/panel/common/unsetParam';
import {
    setElusiveData as origSetElusiveData,
    SetElusiveDataAction,
    clearElusiveData
} from '~/frontend/src/actions/elusiveData';
import {PanelElusiveDataState} from '~/frontend/src/reducers/elusiveData';
import addItem from '~/frontend/src/actions/data/addItem';
import updateItem from '~/frontend/src/actions/data/updateItem';
import addReference from '~/frontend/src/actions/data/addReference';
import deleteReference from '~/frontend/src/actions/data/deleteReference';
import updateReference from '~/frontend/src/actions/data/updateReference';
import deleteItem from '~/frontend/src/actions/data/deleteItem';
import loadDataForDataStructure from '~/frontend/src/actions/panel/form/loadDataForDataStructure';
import startRequest from '~/frontend/src/actions/ajax/startRequest';
import endRequest from '~/frontend/src/actions/ajax/endRequest';


const debugColorScheme = {
    error            : 'F44336',
    loadFunction     : '009688',
    cacheFunction    : '607D8B',
    mouseFunction    : '0095ce',
    dragNdropFunction: 'E91E63',
    helperFunction   : 'FF5722',
    render           : '1565C0'
};

const enumKeyFieldTypes   = enumerate<Required<Required<EnumFieldProperties>[ 'fields' ]>[ 'key' ][ 'type' ]>() as unknown as string[],
      enumValueFieldTypes = enumerate<Required<Required<EnumFieldProperties>[ 'fields' ]>[ 'value' ][ 'type' ]>() as unknown as string[];


type MetaModelMapping = {
    type : 'item',
    metaModels : number[];
    isSettable : boolean;
} | {
    type : 'property',
    metaModels : number[];
    isSettable : boolean;
} | {
    type : 'enumProperty',
    metaModels : number[];
    isSettable : boolean;
} | {
    type : 'reference',
    metaModels : number[];
    isSettable : boolean;
} | {
    type : 'referenceProperty',
    metaModels : number[];
    isSettable : boolean;
} | {
    type : 'enumReferenceProperty',
    metaModels : number[];
    isSettable : boolean;
} | {
    type : 'static',
    metaModels : number[];
    isSettable : false;
};

type DataDescriptorsToMetaModelMapping = {
    [ dataDescriptorAlias : string ] : MetaModelMapping;
};

type DataMapping = {
    type : 'item',
    object? : number;
    value? : AttributeValue;
    originalValue? : AttributeValue;
    isDefault? : never;
    hasNoParent? : never;
} | {
    type : 'property',
    object? : number;
    value? : AttributeValue;
    originalValue? : AttributeValue;
    isDefault : boolean;
    hasNoParent : boolean;
} | {
    type : 'enumProperty',
    object? : number;
    value? : EnumValue;
    originalValue? : EnumValue;
    isDefault : never;
    hasNoParent : boolean;
} | {
    type : 'reference',
    object? : number | number[];
    value? : ReferenceValue | ReferenceValue[];
    originalValue? : ReferenceValue | ReferenceValue[];
    isDefault? : never;
    hasNoParent? : never;
} | {
    type : 'referenceProperty',
    object? : number;
    value? : AttributeValue;
    originalValue? : AttributeValue;
    isDefault : boolean;
    hasNoParent : boolean;
} | {
    type : 'enumReferenceProperty',
    object? : number;
    value? : EnumValue;
    originalValue? : EnumValue;
    isDefault : never;
    hasNoParent : boolean;
} | {
    type : 'static',
    object? : never;
    value? : any;
    originalValue? : never;
    isDefault? : never;
    hasNoParent? : never;
};

type DataDescriptorsToDataMapping = {
    [ dataDescriptorAlias : string ] : DataMapping;
};

type DataDescriptorsToValueMapping = {
    [ dataDescriptorAlias : string ] : AttributeValue | EnumUpdateValue | ReferenceValue | ReferenceValue[] | undefined;
};

type LayoutElementsToPropMapping = {
    [ layoutElementId : number ] : any;
};

type InheritedPropParent = {
    itemType : string;
    id : number;
    key : string;
    name : string;
};

export type LayoutElementsToInheritedPropsMapping = {
    [ layoutElementId : number ] : {
        [ propertyTypeName : string ] : {
            value : any;
            inheritedFrom : InheritedPropParent;
        }
    }
}

type FieldCache = {
    [ layoutElementId : string ] : {
        cacheParameters : any;
        field : React.ReactNode;
    }
};

export type Mode = 'design' | 'input';

export type DragData = {
    items : ItemCollection;
    references? : ReferenceCollection;
    isRepeatedForDataObjectId? : number;
};

export type DragOptions = {
    label? : string;
    icon? : string;
};

export type ReferenceType = {
    dataStructure? : DataStructure;
    layoutElements? : LayoutElements;
    layoutElementsProps? : LayoutElementsToPropMapping;
    layoutElementsInheritedProps? : LayoutElementsToInheritedPropsMapping;
    hasHiddenLayoutElements : boolean;
    repeatedDataObjects? : number[];
    clearRootItemData : () => void;
    markLayoutElementsForAlias : ( alias : string | undefined ) => void;
    startDrag : ( rootLayoutElementId : number, data : DragData, options : DragOptions, e : React.DragEvent<HTMLElement> ) => void;
};

type InsertPosition = 'before' | 'after' | 'insideBefore' | 'insideAfter';
type DragInfo = {
    dragElement : HTMLDivElement;
    insertMarkerElement : HTMLDivElement;
    dragLayoutElementId : number;
    dragLayoutStructure? : ContainerLayoutStructure | FormFieldLayoutStructureAndDataBinding;
    dragParentLayoutElementId : number;
    layoutStructure? : FormLayoutStructure;
    layoutElements? : LayoutElements;
    dataDescriptors? : DataDescriptors;
    dataDescriptorsMetaModel? : DataDescriptorsToMetaModelMapping;
    lastDragOverLayoutElementId? : number;
    isRepeatedForDataObjectId? : number;
    position? : InsertPosition;
    doNothing : boolean;
};

type NewDragInfo = {
    data : DragData;
    layoutStructure? : ContainerLayoutStructure | TabContainerLayoutStructure | FormFieldLayoutStructureAndDataBinding,
    layoutElements? : LayoutElements;
    dataDescriptors? : DataDescriptors;
};

type GetContainerContent = {
    isDirty : boolean;
    isInvalid : boolean;
    content : React.ReactNode[];
    tabIndexMapping? : {
        [ referenceId : number ] : number;
    };
};

type Variables = {
    [ variable : string ] : any;
};

type Parent = {
    id : number;
    type : 'form' | 'layoutWrapper' | 'card' | 'container' | 'tabContainer';
    gridSize : number;
    dataDescriptorAlias? : string;
    referenceId? : number;
    itemId? : number;
    parentItemId? : number;
    isRepeating? : boolean;
    index? : number;
    hasNoParent? : boolean;
    idSuffix? : string;
};

export type DefaultProps = BaseRequestParameters & {
    /**
     * Additional context to be updated (elusive data)
     */
    additionalElusiveDataContexts? : string[];
    readOnly? : boolean;
    mode? : Mode;
    showHiddenLayoutElements? : boolean;
    scrollTop? : number;
    scrollLeft? : number;
    tabIndexOffset? : number;
    activeTabContainerIndexes? : {
        [ layoutElementId : number | string ] : number;
    };
    tabContainerScrollPositions? : {
        [ layoutElementId : number | string ] : number;
    };
    standalone? : boolean;
    rootItemFormState? : 'CLEAR' | 'DIRTY' | 'ERROR';
};
export const defaultProps : Required<DefaultProps> = {
    ...defaultBaseRequestParameters,

    additionalElusiveDataContexts: [],
    readOnly                     : false,
    mode                         : 'design',
    showHiddenLayoutElements     : false,
    scrollTop                    : 0,
    scrollLeft                   : 0,
    tabIndexOffset               : 1000,
    activeTabContainerIndexes    : {},
    tabContainerScrollPositions  : {},
    standalone                   : true,
    rootItemFormState            : 'CLEAR'
};

export type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The dataContext for the form data to access global.data with.
     */
    dataContext : string;

    /**
     * The id of the root data item of the form.
     */
    rootItemId? : number;

    /**
     * The key of the root data item. If set also rootItemType is required.
     */
    rootItemKey? : string;

    /**
     * The ItemType of the root data item. If set also rootItemKey is required.
     */
    rootItemType? : string;


    /**
     * The itemId of the parent form.
     */
    formParentItemId? : number;

    /**
     * The itemId of the parent in a tree.
     * Only valid if running in a tree context.
     */
    treeParentItemId? : number;

    /**
     * The itemId of the root of a tree.
     * Only valid if running in a tree context.
     */
    treeRootItemId? : number;

    /**
     * Reload function for the tree
     */
    treeReloadData? : () => void;

    /**
     * The tree variables
     */
    treeVariables? : Variables;

    /**
     * Getter for the tree variables
     */
    treeSetVariables? : ( variables : Variables ) => void;

    /**
     * Function invoked if config was successfully loaded.
     *
     * @param {LoadConfigResult} config
     */
    onConfigLoad? : ( config : LoadConfigResult ) => void;

    /**
     * Function invoked if config was successfully loaded.
     *
     * @param {LoadDataResult} config
     */
    onDataLoad? : ( config : LoadDataForDataStructureResult ) => void;

    /*** config data ************************************************************************/
    // load from backend

    /**
     * The key of the CFG:Form item to load the config from.
     */
    viewKey? : string;

    /**
     * A hash of the loaded overview configuration to detect changes.
     */
    configHash? : string;

    // OR provided from outside

    /**
     * The uniqueId of the component which has loaded or builds the config (to track config changes)
     */
    configUid? : string;

    /**
     * The dataContext for the form configuration to access global.data with.
     */
    configDataContext? : string;

    /**
     * The id of the root config item of the form.
     */
    configRootItemId : number;


    /*** design mode only *******************************************************************/

    /**
     * The id of currently selected layout Element.
     */
    selectedLayoutElementId? : number;

    /**
     * Callback if a layout element is selected.
     *
     * @param id
     */
    onSelectLayoutElement? : ( id? : number ) => void;

    /**
     * Callback if a layout element displaying data is hovered.
     *
     * @param alias
     */
    onMarkFieldsForAlias? : ( alias : string | undefined ) => void;


    /*** input mode only ********************************************************************/

};
export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

export type AllProps = Required<DefaultProps> & Props;

export type ElusiveData = PanelElusiveDataState & {
    dataDescriptors? : DataDescriptors;
    dataDescriptorsData? : DataDescriptorsToDataMapping;
    dataDescriptorsEditValue? : DataDescriptorsToValueMapping;
    dataDescriptorsMetaModel? : DataDescriptorsToMetaModelMapping;
    dataDescriptorsValue? : DataDescriptorsToValueMapping;
    dataStructure? : DataStructure;
    fieldCache? : FieldCache;
    layoutElements? : LayoutElements;
    layoutElementsProp? : LayoutElementsToPropMapping;
    layoutElementsInheritedProps? : LayoutElementsToInheritedPropsMapping;
    layoutStructure? : FormLayoutStructure;
    repeatedDataObjects? : number[];
    variables? : Variables;
    variablesEditValues? : Variables;
};


const setParam = origSetParam as <K extends keyof AllProps>( panelKey : string, paramKey : K, paramValue : AllProps[K] ) => SetParamAction;
const unsetParam = origUnsetParam as <K extends keyof AllProps>( panelKey : string, paramKey : K ) => UnsetParamAction;
const setParams = origSetParams as ( panelKey : string, panelProps : Partial<AllProps> ) => SetParamsAction;
const setElusiveData = origSetElusiveData as ( panelKey : string, data : ElusiveData ) => SetElusiveDataAction;


const FormDesigner = React.forwardRef<ReferenceType, AllProps>( ( props : AllProps, forwardedRef ) => {
    const startTime                                         = performance.now(),
          dispatch : AppDispatch                            = useDispatch(),
          {
              activeTabContainerIndexes,
              additionalElusiveDataContexts,
              configHash,
              configRootItemId,
              configUid,
              dataContext,
              formParentItemId,
              mode,
              onConfigLoad,
              onDataLoad,
              onMarkFieldsForAlias,
              onSelectLayoutElement,
              readOnly,
              rootItemFormState,
              rootItemId,
              rootItemKey,
              rootItemType,
              schema,
              scrollLeft,
              scrollTop,
              selectedLayoutElementId,
              showHiddenLayoutElements,
              standalone,
              status,
              tabIndexOffset,
              treeParentItemId,
              treeRootItemId,
              uid,
              viewKey
          }                                                 = props,
          configDataContext                                 = props.configDataContext ?? `${props.dataContext}_metaData`,
          dragInfo                                          = useRef<DragInfo | null>( null ),
          newDragInfo                                       = useRef<NewDragInfo | null>( null ),
          tabIndexStep                                      = 1000,
          user                                              = useSelector( ( state : StoreState ) => state.session?.user ? state.session.user : undefined ),
          messageCatalogTimestamp                           = useSelector( ( state : StoreState ) => state.messagecatalog?.timestamp ? state.messagecatalog.timestamp : 0 ),
          metaModelTimestamp                                = useSelector( ( state : StoreState ) => state.metamodel?.timestamp ? state.metamodel.timestamp : 0 ),
          debugState                                        = useSelector( ( state : StoreState ) => state.debugConfig?.formDesignerDebug ),
          elusiveData                                       = useSelector( ( state : StoreState ) => state.elusiveData?.[ uid ] as ElusiveData | undefined ),
          elusiveDataConfigData                             = useSelector( ( state : StoreState ) => configUid ? state.elusiveData?.[ configUid ]?.data : state.elusiveData?.[ uid ]?.metaData ),
          additionalRenderTime                              = useRef<number>( 0 ),
          currentScrollTop                                  = useRef<number>( 0 ),
          currentScrollLeft                                 = useRef<number>( 0 ),
          scrollIntervallId                                 = useRef<NodeJS.Timeout | undefined>(),
          lastParams                                        = useRef<any>( null ),
          droppedLayoutElementId                            = useRef<number | undefined>( undefined ),
          droppedStyleTimeout                               = useRef<NodeJS.Timeout | undefined>(),
          timeoutId                                         = useRef<NodeJS.Timeout | undefined>(),
          initialRendered                                   = useRef<boolean>( false ),
          isLoading                                         = useRef<boolean>( false ),
          [ hoverLayoutElementId, setHoverLayoutElementId ] = useState<number | undefined>( undefined ),
          [ formGridUnitWidth, setFormGridUnitWidth ]       = useState<number>( 1400 / 96 ),
          [ remWidth, setRemWidth ]                         = useState<number>( 12 ),
          [ aliasToMark, setAliasToMark ]                   = useState<string[] | undefined>( undefined );

    let tabIndex                     = 1,
        dataStructure                = useMemo(
            () => {
                return elusiveData?.dataStructure;
            },
            [
                elusiveData?.dataStructure
            ]
        ),
        layoutStructure              = useMemo(
            () => {
                return elusiveData?.layoutStructure;
            },
            [
                elusiveData?.layoutStructure
            ]
        ),
        layoutElements               = useMemo(
            () => {
                return elusiveData?.layoutElements;
            },
            [
                elusiveData?.layoutElements
            ]
        ),
        dataDescriptors              = useMemo(
            () => {
                return elusiveData?.dataDescriptors;
            },
            [
                elusiveData?.dataDescriptors
            ]
        ),
        dataDescriptorsMetaModel     = useMemo(
            () => {
                return elusiveData?.dataDescriptorsMetaModel;
            },
            [
                elusiveData?.dataDescriptorsMetaModel
            ]
        ),
        dataDescriptorsData          = useMemo(
            () => {
                return elusiveData?.dataDescriptorsData;
            },
            [
                elusiveData?.dataDescriptorsData
            ]
        ),
        dataDescriptorsValue         = useMemo(
            () => {
                return elusiveData?.dataDescriptorsValue;
            },
            [
                elusiveData?.dataDescriptorsValue
            ]
        ),
        layoutElementsProp           = useMemo(
            () => {
                return elusiveData?.layoutElementsProp;
            },
            [
                elusiveData?.layoutElementsProp
            ]
        ),
        layoutElementsInheritedProps = useMemo(
            () => {
                return elusiveData?.layoutElementsInheritedProps;
            },
            [
                elusiveData?.layoutElementsInheritedProps
            ]
        ),
        repeatedDataObjects          = useMemo(
            () => {
                return elusiveData?.repeatedDataObjects;
            },
            [
                elusiveData?.repeatedDataObjects
            ]
        ),
        fieldCache                   = useMemo(
            () => {
                return elusiveData?.fieldCache ?? {};
            },
            [
                elusiveData?.fieldCache
            ]
        );

    const dataDescriptorsEditValue                             = useMemo(
              () => {
                  return elusiveData?.dataDescriptorsEditValue;
              },
              [
                  elusiveData?.dataDescriptorsEditValue
              ]
          ),
          variables                                            = useMemo(
              () => {
                  return elusiveData?.variables ?? {};
              },
              [
                  elusiveData?.variables
              ]
          ),
          variablesEditValues                                  = useMemo(
              () => {
                  return elusiveData?.variablesEditValues;
              },
              [
                  elusiveData?.variablesEditValues
              ]
          ),
          globalConfigDataVersions                             = useMemo(
              () => {
                  return JSON.stringify( elusiveDataConfigData ?? {} );
              },
              [
                  elusiveDataConfigData
              ]
          ),
          globalDataVersions                                   = useMemo(
              () => {
                  return JSON.stringify( elusiveData?.data ?? {} );
              },
              [
                  elusiveData?.data
              ]
          ),
          debug                                                = useMemo(
              () => {
                  return debugState?.showAll ?? debugState?.dedicated?.[ uid ];
              },
              [
                  debugState
              ]
          ),
          formItem                                             = useMemo(
              () => {
                  return global.data[ configDataContext ]?.items[ configRootItemId ];
              },
              [
                  configDataContext,
                  globalConfigDataVersions,
                  configRootItemId
              ]
          ),
          dataSchema                                           = useMemo(
              () => {
                  return ( dataStructure?.item.objectSchema?.toUpperCase() ?? ( formItem ? dataExtraction.extractPropertiesOfItem( formItem ).designSchema?.toUpperCase() : undefined ) ?? 'DATA' ) as Schema;
              },
              [
                  dataStructure,
                  formItem
              ]
          ),
          maxTabIndex                                          = useMemo(
              () => {
                  let retVal            = 0,
                      visibleFieldCount = 0;

                  if ( layoutElements && layoutElementsProp ) {
                      for ( const layoutElementId of Object.keys( layoutElements ) ) {
                          const data = layoutElementsProp[ layoutElementId ];

                          if ( data && data.visibility !== 'hidden' && layoutElements[ layoutElementId ].type !== 'CONTAINER' ) {
                              visibleFieldCount++;
                              retVal = Math.max( visibleFieldCount, data.tabOrder ?? 0, retVal );
                          }
                      }
                  }

                  return retVal;
              },
              [
                  layoutElements,
                  layoutElementsProp
              ]
          ),
          hasHiddenLayoutElements                              = useMemo(
              () => {
                  return layoutElements
                      ? Object.values( layoutElements ).findIndex( layoutElement => layoutElement.type !== 'FORM' && layoutElement.data.visibility === 'hidden' ) !== -1
                      : false;
              },
              [
                  layoutElements
              ]
          ),
          load                                                 = async ( reloadConfig : boolean = false ) => {
              const innerStartTime = debug ? performance.now() : 0;

              try {
                  let result : LoadDataForDataStructureResult | false = false;

                  /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
                  if (
                      ( viewKey || dataStructure )
                      && (
                          rootItemId
                          || ( rootItemKey && rootItemType )
                      )
                  ) {
                      /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */
                      if ( debug ) {
                          console.groupCollapsed( `%c${'%cForm - %cload%c'.asLogLine( 106 )}`, 'font-weight: normal;', 'font-weight: bold;', `color: #${debugColorScheme.loadFunction};`, 'font-weight: normal;' );
                      }

                      isLoading.current = true;

                      dispatch( startRequest(
                          () => {
                          },
                          uid,
                          'processing'
                      ) );

                      if ( viewKey ) {
                          // TODO: implement loading via formKey & configHash ....
                      }
                      else if ( dataStructure ) {
                          result = await dispatch( loadDataForDataStructure(
                              uid,
                              dataContext,
                              dataSchema,
                              status,
                              standalone ? 'override' : 'keepExisting',
                              dataStructure,
                              rootItemId,
                              rootItemType,
                              rootItemKey
                          ) );
                      }

                      if ( result ) {
                          let rootItem : Item | undefined;

                          if ( !rootItemId ) {
                              rootItem = dataUtils.findItem( dataContext, rootItemType ?? '', rootItemKey ?? '' );
                          }
                          else {
                              rootItem = global.data[ dataContext ]?.items?.[ rootItemId ];
                          }

                          if ( rootItem ) {
                              dispatch( setParams( uid, {
                                  rootItemId  : rootItem.id,
                                  rootItemKey : rootItem.key,
                                  rootItemType: rootItem.itemType.name
                              } ) );
                          }

                          if ( viewKey && ( reloadConfig || configHash === undefined ) ) {
                              onConfigLoad?.( result as unknown as LoadConfigResult );
                          }

                          onDataLoad?.( result );

                          dataDescriptorsData = buildDataDescriptorsData( dataStructure, dataDescriptors, dataDescriptorsMetaModel );
                          dataDescriptorsValue = buildDataDescriptorsValue( dataDescriptorsData );
                          const layoutElementsPropAndInheritedProps = buildLayoutElementsPropAndInheritedProps( dataStructure, layoutStructure, layoutElements, dataDescriptors, dataDescriptorsMetaModel, dataDescriptorsData );
                          layoutElementsProp = layoutElementsPropAndInheritedProps.props;
                          layoutElementsInheritedProps = layoutElementsPropAndInheritedProps.inheritedProps;
                      }

                      dispatch( endRequest(
                          () => {
                          },
                          uid,
                          'processing'
                      ) );

                      isLoading.current = false;

                      if ( debug ) {
                          console.log( `Form - %cload end - ${Math.round( 1000 * ( performance.now() - innerStartTime ) ) / 1000} ms%c`.asLogLine( 106 ), `color: #${debugColorScheme.loadFunction}; font-weight: bold;`, 'color: unset; font-weight: unset;' );
                          console.groupEnd();
                      }
                  }
              }
              catch ( e ) {
                  console.error( e );
              }
          },
          extractFormFieldData                                 = ( formField : Item, configDataContextToUse = configDataContext, dataStructureToUse = dataStructure ) : FormFieldData & {
              dataDescriptors : DataDescriptor[]
          } | undefined => {
              let fieldType : FormFieldProperties[ 'type' ] | VariableFieldProperties['type'] | undefined,
                  fieldImages : string[]   = commonFormFieldImages,
                  fieldMessages : string[] = commonFormFieldMessages,
                  // eslint-disable-next-line prefer-const
                  fieldStyles : string[]   = commonFormFieldStyles;

              const fieldProps = dataExtraction.extractPropertiesOfItem( formField );

              switch ( formField.itemType.name ) {
                  case 'BooleanField':
                      fieldType = 'BOOLEAN';
                      fieldMessages = booleanFieldMessages;
                      break;

                  case 'ButtonField':
                      fieldType = 'BUTTON';
                      fieldImages = buttonFieldImages;
                      fieldMessages = buttonFieldMessages;
                      break;

                  case 'CalendarView':
                      fieldType = 'CALENDAR';
                      fieldMessages = calendarViewMessages;
                      break;

                  case 'ChartView':
                      fieldType = 'CHART';
                      fieldMessages = chartViewMessages;
                      break;

                  case 'ColorField':
                      fieldType = 'COLOR';
                      break;

                  case 'CoordinateField':
                      fieldType = 'COORDINATE';
                      break;

                  case 'CurrencyField':
                      fieldType = 'CURRENCY';
                      break;

                  case 'DateField':
                      fieldType = 'DATE';
                      break;

                  case 'DoubleField':
                      fieldType = 'DOUBLE';
                      break;

                  case 'DurationField':
                      fieldType = 'DURATION';
                      break;

                  case 'EmailField':
                      fieldType = 'EMAIL';
                      break;

                  case 'GraphView':
                      fieldType = 'GRAPH';
                      fieldMessages = graphViewMessages;
                      break;

                  case 'IbanField':
                      fieldType = 'IBAN';
                      break;

                  case 'ImageDataField':
                      fieldType = 'IMAGEDATA';
                      break;

                  case 'IntegerField':
                      fieldType = 'INTEGER';
                      break;

                  case 'JsonField':
                      fieldType = 'JSON';
                      break;

                  case 'LinkField':
                      fieldType = 'LINK';
                      break;

                  case 'MapView':
                      fieldType = 'MAP';
                      fieldMessages = mapViewMessages;
                      break;

                  case 'OverviewView':
                      fieldType = 'OVERVIEW';
                      fieldMessages = overviewViewMessages;
                      break;

                  case 'PasswordField':
                      fieldType = 'PASSWORD';
                      fieldMessages = passwordFieldMessages;
                      break;

                  case 'PhoneField':
                      fieldType = 'PHONE';
                      break;

                  case 'StaticContentField':
                      fieldType = 'STATIC';
                      fieldImages = staticContentFieldImages;
                      fieldMessages = staticContentFieldMessages;
                      break;

                  case 'StringField':
                      fieldType = 'STRING';
                      break;

                  case 'TextField':
                      fieldType = 'TEXT';
                      break;

                  case 'TimeField':
                      fieldType = 'TIME';
                      break;

                  case 'TimestampField':
                      fieldType = 'TIMESTAMP';
                      break;

                  case 'ValueSetField':
                      fieldType = 'VALUESET';
                      break;

                  case 'EnumField':
                      fieldType = 'ENUM';
                      break;

                  case 'ReferenceField':
                      fieldType = 'REFERENCE';
                      fieldMessages = referenceFieldMessages;
                      break;

                  case 'FileField':
                      fieldType = 'FILE';
                      break;
              }

              if ( fieldType ) {
                  const fieldData                                       = {
                            ...fieldProps,
                            type      : fieldType,
                            actions   : dataExtraction.extractActions( formField, configDataContextToUse, dataStructureToUse ),
                            format    : dataExtraction.extractFormat( formField, configDataContextToUse ),
                            images    : dataExtraction.extractImages( formField, configDataContextToUse, fieldImages ),
                            messages  : dataExtraction.extractMessages( formField, configDataContextToUse, fieldMessages ),
                            query     : dataExtraction.extractQuery( formField, configDataContextToUse ),
                            validators: dataExtraction.extractValidators( formField, configDataContextToUse, dataStructureToUse )
                        } as FormFieldData[ 'data' ] | VariableFieldProperties,
                        fieldAttributePath                              = fieldType === 'REFERENCE' || fieldType === 'FILE' ? 'reference.id' : fieldData.attributePath,
                        fieldDataObject                                 = extractDataObjectForItem( formField, configDataContextToUse, dataStructureToUse! ),
                        fieldDataDescriptor                             = fieldAttributePath && fieldDataObject ? buildDataDescriptor( { attributePath: fieldAttributePath }, fieldDataObject ) : undefined,
                        fieldDataDescriptors : DataDescriptor[]         = [],
                        fieldAdditionalDataDescriptorAliases : string[] = [];

                  let dataDescriptorAlias : string | undefined;

                  if ( fieldDataDescriptor?.alias ) {
                      dataDescriptorAlias = fieldDataDescriptor.alias;

                      if ( fieldDataDescriptor ) {
                          fieldDataDescriptors.push( fieldDataDescriptor );
                      }
                  }
                  // convert to variable field
                  else if ( variableFieldVariableTypes.includes( fieldType as VariableFieldProperties[ 'variableType' ] ) ) {
                      const matches = fieldAttributePath?.match( /^variables\.(.*)$/ );

                      if ( matches && matches.length > 1 ) {
                          ( fieldData as VariableFieldProperties ).variableType = fieldData.type as VariableFieldProperties[ 'variableType' ];
                          ( fieldData as VariableFieldProperties ).variableName = matches[ 1 ];
                          fieldType = fieldData.type = 'VARIABLE';
                      }
                  }

                  // process autoComplete query
                  if ( ( fieldData.type === 'REFERENCE' || fieldData.type === 'STATIC' ) && fieldData.query ) {
                      delete fieldData.query.query;
                      delete fieldData.query.dataStructure;
                  }

                  // process query or getData action
                  if ( fieldData.type === 'REFERENCE' || fieldData.type === 'STATIC' ) {
                      fieldData.getDataAction = dataExtraction.extractAction( formField, configDataContextToUse, dataStructureToUse );
                  }

                  // get viewKey
                  if ( fieldData.type === 'CALENDAR' || fieldData.type === 'CHART' || fieldData.type === 'GRAPH' || fieldData.type === 'MAP' || fieldData.type === 'OVERVIEW' ) {
                      fieldData.viewKey = dataExtraction.extractReferencedItemKey(
                          formField,
                          configDataContextToUse,
                          [
                              { direction: 'DestToSrc', referenceType: 'belongsTo', targetItemType: 'Calendar' },
                              { direction: 'DestToSrc', referenceType: 'belongsTo', targetItemType: 'Chart' },
                              { direction: 'DestToSrc', referenceType: 'belongsTo', targetItemType: 'Graph' },
                              { direction: 'DestToSrc', referenceType: 'belongsTo', targetItemType: 'Map' },
                              { direction: 'DestToSrc', referenceType: 'belongsTo', targetItemType: 'Overview' }
                          ]
                      ) ?? '';
                  }

                  // get valueSetId
                  if ( ( fieldData.type === 'VALUESET' || ( fieldData as VariableFieldProperties ).variableType === 'VALUESET' ) && ( fieldData as ValueSetFieldProperties ).valueSetKey ) {
                      const parts          = ( fieldData as ValueSetFieldProperties ).valueSetKey!.split( ':' ),
                            valueSetSchema = parts.shift()?.toUpperCase() as Schema,
                            valueSetName   = parts.join( ':' );

                      ( fieldData as ValueSetFieldProperties ).valueSetId = metaModel[ valueSetSchema ].getValueSet( valueSetName )?.id;
                  }

                  fieldData.styles = dataExtraction.extractStyles( formField, configDataContextToUse, dataStructureToUse!, fieldStyles, fieldDataDescriptor );

                  // get dataDescriptors of style conditions
                  if ( fieldData.styles && Object.keys( fieldData.styles ).length ) {
                      const getConditionDataDescriptorRecursive = ( condition : Condition ) => {
                          if ( condition.leftExpressionDataDescriptor?.alias ) {
                              if (
                                  condition.leftExpressionDataDescriptor?.alias !== fieldDataDescriptor?.alias
                                  && !fieldAdditionalDataDescriptorAliases.includes( condition.leftExpressionDataDescriptor.alias )
                              ) {
                                  fieldAdditionalDataDescriptorAliases.push( condition.leftExpressionDataDescriptor.alias );
                              }

                              if ( !fieldDataDescriptors.map( dD => dD.alias ).includes( condition.leftExpressionDataDescriptor.alias ) ) {
                                  fieldDataDescriptors.push( condition.leftExpressionDataDescriptor );
                              }
                          }
                          if ( Array.isArray( condition.leftExpression ) ) {
                              for ( const subCondition of condition.leftExpression ) {
                                  getConditionDataDescriptorRecursive( subCondition );
                              }
                          }

                          if ( condition.rightExpressionDataDescriptor?.alias ) {
                              if (
                                  condition.leftExpressionDataDescriptor?.alias !== fieldDataDescriptor?.alias
                                  && !fieldAdditionalDataDescriptorAliases.includes( condition.rightExpressionDataDescriptor.alias )
                              ) {
                                  fieldAdditionalDataDescriptorAliases.push( condition.rightExpressionDataDescriptor.alias );
                              }

                              if ( !fieldDataDescriptors.map( dD => dD.alias ).includes( condition.rightExpressionDataDescriptor.alias ) ) {
                                  fieldDataDescriptors.push( condition.rightExpressionDataDescriptor );
                              }
                          }
                          if ( Array.isArray( condition.rightExpression ) ) {
                              for ( const subCondition of condition.rightExpression ) {
                                  getConditionDataDescriptorRecursive( subCondition );
                              }
                          }
                      };

                      for ( const style of Object.keys( fieldData.styles ) ) {
                          for ( const subStyle of fieldData.styles[ style ] ?? [] ) {
                              if ( subStyle.condition ) {
                                  getConditionDataDescriptorRecursive( subStyle.condition );
                              }
                          }
                      }
                  }

                  if ( fieldData.type === 'ENUM' ) {
                      for ( const referenceId of formField?.references ?? [] ) {
                          const reference = global.data[ configDataContextToUse ]?.references?.[ referenceId ];

                          if ( reference && reference.destItemId === formField.id && reference.srcItem ) {
                              if ( reference.referenceSet.referenceType.name === 'isKeyFieldFor' ) {
                                  const keyField = extractFormFieldData( reference.srcItem, configDataContextToUse, dataStructureToUse );

                                  if ( enumKeyFieldTypes.includes( keyField?.type as string ) ) {
                                      fieldData.fields = {
                                          ...fieldData.fields,
                                          // keyField.type was checked
                                          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                          // @ts-ignore
                                          key: keyField
                                      };
                                  }
                              }
                              else if ( reference.referenceSet.referenceType.name === 'isValueFieldFor' ) {
                                  const valueField = extractFormFieldData( reference.srcItem, configDataContextToUse, dataStructureToUse );

                                  if ( enumValueFieldTypes.includes( valueField?.type as string ) ) {
                                      fieldData.fields = {
                                          ...fieldData.fields,
                                          // valueField.type was checked
                                          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                          // @ts-ignore
                                          value: valueField
                                      };
                                  }
                              }
                          }
                      }
                  }

                  return {
                      type                           : fieldType,
                      data                           : fieldData,
                      dataDescriptorAlias,
                      additionalDataDescriptorAliases: fieldAdditionalDataDescriptorAliases,
                      permissions                    : dataExtraction.extractPermissions( formField, configDataContextToUse ),
                      dataDescriptors                : fieldDataDescriptors
                  };
              }

              return undefined;
          },
          extractLayoutStructure                               = ( parent : LoadFormData | LoadContainerData | LoadTabContainerData, configDataContextToUse = configDataContext, dataStructureToUse = dataStructure ) => {
              const layoutElementId = parent.data.id,
                    retVal          = {
                        layoutStructure: [] as ( ContainerLayoutStructure | TabContainerLayoutStructure | FormFieldLayoutStructureAndDataBinding )[],
                        layoutElements : {} as LayoutElements,
                        dataDescriptors: {} as DataDescriptors
                    },
                    item            = global.data[ configDataContextToUse ]?.items?.[ layoutElementId ];

              retVal.layoutElements[ layoutElementId ] = parent;

              for ( const referenceId of item?.references ?? [] ) {
                  const reference = global.data[ configDataContextToUse ]?.references?.[ referenceId ];

                  if (
                      reference
                      && !reference.isDeleted()
                      && reference.destItemId === item.id
                      && global.data[ configDataContextToUse ]?.items?.[ reference.srcItemId ]
                  ) {
                      const referenceSrcItem = global.data[ configDataContextToUse ].items[ reference.srcItemId ];

                      if ( ( referenceSrcItem.itemType.name === 'Container' || referenceSrcItem.itemType.name === 'TabContainer' ) && referenceSrcItem.id !== layoutElementId ) {
                          const containerData : LoadContainerData | LoadTabContainerData = referenceSrcItem.itemType.name === 'Container'
                                    ? {
                                        type       : 'CONTAINER',
                                        data       : {
                                            ...dataExtraction.extractPropertiesOfItem( referenceSrcItem ),
                                            images  : dataExtraction.extractImages( referenceSrcItem, configDataContextToUse, containerImages ),
                                            messages: dataExtraction.extractMessages( referenceSrcItem, configDataContextToUse, containerMessages, dataStructure ),
                                            styles  : dataExtraction.extractStyles( referenceSrcItem, configDataContextToUse, dataStructure!, containerStyles )
                                        },
                                        permissions: dataExtraction.extractPermissions( referenceSrcItem, configDataContextToUse )
                                    }
                                    : {
                                        type       : 'TABCONTAINER',
                                        data       : {
                                            ...dataExtraction.extractPropertiesOfItem( referenceSrcItem ),
                                            images  : dataExtraction.extractImages( referenceSrcItem, configDataContextToUse, containerImages ),
                                            messages: dataExtraction.extractMessages( referenceSrcItem, configDataContextToUse, containerMessages, dataStructure ),
                                            styles  : dataExtraction.extractStyles( referenceSrcItem, configDataContextToUse, dataStructure!, containerStyles )
                                        },
                                        permissions: dataExtraction.extractPermissions( referenceSrcItem, configDataContextToUse )
                                    },
                                containerLayoutElementId                                 = containerData.data.id,
                                containerLayoutStructureAndDataDescriptors               = extractLayoutStructure( containerData, configDataContextToUse, dataStructureToUse ),
                                containerDataDescriptorsKeys                             = Object.keys( containerLayoutStructureAndDataDescriptors.dataDescriptors ) as string[];

                          if ( referenceSrcItem.itemType.name === 'Container' ) {
                              const isRepeatedForDataObject = dataStructureToUse ? extractDataObjectForItem( referenceSrcItem, configDataContextToUse, dataStructureToUse, 'isRepeatedFor', 'dest' ) : undefined;

                              if ( isRepeatedForDataObject ) {
                                  const isRepeatedForDataDescriptor = buildDataDescriptor( { attributePath: 'reference.id' }, isRepeatedForDataObject );

                                  if ( isRepeatedForDataDescriptor?.alias ) {
                                      ( containerData as LoadContainerData ).isRepeatedForDataDescriptorAlias = isRepeatedForDataDescriptor.alias;
                                  }
                              }

                              retVal.layoutStructure.push( {
                                  type: 'CONTAINER',

                                  layoutElementId: containerLayoutElementId,
                                  children       : containerLayoutStructureAndDataDescriptors.layoutStructure
                              } );
                          }
                          else {
                              retVal.layoutStructure.push( {
                                  type: 'TABCONTAINER',

                                  layoutElementId: containerLayoutElementId,
                                  children       : containerLayoutStructureAndDataDescriptors.layoutStructure as ContainerLayoutStructure[]
                              } );
                          }
                          retVal.layoutElements = {
                              ...retVal.layoutElements,
                              ...containerLayoutStructureAndDataDescriptors.layoutElements
                          };

                          for ( const dataDescriptorKey of containerDataDescriptorsKeys ) {
                              if ( !retVal.dataDescriptors[ dataDescriptorKey ] ) {
                                  retVal.dataDescriptors[ dataDescriptorKey ] = containerLayoutStructureAndDataDescriptors.dataDescriptors[ dataDescriptorKey ];
                              }
                          }
                      }
                      else if ( parent.type !== 'FORM' ) {
                          const formFieldData = extractFormFieldData( referenceSrcItem, configDataContextToUse, dataStructure );

                          if ( formFieldData ) {
                              const formFieldLayoutElementId = formFieldData.data.id;

                              retVal.layoutStructure.push( {
                                  type: formFieldData.type,

                                  layoutElementId    : formFieldLayoutElementId,
                                  dataDescriptorAlias: formFieldData.dataDescriptorAlias
                              } );

                              retVal.layoutElements[ formFieldLayoutElementId ] = { ...formFieldData, dataDescriptors: undefined } as FormFieldData;

                              for ( const dataDescriptor of formFieldData.dataDescriptors ) {
                                  if ( dataDescriptor.alias && !retVal.dataDescriptors[ dataDescriptor.alias ] ) {
                                      retVal.dataDescriptors[ dataDescriptor.alias ] = dataDescriptor;
                                  }
                              }
                          }
                      }
                  }
              }

              return retVal;
          },
          buildDataStructure                                   = () => {
              let retVal : DataStructure | undefined;

              if ( formItem ) {
                  retVal = dataExtraction.extractDataStructure( formItem, configDataContext );
              }

              return retVal;
          },
          buildLayoutStructureElementsAndDataDescriptors       = ( dataStructureToUse = dataStructure ) => {
              if ( debug ) {
                  console.groupCollapsed( `%c${`%cbuild layout structure and elements%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.cacheFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              const innerStartTime = debug ? performance.now() : 0,
                    retVal : {
                        layoutStructure? : FormLayoutStructure;
                        layoutElements? : LayoutElements;
                        dataDescriptors? : DataDescriptors
                    }              = {};

              if ( formItem && dataStructureToUse ) {
                  const formData : LoadFormData                     = {
                            type       : 'FORM',
                            data       : {
                                ...dataExtraction.extractPropertiesOfItem( formItem ),
                                functions: dataExtraction.extractFunctions( formItem, configDataContext, dataStructureToUse ),
                                images   : dataExtraction.extractImages( formItem, configDataContext, formImages ),
                                messages : dataExtraction.extractMessages( formItem, configDataContext, formMessages, dataStructureToUse )
                            },
                            permissions: dataExtraction.extractPermissions( formItem, configDataContext )
                        },
                        extractedLayoutStructure                    = extractLayoutStructure( formData, undefined, dataStructureToUse ),
                        extractedLayoutStructureDataDescriptorsKeys = Object.keys( extractedLayoutStructure.dataDescriptors ) as string[];

                  retVal.layoutStructure = {
                      type           : 'FORM',
                      layoutElementId: formItem.id,
                      children       : extractedLayoutStructure.layoutStructure as ContainerLayoutStructure[]
                  };

                  retVal.layoutElements = {
                      [ formItem.id ]: formData,
                      ...extractedLayoutStructure.layoutElements
                  };

                  for ( const dataDescriptorKey of extractedLayoutStructureDataDescriptorsKeys ) {
                      if ( !retVal.dataDescriptors ) {
                          retVal.dataDescriptors = {};
                      }
                      if ( retVal.dataDescriptors && !retVal.dataDescriptors[ dataDescriptorKey ] ) {
                          retVal.dataDescriptors![ dataDescriptorKey ] = extractedLayoutStructure.dataDescriptors[ dataDescriptorKey ];
                      }
                  }
              }

              if ( debug ) {
                  console.log( `%c${Math.round( 1000 * ( performance.now() - innerStartTime ) ) / 1000} ms%c`.asLogLine( 100 ), `color: #${debugColorScheme.cacheFunction}; font-weight: bold;`, 'font-weight: unset;' );
                  console.groupEnd();
              }

              return retVal;
          },
          buildDataDescriptorsMetaModel                        = ( dataStructureToUse = dataStructure, dataDescriptorsToUse = dataDescriptors ) : DataDescriptorsToMetaModelMapping => {
              const innerStartTime                             = debug ? performance.now() : 0,
                    retVal : DataDescriptorsToMetaModelMapping = {};

              if ( dataStructureToUse && dataDescriptorsToUse && user ) {
                  const dataDescriptorKeys = Object.keys( dataDescriptorsToUse ) as string[];

                  for ( const dataDescriptorAlias of dataDescriptorKeys ) {
                      const dataDescriptor = dataDescriptorsToUse[ dataDescriptorAlias ];

                      if ( dataDescriptor.type !== 'expression' && dataDescriptor.type !== 'static' ) {
                          const metaModelData = dataUtils.getMetaModelData( dataDescriptor, dataStructureToUse, dataSchema );

                          if ( metaModelData?.length ) {
                              let type : MetaModelMapping[ 'type' ] = dataDescriptor.type,
                                  isSettable : boolean              = false;

                              if ( dataDescriptor.type === 'property' && ( metaModelData[ 0 ].object as PropertySet )?.isEnumerated ) {
                                  type = 'enumProperty';
                              }
                              else if ( dataDescriptor.type === 'referenceProperty' && ( metaModelData[ 0 ].object as ReferencePropertySet )?.isEnumerated ) {
                                  type = 'enumReferenceProperty';
                              }

                              if ( dataDescriptor?.type === 'item' ) {
                                  metaModelData[ 0 ].object = metaModelData[ 0 ].object as ItemType;

                                  isSettable = itemOnlyAttributesSettable.includes( dataDescriptor.attribute )
                                      && !( dataDescriptor.attribute === 'key' && metaModelData[ 0 ].object.isKeyAutogenerated )
                                      && !!user?.hasPermissionOnItemType( 'update', metaModelData[ 0 ].object, dataSchema );
                              }
                              else if ( dataDescriptor?.type === 'property' ) {
                                  metaModelData[ 0 ].object = metaModelData[ 0 ].object as PropertySet;

                                  isSettable = propertyAttributesSettable.includes( dataDescriptor.attribute )
                                      && !!user?.hasPermissionOnPropertySet( 'update', metaModelData[ 0 ].object, dataSchema );
                              }
                              else if ( dataDescriptor?.type === 'reference' ) {
                                  metaModelData[ 0 ].object = metaModelData[ 0 ].object as ReferenceSet;

                                  isSettable = referenceOnlyAttributesSettable.includes( dataDescriptor.attribute )
                                      && !!user?.hasPermissionOnReferenceSet( 'update', metaModelData[ 0 ].object, dataSchema );
                              }
                              else if ( dataDescriptor?.type === 'referenceProperty' ) {
                                  metaModelData[ 0 ].object = metaModelData[ 0 ].object as ReferencePropertySet;

                                  isSettable = referencePropertyAttributesSettable.includes( dataDescriptor.attribute )
                                      && !!user?.hasPermissionOnReferencePropertySet( 'update', metaModelData[ 0 ].object, dataSchema );
                              }

                              // type static is always excluded by if condition
                              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                              // @ts-ignore
                              retVal[ dataDescriptorAlias ] = {
                                  type,
                                  metaModels: ( metaModelData ? metaModelData.map( m => m.object.id ) : [] ) as number[],
                                  isSettable
                              };
                          }
                      }
                  }
              }

              if ( debug ) {
                  console.log( `%cbuild dataDescriptorsMetaModel - ${Math.round( 1000 * ( performance.now() - innerStartTime ) ) / 1000} ms%c`.asLogLine( 104 ), `color: #${debugColorScheme.cacheFunction}; font-weight: bold;`, 'font-weight: unset;' );
              }

              return retVal;
          },
          buildDataDescriptorsData                             = ( dataStructureToUse = dataStructure, dataDescriptorsToUse = dataDescriptors, dataDescriptorsMetaModelToUse = dataDescriptorsMetaModel ) : DataDescriptorsToDataMapping => {
              const innerStartTime                        = debug ? performance.now() : 0,
                    retVal : DataDescriptorsToDataMapping = {};

              if ( rootItemId && dataStructureToUse && dataDescriptorsToUse && dataDescriptorsMetaModelToUse ) {
                  const dataDescriptorKeys = Object.keys( dataDescriptorsToUse ) as string[];

                  for ( const dataDescriptorAlias of dataDescriptorKeys ) {
                      const dataDescriptorType = dataDescriptorsToUse[ dataDescriptorAlias ].type;

                      if ( dataDescriptorType !== 'expression' && dataDescriptorType !== 'static' ) {
                          const dataDescriptor = dataDescriptorsToUse[ dataDescriptorAlias ] as ObjectDataDescriptor;

                          if ( dataDescriptorType === 'item' || dataDescriptorType === 'reference' ) {
                              retVal[ dataDescriptorAlias ] = {
                                  type: dataDescriptorType
                              };
                          }
                          else {
                              retVal[ dataDescriptorAlias ] = {
                                  type       : dataDescriptorType,
                                  isDefault  : false,
                                  hasNoParent: true
                              };
                          }

                          const data = dataUtils.getData( dataDescriptor, dataStructureToUse, dataContext, rootItemId );

                          // extract values and original values (no originalValues for references!)
                          if ( data?.length ) {
                              if ( data[ 0 ].type === 'property' ) {
                                  const propertyData = data[ 0 ],
                                        property     = propertyData.object;

                                  retVal[ dataDescriptorAlias ].object = ( property.parent as Item ).id;
                                  retVal[ dataDescriptorAlias ].hasNoParent = false;

                                  if ( property.isDeleted() && property.propertySet.valueDefault !== undefined && property.propertySet.valueDefault !== '' ) {
                                      retVal[ dataDescriptorAlias ].value = property.propertySet.valueDefault;
                                      retVal[ dataDescriptorAlias ].isDefault = true;
                                  }
                                  else if ( property.isDeleted() ) {
                                      retVal[ dataDescriptorAlias ].value = undefined;
                                      retVal[ dataDescriptorAlias ].isDefault = false;
                                  }
                                  else {
                                      retVal[ dataDescriptorAlias ].value = dataUtils.getValueFromData( dataDescriptor, propertyData ) as AttributeValue | undefined;
                                      retVal[ dataDescriptorAlias ].isDefault = false;
                                  }

                                  if (
                                      (
                                          !property.isDeleted()
                                          && !property.attributesModified.has( 'statusId' )
                                      )
                                      || (
                                          property.attributesModified.has( 'statusId' )
                                          && !metaModel[ schema ].getDeletedStatus().map( s => s.id ).includes( property.attributesModified.get( 'statusId' )! as number )
                                      )
                                  ) {
                                      retVal[ dataDescriptorAlias ].originalValue = dataUtils.getOriginalValueFromData( dataDescriptor, propertyData ) as AttributeValue | undefined;
                                  }
                              }

                              else if ( data[ 0 ].type === 'enumProperty' ) {
                                  const enumPropertyData                = data[ 0 ],
                                        enumProperty                    = enumPropertyData.object,
                                        propertyKeys : AttributeValue[] = [],
                                        value                           = dataUtils.getValueFromData( dataDescriptor, enumPropertyData ) as EnumValue | undefined,
                                        originalValue                   = dataUtils.getOriginalValueFromData( dataDescriptor, enumPropertyData ) as EnumValue | undefined;

                                  retVal[ dataDescriptorAlias ].type = 'enumProperty';
                                  retVal[ dataDescriptorAlias ].object = ( enumProperty.parent as Item ).id;
                                  retVal[ dataDescriptorAlias ].hasNoParent = false;

                                  // filter value by keys of non deleted properties
                                  if ( value ) {
                                      // get only keys of non deleted properties
                                      for ( const property of enumProperty.properties.values() ) {
                                          if ( !property.isDeleted() ) {
                                              propertyKeys.push( property.stringKey );
                                          }
                                      }

                                      for ( const key of Object.keys( value ) ) {
                                          if ( !propertyKeys.includes( key ) ) {
                                              delete value[ key ];
                                          }
                                      }

                                      retVal[ dataDescriptorAlias ].value = value;
                                  }
                                  else {
                                      retVal[ dataDescriptorAlias ].value = undefined;
                                  }

                                  retVal[ dataDescriptorAlias ].originalValue = originalValue;
                              }

                              else if ( data[ 0 ].type === 'referenceProperty' ) {
                                  const propertyData = data[ 0 ],
                                        property     = propertyData.object;

                                  retVal[ dataDescriptorAlias ].object = ( property.parent as Reference ).id;
                                  retVal[ dataDescriptorAlias ].hasNoParent = false;

                                  if ( property.isDeleted() && property.referencePropertySet.valueDefault !== undefined && property.referencePropertySet.valueDefault !== '' ) {
                                      retVal[ dataDescriptorAlias ].value = property.referencePropertySet.valueDefault;
                                      retVal[ dataDescriptorAlias ].isDefault = true;
                                  }
                                  else if ( property.isDeleted() ) {
                                      retVal[ dataDescriptorAlias ].value = undefined;
                                      retVal[ dataDescriptorAlias ].isDefault = false;
                                  }
                                  else {
                                      retVal[ dataDescriptorAlias ].value = dataUtils.getValueFromData( dataDescriptor, propertyData ) as AttributeValue | undefined;
                                      retVal[ dataDescriptorAlias ].isDefault = false;
                                  }

                                  if (
                                      (
                                          !property.isDeleted()
                                          && !property.attributesModified.has( 'statusId' )
                                      )
                                      || (
                                          property.attributesModified.has( 'statusId' )
                                          && !metaModel[ schema ].getDeletedStatus().map( s => s.id ).includes( property.attributesModified.get( 'statusId' )! as number )
                                      )
                                  ) {
                                      retVal[ dataDescriptorAlias ].originalValue = dataUtils.getOriginalValueFromData( dataDescriptor, propertyData ) as AttributeValue | undefined;
                                  }
                              }

                              else if ( data[ 0 ].type === 'enumReferenceProperty' ) {
                                  const enumPropertyData                = data[ 0 ],
                                        enumProperty                    = enumPropertyData.object,
                                        propertyKeys : AttributeValue[] = [],
                                        value                           = dataUtils.getValueFromData( dataDescriptor, enumPropertyData ) as EnumValue | undefined,
                                        originalValue                   = dataUtils.getOriginalValueFromData( dataDescriptor, enumPropertyData ) as EnumValue | undefined;

                                  retVal[ dataDescriptorAlias ].type = 'enumReferenceProperty';
                                  retVal[ dataDescriptorAlias ].object = ( enumProperty.parent as Reference ).id;
                                  retVal[ dataDescriptorAlias ].hasNoParent = false;

                                  // filter value by keys of non deleted properties
                                  if ( value ) {
                                      // get only keys of non deleted properties
                                      for ( const property of enumProperty.properties.values() ) {
                                          if ( !property.isDeleted() ) {
                                              propertyKeys.push( property.stringKey );
                                          }
                                      }

                                      for ( const key of Object.keys( value ) ) {
                                          if ( !propertyKeys.includes( key ) ) {
                                              delete value[ key ];
                                          }
                                      }

                                      retVal[ dataDescriptorAlias ].value = value;
                                  }
                                  else {
                                      retVal[ dataDescriptorAlias ].value = undefined;
                                  }

                                  retVal[ dataDescriptorAlias ].originalValue = originalValue;
                              }

                              else if ( data[ 0 ].type === 'reference' ) {
                                  for ( const d of data as ReferenceData[] ) {
                                      const objectId = ( d.object as Reference ).id,
                                            value    = dataUtils.getValueFromData( dataDescriptor, d ) as ReferenceValue | undefined;

                                      if ( objectId ) {
                                          if ( retVal[ dataDescriptorAlias ].object ) {
                                              if ( !Array.isArray( retVal[ dataDescriptorAlias ].object ) ) {
                                                  retVal[ dataDescriptorAlias ].object = [ retVal[ dataDescriptorAlias ].object as number ];
                                              }
                                              ( retVal[ dataDescriptorAlias ].object as number[] ).push( objectId );
                                          }
                                          else {
                                              retVal[ dataDescriptorAlias ].object = objectId;
                                          }
                                      }

                                      if ( value ) {
                                          if ( retVal[ dataDescriptorAlias ].value ) {
                                              if ( !Array.isArray( retVal[ dataDescriptorAlias ].value ) ) {
                                                  retVal[ dataDescriptorAlias ].value = [ retVal[ dataDescriptorAlias ].value ];
                                              }
                                              retVal[ dataDescriptorAlias ].value.push( value );
                                          }
                                          else {
                                              retVal[ dataDescriptorAlias ].value = value;
                                          }
                                      }
                                  }
                              }

                              // item
                              else if ( data[ 0 ].type === 'item' ) {
                                  const itemData      = data[ 0 ] as ItemData,
                                        item          = itemData.object,
                                        value         = dataUtils.getValueFromData( dataDescriptor, itemData ) as AttributeValue | undefined,
                                        originalValue = dataUtils.getOriginalValueFromData( dataDescriptor, itemData ) as AttributeValue | undefined;

                                  retVal[ dataDescriptorAlias ].object = item.id;
                                  retVal[ dataDescriptorAlias ].value = value;
                                  retVal[ dataDescriptorAlias ].originalValue = originalValue;
                              }
                          }

                          // no data - get default for property or referenceProperty
                          else if (
                              retVal[ dataDescriptorAlias ].type === 'property'
                              || retVal[ dataDescriptorAlias ].type === 'enumProperty'
                              || retVal[ dataDescriptorAlias ].type === 'referenceProperty'
                              || retVal[ dataDescriptorAlias ].type === 'enumReferenceProperty'
                          ) {
                              if ( retVal[ dataDescriptorAlias ].type === 'property' || retVal[ dataDescriptorAlias ].type === 'referenceProperty' ) {
                                  const propertySet = retVal[ dataDescriptorAlias ].type === 'property'
                                      ? metaModel[ schema ].getPropertySet( dataDescriptorsMetaModelToUse[ dataDescriptorAlias ]?.metaModels[ 0 ] ?? 0 )
                                      : metaModel[ schema ].getReferencePropertySet( dataDescriptorsMetaModelToUse[ dataDescriptorAlias ]?.metaModels[ 0 ] ?? 0 );

                                  if ( propertySet?.valueDefault !== undefined && propertySet?.valueDefault !== '' ) {
                                      retVal[ dataDescriptorAlias ].isDefault = true;
                                      retVal[ dataDescriptorAlias ].value = propertySet.valueDefault;
                                      retVal[ dataDescriptorAlias ].originalValue = undefined;
                                  }
                              }

                              if ( retVal[ dataDescriptorAlias ].type === 'property' || retVal[ dataDescriptorAlias ].type === 'enumProperty' ) {
                                  const item = dataUtils.getData(
                                      buildDataDescriptor( { attributePath: 'item.id' }, dataDescriptor.dataObject ) as ObjectDataDescriptor,
                                      dataStructureToUse,
                                      dataContext,
                                      rootItemId
                                  )?.[ 0 ].object as Item;

                                  retVal[ dataDescriptorAlias ].hasNoParent = !item;
                              }
                              else {
                                  const reference = dataUtils.getData(
                                      buildDataDescriptor( { attributePath: 'reference.id' }, dataDescriptor.dataObject ) as ObjectDataDescriptor,
                                      dataStructureToUse,
                                      dataContext,
                                      rootItemId
                                  )?.[ 0 ].object as Reference;

                                  retVal[ dataDescriptorAlias ].hasNoParent = !reference;
                              }
                          }

                          // get originalValues for references
                          if ( retVal[ dataDescriptorAlias ].type === 'reference' ) {
                              // resolve data with deleted items and references to resolve original paths (and references therefor)
                              const originalValueData = dataUtils.getData( dataDescriptor, dataStructureToUse, dataContext, rootItemId, true );

                              if ( originalValueData?.length ) {
                                  for ( const d of originalValueData ) {
                                      if ( d.type === 'reference' ) {
                                          const originalValue = dataUtils.getOriginalValueFromData( dataDescriptor, d ) as ReferenceValue;

                                          if ( originalValue?.id > 0 ) {
                                              if ( retVal[ dataDescriptorAlias ].originalValue ) {
                                                  if ( !Array.isArray( retVal[ dataDescriptorAlias ].originalValue ) ) {
                                                      retVal[ dataDescriptorAlias ].originalValue = [ retVal[ dataDescriptorAlias ].originalValue as ReferenceValue ];
                                                  }
                                                  ( retVal[ dataDescriptorAlias ].originalValue as ReferenceValue[] ).push( originalValue );
                                              }
                                              else {
                                                  retVal[ dataDescriptorAlias ].originalValue = originalValue;
                                              }
                                          }
                                      }
                                  }
                              }
                          }
                      }
                  }
              }

              if ( debug ) {
                  console.log( `%cbuild dataDescriptorsData - ${Math.round( 1000 * ( performance.now() - innerStartTime ) ) / 1000} ms%c`.asLogLine( 104 ), `color: #${debugColorScheme.cacheFunction}; font-weight: bold;`, 'font-weight: unset;' );
              }

              return retVal;
          },
          buildDataDescriptorsValue                            = ( dataDescriptorsDataToUse = dataDescriptorsData, dataDescriptorsEditValueToUse = dataDescriptorsEditValue ) => {
              const retVal : DataDescriptorsToValueMapping = {},
                    dataDescriptorsAlias                   = Object.keys( dataDescriptorsDataToUse ?? {} );

              for ( const dataDescriptorAlias of dataDescriptorsAlias ) {
                  const dataDescriptorData = dataDescriptorsDataToUse?.[ dataDescriptorAlias ];

                  if ( dataDescriptorData ) {
                      const dataDescriptorEditValue = dataDescriptorsEditValueToUse?.[ dataDescriptorAlias ];

                      // value
                      // special editValue handling for enums (merge editValues over values)
                      if ( dataDescriptorEditValue && ( dataDescriptorData.type === 'enumProperty' || dataDescriptorData.type === 'enumReferenceProperty' ) ) {
                          if ( dataDescriptorData.value ) {
                              retVal[ dataDescriptorAlias ] = { ...dataDescriptorData.value } as EnumUpdateValue;
                          }
                          else {
                              retVal[ dataDescriptorAlias ] = {} as EnumUpdateValue;
                          }

                          for ( const stringKey of Object.keys( dataDescriptorsEditValueToUse ) as string[] ) {
                              ( retVal[ dataDescriptorAlias ] as EnumUpdateValue )[ stringKey ] = {
                                  ...( ( retVal[ dataDescriptorAlias ] as EnumUpdateValue )[ stringKey ] ?? {} ),
                                  ...( dataDescriptorEditValue as EnumUpdateValue )[ stringKey ]
                              }
                          }
                      }
                      else {
                          retVal[ dataDescriptorAlias ] = dataDescriptorEditValue ?? dataDescriptorData?.value;
                      }
                  }
              }

              return retVal;
          },
          buildLayoutElementsPropAndInheritedProps             = ( dataStructureToUse = dataStructure, layoutStructureToUse = layoutStructure, layoutElementsToUse = layoutElements, dataDescriptorsToUse = dataDescriptors, dataDescriptorsMetaModelToUse = dataDescriptorsMetaModel, dataDescriptorsDataToUse = dataDescriptorsData ) : {
              props : LayoutElementsToPropMapping;
              inheritedProps : LayoutElementsToInheritedPropsMapping;
              repeatedDataObjects : number[];
          } => {
              if ( debug ) {
                  console.groupCollapsed( `%c${'%cbuild layoutElementsProp%c'.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.cacheFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              const innerStartTime                                                = debug ? performance.now() : 0,
                    propsMapping : LayoutElementsToPropMapping                    = {},
                    inheritedPropsMapping : LayoutElementsToInheritedPropsMapping = {},
                    repeatedDataObjectsMapping : number[]                         = [],
                    formLayoutElementId                                           = layoutStructureToUse?.layoutElementId;

              if ( dataStructureToUse && layoutStructureToUse && layoutElementsToUse && formLayoutElementId ) {
                  const layoutElementKeys = Object.keys( layoutElementsToUse ).map( key => Number( key ) ) as number[];

                  // clone layoutElements data
                  for ( const layoutElementId of layoutElementKeys ) {
                      propsMapping[ layoutElementId ] = omniclone( layoutElementsToUse[ layoutElementId ].data );
                  }

                  propsMapping[ formLayoutElementId ] = {
                      ...dataExtraction.getDefaultsForItemType( 'CFG', 'Form' ) as RequiredOf<FormProperties>,
                      ...propsMapping[ formLayoutElementId ]
                  };
                  propsMapping[ formLayoutElementId ].elementStyle = {
                      minWidth: `calc( ${propsMapping[ formLayoutElementId ].minWidth}${widthUnitMapping[ propsMapping[ formLayoutElementId ].minWidthUnit as WidthUnit ]} + 1rem )`, // p-card-body padding-left: 0.5rem + p-card-body padding-right: 0.5rem
                      maxWidth: `calc( ${propsMapping[ formLayoutElementId ].maxWidth}${widthUnitMapping[ propsMapping[ formLayoutElementId ].maxWidthUnit as WidthUnit ]} + 1rem )`, // p-card-body padding-left: 0.5rem + p-card-body padding-right: 0.5rem
                      ...propsMapping[ formLayoutElementId ].elementStyle
                  };

                  // layoutStructure
                  const applyInheritanceAndProcessingRecursive = ( parentLayoutElementId : number, containerChildren : ( FormFieldLayoutStructureAndDataBinding | ContainerLayoutStructure | TabContainerLayoutStructure )[], inheritedProps : FormInheritedProperties | ( FormInheritedProperties & ContainerCommonInheritedProperties & ContainerToContainerInheritedProperties ) ) => {
                      const parentLayoutElement                     = layoutElementsToUse![ parentLayoutElementId ],
                            inheritanceParent : InheritedPropParent = {
                                itemType: parentLayoutElement.type === 'FORM'
                                    ? 'Form'
                                    : (
                                        parentLayoutElement.type === 'CONTAINER'
                                            ? 'Container'
                                            : 'TabContainer'
                                    ),
                                id      : parentLayoutElementId,
                                key     : parentLayoutElement.data.key,
                                name    : parentLayoutElement.data.name
                            };

                      for ( const layoutElementStructure of containerChildren ) {
                          const layoutElementId = layoutElementStructure.layoutElementId;

                          if ( layoutElementStructure.type === 'CONTAINER' ) {
                              const layoutElementInheritedProps = _.pick( inheritedProps, containerInheritedProperties );

                              for ( const inheritedProp of Object.keys( layoutElementInheritedProps ) ) {
                                  if ( propsMapping[ layoutElementId ]?.[ inheritedProp ] === undefined ) {
                                      if ( !inheritedPropsMapping[ layoutElementId ] ) {
                                          inheritedPropsMapping[ layoutElementId ] = {};
                                      }

                                      if ( inheritedPropsMapping[ parentLayoutElementId ]?.[ inheritedProp ] !== undefined ) {
                                          inheritedPropsMapping[ layoutElementId ][ inheritedProp ] = inheritedPropsMapping[ parentLayoutElementId ][ inheritedProp ];
                                      }
                                      else {
                                          inheritedPropsMapping[ layoutElementId ][ inheritedProp ] = {
                                              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                              // @ts-expect-error
                                              value        : layoutElementInheritedProps[ inheritedProp ],
                                              inheritedFrom: inheritanceParent
                                          }
                                      }
                                  }
                              }

                              // inheritance
                              propsMapping[ layoutElementId ] = {
                                  ...dataExtraction.getDefaultsForItemType( 'CFG', 'Container' ) as RequiredOf<ContainerProperties>,
                                  ...layoutElementInheritedProps,
                                  ...propsMapping[ layoutElementId ],

                                  key: `${uid}_container_${layoutElementId}`
                              } as ContainerProperties;

                              const containerLayoutElement = layoutElementsToUse![ layoutElementId ] as LoadContainerData;

                              if ( containerLayoutElement.isRepeatedForDataDescriptorAlias ) {
                                  const dataDescriptorAlias = containerLayoutElement.isRepeatedForDataDescriptorAlias ?? '',
                                        dataDescriptor      = dataDescriptorsToUse?.[ dataDescriptorAlias ];

                                  if ( dataDescriptor?.dataObject ) {
                                      repeatedDataObjectsMapping.push( dataDescriptor.dataObject.origId );
                                  }

                                  // TODO: implement repeated container
                                  /*const dataAndMetaModel = dataDescriptorsMetaModelToUse[ dataDescriptorAlias ] ?? {},
                                        referenceSet     = dataAndMetaModel?.type === 'reference' ? metaModel[ schema ].getReferenceSet( dataAndMetaModel.metaModels[ 0 ] ?? 0 ) : undefined;

                                  if ( referenceSet && dataDescriptor.type === 'reference' ) {
                                      let referenceDirection                  = Object.values( dataDescriptor.dataObject.reference?.referenceConfiguration ?? {} )?.[ 0 ].referenceDirection,
                                          referenceRootItemId : number        = 0,
                                          newReferenceId : number | undefined = undefined;

                                      if ( referenceDirection ) {
                                          // reverse the referenceDirection from dataObject since it is seen from the referenced items dataObject to parent items dataObject (e.g. rootItem)
                                          if ( referenceDirection === 'SrcToDest' ) {
                                              referenceDirection = 'DestToSrc';
                                          }
                                          else {
                                              referenceDirection = 'SrcToDest';
                                          }
                                      }
                                      else if ( rootItemId && dataAndMetaModel.type === 'reference' ) {
                                          const item = dataUtils.getData(
                                              buildDataDescriptor( { attributePath: 'item.id' }, dataDescriptor.dataObject ) as ObjectDataDescriptor,
                                              dataStructure,
                                              dataContext,
                                              rootItemId
                                          )?.[ 0 ].object as Item;

                                          if ( referenceSet.srcItemTypeId === item.itemTypeId ) {
                                              referenceDirection = 'DestToSrc';
                                          }
                                          else {
                                              referenceDirection = 'SrcToDest';
                                          }
                                      }

                                      if ( rootItemId ) {
                                          const parentDataObject = getParentDataObject( dataDescriptor.dataObject.id, dataStructure ),
                                                item             = parentDataObject
                                                    ? dataUtils.getData(
                                                        buildDataDescriptor( { attributePath: 'item.id' }, parentDataObject ) as ObjectDataDescriptor,
                                                        dataStructure,
                                                        dataContext,
                                                        rootItemId
                                                    )?.[ 0 ].object as Item
                                                    : undefined;

                                          if ( item ) {
                                              referenceRootItemId = item.id;
                                          }
                                      }

                                      if ( dataAndMetaModel.type === 'reference' && dataAndMetaModel.objects && currentLayoutElementMappings?.cache?.[ layoutElementId ]?.repeatConfig?.referenceIds?.length ) {
                                          for ( const referenceId of dataAndMetaModel.objects ) {
                                              if ( !currentLayoutElementMappings.cache[ layoutElementId ].repeatConfig.referenceIds.includes( referenceId ) ) {
                                                  newReferenceId = referenceId;
                                                  break;
                                              }
                                          }
                                      }

                                      retVal[ layoutElementId ].repeatConfig = {
                                          dataDescriptorAlias,
                                          rootItemId        : referenceRootItemId,
                                          referenceSetId    : referenceSet.id,
                                          referenceDirection: referenceDirection,
                                          referenceIds      : dataAndMetaModel.objects,
                                          newReferenceId
                                      };
                                  }
                                  else {
                                      // TODO: how to handle error?
                                  }*/
                              }

                              if ( layoutElementStructure.children?.length ) {
                                  applyInheritanceAndProcessingRecursive( layoutElementId, layoutElementStructure.children, _.pick( propsMapping[ layoutElementId ], formFieldInheritedProperties ) as FormFieldInheritedProperties );
                              }
                          }
                          else if ( layoutElementStructure.type === 'TABCONTAINER' ) {
                              const layoutElementInheritedProps = _.pick( inheritedProps, containerInheritedProperties );

                              for ( const inheritedProp of Object.keys( layoutElementInheritedProps ) ) {
                                  if ( propsMapping[ layoutElementId ]?.[ inheritedProp ] === undefined ) {
                                      if ( !inheritedPropsMapping[ layoutElementId ] ) {
                                          inheritedPropsMapping[ layoutElementId ] = {};
                                      }

                                      if ( inheritedPropsMapping[ parentLayoutElementId ]?.[ inheritedProp ] !== undefined ) {
                                          inheritedPropsMapping[ layoutElementId ][ inheritedProp ] = inheritedPropsMapping[ parentLayoutElementId ][ inheritedProp ];
                                      }
                                      else {
                                          inheritedPropsMapping[ layoutElementId ][ inheritedProp ] = {
                                              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                              // @ts-expect-error
                                              value        : layoutElementInheritedProps[ inheritedProp ],
                                              inheritedFrom: inheritanceParent
                                          }
                                      }
                                  }
                              }

                              propsMapping[ layoutElementId ] = {
                                  ...dataExtraction.getDefaultsForItemType( 'CFG', 'TabContainer' ) as RequiredOf<TabContainerProperties>,
                                  ...layoutElementInheritedProps,
                                  ...propsMapping[ layoutElementId ],

                                  key: `${uid}_tabcontainer_${layoutElementId}`
                              } as TabContainerProperties;

                              if ( layoutElementStructure.children?.length ) {
                                  applyInheritanceAndProcessingRecursive( layoutElementId, layoutElementStructure.children, _.pick( propsMapping[ layoutElementId ], formFieldInheritedProperties ) as FormFieldInheritedProperties );
                              }
                          }
                          else {
                              const formFieldType               = ( propsMapping[ layoutElementId ] as FormFieldData ).type,
                                    formFieldDefaultsType       = formFieldType === 'VARIABLE' ? ( propsMapping[ layoutElementId ] as VariableFieldProperties ).variableType : formFieldType,
                                    formFieldDefaults           = dataExtraction.getDefaultsForItemType( 'CFG', fieldTypeToItemTypeMapping[ formFieldDefaultsType ] ) as RequiredOf<FormFieldProperties>,
                                    layoutElementInheritedProps = _.pick( inheritedProps, formFieldInheritedProperties );

                              for ( const inheritedProp of Object.keys( layoutElementInheritedProps ) ) {
                                  if ( propsMapping[ layoutElementId ]?.[ inheritedProp ] === undefined ) {
                                      if ( !inheritedPropsMapping[ layoutElementId ] ) {
                                          inheritedPropsMapping[ layoutElementId ] = {};
                                      }

                                      if ( inheritedPropsMapping[ parentLayoutElementId ]?.[ inheritedProp ] !== undefined ) {
                                          inheritedPropsMapping[ layoutElementId ][ inheritedProp ] = inheritedPropsMapping[ parentLayoutElementId ][ inheritedProp ];
                                      }
                                      else {
                                          inheritedPropsMapping[ layoutElementId ][ inheritedProp ] = {
                                              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                              // @ts-expect-error
                                              value        : layoutElementInheritedProps[ inheritedProp ],
                                              inheritedFrom: inheritanceParent
                                          }
                                      }
                                  }
                              }

                              propsMapping[ layoutElementId ] = {
                                  ...formFieldDefaults,
                                  ...layoutElementInheritedProps,
                                  ...propsMapping[ layoutElementId ],

                                  key: `${uid}_field-${layoutElementId}`,
                                  uid: `${uid}_field-${layoutElementId}`,

                                  // use FormField.key as input.name
                                  name: propsMapping[ layoutElementId ].key
                              } as FormFieldProperties;

                              const layoutElement                                              = layoutElementsToUse![ layoutElementId ] as FormFieldData,
                                    {
                                        readOnly: formFieldReadOnly,
                                        type,
                                        images,
                                        messages
                                    }                                                          = propsMapping[ layoutElementId ],
                                    dataDescriptorAlias                                        = layoutElement.dataDescriptorAlias ?? '',
                                    dataDescriptor                                             = dataDescriptorsToUse?.[ dataDescriptorAlias ] ?? {} as DataDescriptor,
                                    dataDescriptorMetaModel                                    = dataDescriptorsMetaModelToUse?.[ dataDescriptorAlias ] ?? {} as MetaModelMapping,
                                    dataDescriptorData                                         = dataDescriptorsDataToUse?.[ dataDescriptorAlias ] ?? {} as DataMapping,
                                    additionalMessages : FormFieldData[ 'data' ][ 'messages' ] = {},
                                    disabled : boolean                                         = Boolean( formFieldReadOnly || !user || ( layoutElement.type !== 'BUTTON' && dataDescriptorMetaModel.type !== 'reference' && layoutElement.type !== 'VARIABLE' && !dataDescriptorMetaModel.isSettable ) || dataDescriptorData.hasNoParent );

                              if ( dataDescriptor.type !== 'expression' ) {
                                  let propertySet : PropertySet | ReferencePropertySet | undefined,
                                      referenceSet : ReferenceSet | undefined,
                                      defaultLabel : string | undefined,
                                      autoComplete : boolean | undefined,
                                      maxLength : number | undefined;

                                  if ( dataDescriptorMetaModel.type === 'reference' ) {
                                      referenceSet = metaModel[ dataSchema ].getReferenceSet( dataDescriptorMetaModel.metaModels[ 0 ] );
                                  }
                                  else if ( dataDescriptorMetaModel.type === 'property' || dataDescriptorMetaModel.type === 'enumProperty' ) {
                                      propertySet = metaModel[ dataSchema ].getPropertySet( dataDescriptorMetaModel.metaModels[ 0 ] );
                                  }
                                  else if ( dataDescriptorMetaModel.type === 'referenceProperty' || dataDescriptorMetaModel.type === 'enumReferenceProperty' ) {
                                      propertySet = metaModel[ dataSchema ].getReferencePropertySet( dataDescriptorMetaModel.metaModels[ 0 ] );
                                  }

                                  // max length for item.key and item.name
                                  if (
                                      dataDescriptor.type === 'item'
                                      && (
                                          dataDescriptor.attribute === 'key'
                                          || dataDescriptor.attribute === 'name'
                                      )
                                  ) {
                                      maxLength = 255;
                                  }

                                  // no autocomplete for reference and static fields
                                  if (
                                      dataDescriptor.type === 'reference'
                                      || dataDescriptor.type === 'static'
                                      || !dataDescriptorMetaModel?.metaModels?.[ 0 ]
                                  ) {
                                      autoComplete = false;
                                  }

                                  propsMapping[ layoutElementId ] = {
                                      ...propsMapping[ layoutElementId ],

                                      disabled,
                                      maxLength,
                                      markDirty: !disabled && formFieldType !== 'VARIABLE',

                                      useHookForm,

                                      autoCompleteRequestParameters: dataDescriptor.type !== 'reference' && dataDescriptor.type !== 'static' && dataDescriptorMetaModel?.metaModels?.[ 0 ]
                                          ? ( propsMapping[ layoutElementId ].query
                                              ? {
                                                  schema    : dataSchema,
                                                  type      : 'query',
                                                  id        : propsMapping[ layoutElementId ].query.id,
                                                  parameters: propsMapping[ layoutElementId ].query.parameters
                                              }
                                              : {
                                                  schema   : dataSchema,
                                                  type     : dataDescriptor.type,
                                                  id       : dataDescriptorMetaModel.metaModels[ 0 ],
                                                  attribute: dataDescriptor.attribute
                                              } )
                                          : undefined
                                  };

                                  if ( autoComplete !== undefined ) {
                                      propsMapping[ layoutElementId ].autoComplete = autoComplete;
                                  }

                                  // process additional parameters by building a dataDescriptor for values containing an attributePath
                                  if ( propsMapping[ layoutElementId ].additionalParameters ) {
                                      const dataObjects = extractDataObjectsFromDataStructure( dataStructureToUse );

                                      for ( const parameter of Object.keys( propsMapping[ layoutElementId ].additionalParameters ) ) {
                                          const value          = propsMapping[ layoutElementId ].additionalParameters[ parameter ],
                                                attributePaths = value.match( /((?:[\w\p{L}\p{N}:]+\.)+[\w\p{L}\p{N}:]+)/gimu );

                                          if ( attributePaths ) {
                                              for ( let attributePath of attributePaths ) {
                                                  attributePath = attributePath.replaceAll( /(^\{|\}$)/g, '' );

                                                  const attributePathParts = attributePath.split( '.' ),
                                                        dataObjectKey      = attributePathParts.shift(),
                                                        dataObject         = dataObjects.find( dO => dO.item.key === dataObjectKey );

                                                  if ( dataObject ) {
                                                      if ( !propsMapping[ layoutElementId ].additionalParametersDataDescriptors ) {
                                                          propsMapping[ layoutElementId ].additionalParametersDataDescriptors = {};
                                                      }
                                                      if ( !propsMapping[ layoutElementId ].additionalParametersDataDescriptors[ parameter ] ) {
                                                          propsMapping[ layoutElementId ].additionalParametersDataDescriptors[ parameter ] = [];
                                                      }
                                                      propsMapping[ layoutElementId ].additionalParametersDataDescriptors[ parameter ].push( buildDataDescriptor( { attributePath: attributePathParts.join( '.' ) }, dataObject ) );


                                                      // TODO: rename rootItem to rootObject
                                                      // TODO: What about treeParentObject, treeRootObject...


                                                      // if dataObjectKey === 'rootItem' rewrite parameter value
                                                      if ( dataObjectKey === 'rootItem' ) {
                                                          propsMapping[ layoutElementId ].additionalParameters[ parameter ] = propsMapping[ layoutElementId ].additionalParameters[ parameter ].replaceAll( /\$\{rootItem./g, `\${${dataObject.item.key}.` );
                                                      }
                                                  }
                                                  else if ( dataObjectKey === 'user' ) {
                                                      propsMapping[ layoutElementId ].additionalParameters[ parameter ] = getMessageTemplateStringResolver()( value, true ) ?? value;
                                                  }
                                              }
                                          }
                                      }
                                  }

                                  // process filters by building dataDescriptors for filters containing attributePaths
                                  if ( propsMapping[ layoutElementId ].filters ) {
                                      const dataObjects    = extractDataObjectsFromDataStructure( dataStructureToUse ),
                                            value          = propsMapping[ layoutElementId ].filters,
                                            attributePaths = value.match( /(?:\$\{)((?:[\w\p{L}\p{N}:]+\.)+[\w\p{L}\p{N}:]+)(?:\})/gimu );

                                      if ( attributePaths ) {
                                          for ( let attributePath of attributePaths ) {
                                              attributePath = attributePath.replaceAll( /(\$\{|\}$)/g, '' );

                                              const attributePathParts = attributePath.split( '.' ),
                                                    dataObjectKey      = attributePathParts.shift(),
                                                    dataObject         = dataObjectKey === 'rootItem'
                                                        ? dataStructure
                                                        : dataObjects.find( dO => dO.item.key === dataObjectKey );

                                              if ( dataObject ) {
                                                  if ( !propsMapping[ layoutElementId ].additionalFiltersDataDescriptors ) {
                                                      propsMapping[ layoutElementId ].additionalFiltersDataDescriptors = [];
                                                  }
                                                  propsMapping[ layoutElementId ].additionalFiltersDataDescriptors.push( buildDataDescriptor( { attributePath: attributePathParts.join( '.' ) }, dataObject ) );

                                                  // if dataObjectKey === 'rootItem' rewrite parameter value
                                                  if ( dataObjectKey === 'rootItem' ) {
                                                      propsMapping[ layoutElementId ].filters = propsMapping[ layoutElementId ].filters.replaceAll( /\$\{rootItem./g, `\${${dataObject.item.key}.` );
                                                  }
                                              }
                                              else if ( dataObjectKey === 'user' ) {
                                                  propsMapping[ layoutElementId ].filters = getMessageTemplateStringResolver()( propsMapping[ layoutElementId ].filters, true ) ?? propsMapping[ layoutElementId ].filters;
                                              }
                                          }
                                      }
                                  }

                                  // process fixFilters by building dataDescriptors for fixFilters containing attributePaths
                                  if ( propsMapping[ layoutElementId ].fixFilters ) {
                                      const dataObjects    = extractDataObjectsFromDataStructure( dataStructureToUse ),
                                            value          = propsMapping[ layoutElementId ].fixFilters,
                                            attributePaths = value.match( /(?:\$\{)((?:[\w\p{L}\p{N}:]+\.)+[\w\p{L}\p{N}:]+)(?:\})/gimu );

                                      if ( attributePaths ) {
                                          for ( let attributePath of attributePaths ) {
                                              attributePath = attributePath.replaceAll( /(\$\{|\}$)/g, '' );

                                              const attributePathParts = attributePath.split( '.' ),
                                                    dataObjectKey      = attributePathParts.shift(),
                                                    dataObject         = dataObjectKey === 'rootItem'
                                                        ? dataStructure
                                                        : dataObjects.find( dO => dO.item.key === dataObjectKey );

                                              if ( dataObject ) {
                                                  if ( !propsMapping[ layoutElementId ].additionalFixFiltersDataDescriptors ) {
                                                      propsMapping[ layoutElementId ].additionalFixFiltersDataDescriptors = [];
                                                  }
                                                  propsMapping[ layoutElementId ].additionalFixFiltersDataDescriptors.push( buildDataDescriptor( { attributePath: attributePathParts.join( '.' ) }, dataObject ) );

                                                  // if dataObjectKey === 'rootItem' rewrite parameter value
                                                  if ( dataObjectKey === 'rootItem' ) {
                                                      propsMapping[ layoutElementId ].fixFilters = propsMapping[ layoutElementId ].fixFilters.replaceAll( /\$\{rootItem./g, `\${${dataObject.item.key}.` );
                                                  }
                                              }
                                              else if ( dataObjectKey === 'user' ) {
                                                  propsMapping[ layoutElementId ].fixFilters = getMessageTemplateStringResolver()( propsMapping[ layoutElementId ].fixFilters, true ) ?? propsMapping[ layoutElementId ].fixFilters;
                                              }
                                          }
                                      }
                                  }

                                  // remove defaultValue
                                  delete propsMapping[ layoutElementId ].defaultValue;

                                  // Reference fields
                                  if ( ( type === 'FILE' || type === 'REFERENCE' ) ) {
                                      if ( referenceSet ) {
                                          const newStatusId : number = metaModel[ dataSchema ].getStatus( 'active' )!.id;

                                          let referenceConfigRootItemId : number = 0,
                                              referenceDirection                 = Object.values( dataDescriptor.dataObject.reference?.referenceConfiguration ?? {} )?.[ 0 ].referenceDirection;

                                          if ( referenceDirection ) {
                                              // reverse the referenceDirection from dataObject since it is seen from the referenced items dataObject to parent items dataObject (e.g. rootItem)
                                              if ( referenceDirection === 'SrcToDest' ) {
                                                  referenceDirection = 'DestToSrc';
                                              }
                                              else {
                                                  referenceDirection = 'SrcToDest';
                                              }
                                          }
                                          else if ( configRootItemId && dataDescriptorMetaModel.type === 'reference' ) {
                                              const metaModelData = dataUtils.getMetaModelData( dataDescriptor as ReferenceDataDescriptor, dataStructureToUse!, dataSchema ),
                                                    itemTypeId    = ( metaModelData?.[ 0 ].object as ItemType | undefined )?.id;

                                              if ( referenceSet.srcItemTypeId === itemTypeId ) {
                                                  referenceDirection = 'DestToSrc';
                                              }
                                              else {
                                                  referenceDirection = 'SrcToDest';
                                              }
                                          }

                                          if ( configRootItemId ) {
                                              const parentDataObject = getParentDataObject( dataDescriptor.dataObject.id, dataStructureToUse ),
                                                    item             = parentDataObject
                                                        ? dataUtils.getData(
                                                            buildDataDescriptor( { attributePath: 'item.id' }, parentDataObject ) as ObjectDataDescriptor,
                                                            dataStructureToUse,
                                                            configDataContext,
                                                            configRootItemId
                                                        )?.[ 0 ].object as Item
                                                        : undefined;

                                              if ( item ) {
                                                  referenceConfigRootItemId = item.id;
                                              }
                                          }

                                          // default label
                                          if ( !messages?.label ) {
                                              if ( type === 'REFERENCE' ) {
                                                  defaultLabel = getItemTypeDisplayName( referenceSet[ referenceDirection === 'DestToSrc' ? 'srcItemTypeId' : 'destItemTypeId' ], referenceSet.schema );
                                              }
                                              else {
                                                  defaultLabel = 'YN.base.common.files';
                                              }
                                          }

                                          if ( type === 'REFERENCE' ) {
                                              propsMapping[ layoutElementId ] = {
                                                  label: defaultLabel,
                                                  ...messages,
                                                  ...propsMapping[ layoutElementId ],
                                                  schema          : dataSchema,
                                                  configDataContext,
                                                  configRootItemId: referenceConfigRootItemId,
                                                  referenceSetId  : referenceSet.id,
                                                  newStatusId,
                                                  referenceDirection,
                                                  disabled        : referenceConfigRootItemId === 0
                                              };
                                              propsMapping[ layoutElementId ].props = _.pick( propsMapping[ layoutElementId ], [ 'key' ].concat( referenceFieldComponentProps ) ) as ReferenceFieldComponentProps;
                                          }
                                          else {
                                              propsMapping[ layoutElementId ] = {
                                                  label: defaultLabel,
                                                  ...messages,
                                                  ...propsMapping[ layoutElementId ],
                                                  schema          : dataSchema,
                                                  configDataContext,
                                                  configRootItemId: referenceConfigRootItemId,
                                                  referenceSetId  : referenceSet.id,
                                                  newStatusId,
                                                  disabled        : referenceConfigRootItemId === 0
                                              };
                                              propsMapping[ layoutElementId ].props = _.pick( propsMapping[ layoutElementId ], [ 'key' ].concat( fileFieldComponentProps ) ) as FileFieldComponentProps;
                                          }
                                      }
                                      else {
                                          propsMapping[ layoutElementId ] = {
                                              label: defaultLabel,
                                              ...messages,
                                              ...propsMapping[ layoutElementId ]
                                          };
                                          propsMapping[ layoutElementId ].props = { ...propsMapping[ layoutElementId ] };

                                          console.error( 'no referenceSet: %o, %o', dataDescriptorMetaModel, propsMapping[ layoutElementId ] );
                                      }
                                  }

                                  // EnumProperty fields
                                  else if ( propertySet && type === 'ENUM' ) {
                                      const newStatusId : number = metaModel[ dataSchema ].getStatus( 'active' )!.id;

                                      // default label
                                      if ( !messages?.label ) {
                                          if ( dataDescriptorMetaModel.type === 'enumProperty' ) {
                                              defaultLabel = getPropertySetDisplayName( propertySet.id, propertySet.schema );
                                          }
                                          else {
                                              defaultLabel = getReferencePropertySetDisplayName( propertySet.id, propertySet.schema );
                                          }
                                      }

                                      propsMapping[ layoutElementId ] = {
                                          label: defaultLabel,
                                          ...messages,
                                          ...propsMapping[ layoutElementId ],
                                          schema                : propertySet.schema,
                                          propertySetId         : dataDescriptorMetaModel.type === 'enumProperty' ? propertySet.id : undefined,
                                          referencePropertySetId: dataDescriptorMetaModel.type === 'enumProperty' ? undefined : propertySet.id,
                                          newStatusId,
                                          keyEditorParams       : propsMapping[ layoutElementId ].fields?.key
                                              ? {
                                                  ...fieldUtils.extractPropsForValueTypeFields( {
                                                      ...propsMapping[ layoutElementId ].fields?.key.data
                                                  } as FormFieldProperties ),
                                                  type : propsMapping[ layoutElementId ].fields?.key.type,
                                                  query: propsMapping[ layoutElementId ].fields?.key.data.query
                                              }
                                              : undefined,
                                          valueEditorParams     : propsMapping[ layoutElementId ].fields?.value
                                              ? {
                                                  ...fieldUtils.extractPropsForValueTypeFields( {
                                                      ...propsMapping[ layoutElementId ].fields?.value.data
                                                  } as FormFieldProperties ),
                                                  type : propsMapping[ layoutElementId ].fields?.value.type,
                                                  query: propsMapping[ layoutElementId ].fields?.value.data.query
                                              }
                                              : undefined
                                      };

                                      if ( propsMapping[ layoutElementId ].keyEditorParams?.query ) {
                                          propsMapping[ layoutElementId ].keyEditorParams.autoCompleteRequestParameters = {
                                              schema    : dataSchema,
                                              type      : 'query',
                                              id        : propsMapping[ layoutElementId ].keyEditorParams.query.id,
                                              parameters: propsMapping[ layoutElementId ].keyEditorParams.query.parameters
                                          };
                                      }

                                      if ( propsMapping[ layoutElementId ].valueEditorParams?.query ) {
                                          propsMapping[ layoutElementId ].valueEditorParams.autoCompleteRequestParameters = {
                                              schema    : dataSchema,
                                              type      : 'query',
                                              id        : propsMapping[ layoutElementId ].valueEditorParams.query.id,
                                              parameters: propsMapping[ layoutElementId ].valueEditorParams.query.parameters
                                          };
                                      }

                                      if ( propsMapping[ layoutElementId ].valueEditorParams?.type === 'JSON' ) {
                                          try {
                                              if ( propsMapping[ layoutElementId ].valueEditorParams.jsonSchema ) {
                                                  propsMapping[ layoutElementId ].valueEditorParams.jsonSchema = JSON.parse( propsMapping[ layoutElementId ].valueEditorParams.jsonSchema );
                                              }
                                              if ( propsMapping[ layoutElementId ].valueEditorParams.uiSchema ) {
                                                  propsMapping[ layoutElementId ].valueEditorParams.uiSchema = JSON.parse( propsMapping[ layoutElementId ].valueEditorParams.uiSchema );
                                              }
                                          }
                                          catch ( e ) {
                                              console.error( e );
                                          }
                                      }

                                      propsMapping[ layoutElementId ].props = _.pick( propsMapping[ layoutElementId ], [ 'key' ].concat( enumFieldComponentProps ) ) as EnumFieldComponentProps;
                                  }

                                      // ValueSet fields
                                  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                                  else if ( ( propertySet?.valueSetId || propsMapping[ layoutElementId ].valueSetId ) && ( dataDescriptor.attribute === 'value' || type === 'VARIABLE' ) ) {
                                      // default label
                                      if ( propertySet && !messages?.label ) {
                                          if ( dataDescriptorMetaModel.type === 'property' ) {
                                              defaultLabel = getPropertySetDisplayName( propertySet.id, propertySet.schema );
                                          }
                                          else {
                                              defaultLabel = getReferencePropertySetDisplayName( propertySet.id, propertySet.schema );
                                          }
                                      }

                                      propsMapping[ layoutElementId ] = {
                                          label: defaultLabel,
                                          ...messages,
                                          ...propsMapping[ layoutElementId ],
                                          schema                : dataSchema,
                                          propertySetId         : dataDescriptorMetaModel.type === 'property' ? propertySet?.id : undefined,
                                          referencePropertySetId: dataDescriptorMetaModel.type === 'property' ? undefined : propertySet?.id,
                                          hasValueSet           : true
                                      };
                                      propsMapping[ layoutElementId ].props = _.pick( propsMapping[ layoutElementId ], [ 'key' ].concat( valueSetFieldComponentProps ) ) as ValueSetFieldComponentProps;
                                  }

                                  // all other fields
                                  else {
                                      // get definitions as placeholder for item.key and item.name if there is no one
                                      if ( !messages?.placeholder && dataDescriptorMetaModel.type === 'item' ) {

                                          if ( dataDescriptor.attribute === 'key' || dataDescriptor.attribute === 'name' ) {
                                              const itemType = metaModel[ dataSchema ].getItemType( dataDescriptorMetaModel.metaModels[ 0 ] ?? 0 );

                                              additionalMessages.placeholder = itemType?.[ `${dataDescriptor.attribute}Definition` ];
                                          }
                                      }

                                      // default label
                                      if ( !messages?.label ) {
                                          if ( dataDescriptorMetaModel.type === 'item' ) {
                                              const itemType = metaModel[ dataSchema ].getItemType( dataDescriptorMetaModel.metaModels[ 0 ] ?? 0 );

                                              if ( itemType && dataDescriptor.attribute === 'key' ) {
                                                  defaultLabel = getItemKeyDisplayName( itemType.id, itemType.schema );
                                              }
                                              else if ( itemType && dataDescriptor.attribute === 'name' ) {
                                                  defaultLabel = getItemNameDisplayName( itemType.id, itemType.schema );
                                              }
                                              else {
                                                  defaultLabel = `YN.base.common.attribute.${dataDescriptor.attribute}`;
                                              }
                                          }
                                          else if ( dataDescriptorMetaModel.type === 'reference' ) {
                                              defaultLabel = `YN.base.common.attribute.${dataDescriptor.attribute}`;
                                          }
                                          else if ( propertySet ) {
                                              if ( dataDescriptorMetaModel.type === 'property' ) {
                                                  defaultLabel = getPropertySetDisplayName( propertySet.id, propertySet.schema );
                                              }
                                              else {
                                                  defaultLabel = getReferencePropertySetDisplayName( propertySet.id, propertySet.schema );
                                              }

                                              if ( dataDescriptor.attribute === 'key' ) {
                                                  if ( propertySet.isValueSettable ) {
                                                      defaultLabel += propertySet.keyLabel;
                                                  }
                                              }
                                              else if ( dataDescriptor.attribute !== 'value' ) {
                                                  defaultLabel += ` ${__( `YN.base.common.attribute.${dataDescriptor.attribute}` )}`;
                                              }
                                          }
                                      }

                                      if ( type === 'BUTTON' ) {
                                          propsMapping[ layoutElementId ].icon = images?.icon ? `yn-icon ${getIconCssClassName( 'byRef', images.icon, 'CFG' )}` : propsMapping[ layoutElementId ].iconCls;
                                      }

                                      else if ( type === 'JSON' || ( type === 'VARIABLE' && ( propsMapping[ layoutElementId ] as VariableFieldProperties ).variableType === 'JSON' ) ) {
                                          try {
                                              if ( propsMapping[ layoutElementId ].jsonSchema ) {
                                                  propsMapping[ layoutElementId ].jsonSchema = JSON.parse( propsMapping[ layoutElementId ].jsonSchema );
                                              }
                                              if ( propsMapping[ layoutElementId ].uiSchema ) {
                                                  propsMapping[ layoutElementId ].uiSchema = JSON.parse( propsMapping[ layoutElementId ].uiSchema );
                                              }
                                          }
                                          catch ( e ) {
                                              console.error( e );
                                          }
                                      }

                                      else if ( type === 'STATIC' && !propsMapping[ layoutElementId ].content ) {
                                          if ( images?.content ) {
                                              propsMapping[ layoutElementId ].content = formatValue( images.content, { type: 'Image', options: { unit: '%', width: 100 } } );
                                          }
                                          else if ( messages?.content ) {
                                              propsMapping[ layoutElementId ].content = messages.content;
                                          }
                                      }


                                      if ( type === 'BOOLEAN' && propsMapping[ layoutElementId ].useCheckboxLabelAsDefault ) {
                                          propsMapping[ layoutElementId ] = {
                                              checkboxLabel: defaultLabel,
                                              propertySetId: propertySet?.id,
                                              ...images,
                                              ...messages,
                                              ...additionalMessages,
                                              ...propsMapping[ layoutElementId ],
                                              valueUnit: propertySet?.valueUnit
                                          };
                                      }
                                      else {
                                          propsMapping[ layoutElementId ] = {
                                              label        : defaultLabel,
                                              propertySetId: propertySet?.id,
                                              ...images,
                                              ...messages,
                                              ...additionalMessages,
                                              ...propsMapping[ layoutElementId ],
                                              valueUnit: propertySet?.valueUnit
                                          };
                                      }

                                      propsMapping[ layoutElementId ].props = fieldUtils.extractPropsForValueTypeFields( {
                                          ...propsMapping[ layoutElementId ]
                                      } );
                                  }

                                  delete propsMapping[ layoutElementId ].props?.id;
                              }
                          }
                      }
                  };

                  if ( layoutStructureToUse.children?.length ) {
                      applyInheritanceAndProcessingRecursive( formLayoutElementId, layoutStructureToUse.children, _.pick( propsMapping[ formLayoutElementId ], formInheritedProperties ) as FormInheritedProperties );
                  }
              }

              if ( debug ) {
                  console.log( `%c${Math.round( 1000 * ( performance.now() - innerStartTime ) ) / 1000} ms%c`.asLogLine( 100 ), `color: #${debugColorScheme.cacheFunction}; font-weight: bold;`, 'font-weight: unset;' );
                  console.groupEnd();
              }

              return {
                  props              : propsMapping,
                  inheritedProps     : inheritedPropsMapping,
                  repeatedDataObjects: repeatedDataObjectsMapping
              };
          },
          orderLayoutStructure                                 = ( layoutStructure1 : ContainerLayoutStructure | TabContainerLayoutStructure | FormFieldLayoutStructureAndDataBinding, layoutStructure2 : ContainerLayoutStructure | TabContainerLayoutStructure | FormFieldLayoutStructureAndDataBinding ) : -1 | 0 | 1 => {
              const layoutStructure1Order = ( layoutElementsProp![ layoutStructure1.layoutElementId ] as ( ContainerProperties | FormFieldProperties ) )?.order,
                    layoutStructure2Order = ( layoutElementsProp![ layoutStructure2.layoutElementId ] as ( ContainerProperties | FormFieldProperties ) )?.order;

              if ( layoutStructure1Order > layoutStructure2Order ) {
                  return 1;
              }
              else if ( layoutStructure1Order < layoutStructure2Order ) {
                  return -1;
              }
              else {
                  return 0;
              }
          },
          orderLayoutElements                                  = ( layoutElement1 : LoadContainerData | LoadTabContainerData | FormFieldData, layoutElement2 : LoadContainerData | LoadTabContainerData | FormFieldData ) => {
              if ( ( layoutElement1.data.order ?? 100 ) > ( layoutElement2.data.order ?? 100 ) ) {
                  return 1;
              }
              else if ( ( layoutElement2.data.order ?? 100 ) > ( layoutElement1.data.order ?? 100 ) ) {
                  return -1;
              }
              else {
                  return 0;
              }
          },
          getFormLayoutStyle                                   = ( elementProperties : {
              minWidth? : number;
              minWidthUnit : WidthUnit;
              maxWidth? : number;
              maxWidthUnit : WidthUnit;
              width? : number;
              widthUnit : WidthUnit;
          }, gridSize : number ) : React.CSSProperties => {
              const retVal : React.CSSProperties = {},
                    {
                        minWidth,
                        minWidthUnit,
                        maxWidth,
                        maxWidthUnit,
                        width,
                        widthUnit
                    }                            = elementProperties;

              if ( width ) {
                  if ( width < 0 ) {
                      retVal.flex = `${-1 * width} ${-1 * width} auto`;
                      retVal.width = '100px';
                  }
                  else {
                      retVal.flex = 'none';
                      if ( widthUnit === 'gridunit' ) {
                          retVal.width = `${100 * Math.min( 1, width / gridSize )}%`;
                      }
                      else {
                          retVal.width = `${width}${widthUnitMapping[ widthUnit ]}`;
                      }
                      retVal.minWidth = retVal.width;
                      retVal.maxWidth = retVal.width;
                  }
              }
              else {
                  retVal.flex = 'none';
                  retVal.width = '100%';
                  retVal.minWidth = retVal.width;
                  retVal.maxWidth = retVal.width;
              }

              if ( minWidth ) {
                  if ( minWidthUnit === 'gridunit' ) {
                      retVal.minWidth = `${100 * Math.min( 1, minWidth / gridSize )}%`;
                  }
                  else {
                      retVal.minWidth = `${minWidth}${widthUnitMapping[ minWidthUnit ]}`;
                  }
              }

              if ( maxWidth ) {
                  if ( maxWidthUnit === 'gridunit' ) {
                      retVal.maxWidth = `${100 * Math.min( 1, maxWidth / gridSize )}%`;
                  }
                  else {
                      retVal.maxWidth = `${maxWidth}${widthUnitMapping[ maxWidthUnit ]}`;
                  }
              }

              return retVal;
          },
          getLayoutStructureForLayoutElement                   = ( layoutElementId : number, layoutStructureToUse : FormLayoutStructure ) : FormLayoutStructure | ContainerLayoutStructure | TabContainerLayoutStructure | FormFieldLayoutStructureAndDataBinding | undefined => {
              let retVal : FormLayoutStructure | ContainerLayoutStructure | TabContainerLayoutStructure | FormFieldLayoutStructureAndDataBinding | undefined;

              if ( layoutStructureToUse ) {
                  const recursiveLookup = ( innerLayoutStructure : FormLayoutStructure | ContainerLayoutStructure | TabContainerLayoutStructure | FormFieldLayoutStructureAndDataBinding ) : FormLayoutStructure | ContainerLayoutStructure | TabContainerLayoutStructure | FormFieldLayoutStructureAndDataBinding | undefined => {
                      let innerRetVal : FormLayoutStructure | ContainerLayoutStructure | TabContainerLayoutStructure | FormFieldLayoutStructureAndDataBinding | undefined;

                      if ( innerLayoutStructure.layoutElementId === layoutElementId ) {
                          innerRetVal = innerLayoutStructure;
                      }
                      else if ( innerLayoutStructure.type === 'FORM' || innerLayoutStructure.type === 'CONTAINER' || innerLayoutStructure.type === 'TABCONTAINER' ) {
                          for ( const childLayoutStructure of innerLayoutStructure.children ) {
                              innerRetVal = recursiveLookup( childLayoutStructure );
                              if ( innerRetVal ) {
                                  break;
                              }
                          }
                      }

                      return innerRetVal;
                  };

                  retVal = recursiveLookup( layoutStructureToUse );
              }

              return retVal;
          },
          getParentLayoutStructureForLayoutElement             = ( layoutElementId : number, layoutStructureToUse : FormLayoutStructure ) : FormLayoutStructure | ContainerLayoutStructure | TabContainerLayoutStructure | undefined => {
              let retVal : FormLayoutStructure | ContainerLayoutStructure | TabContainerLayoutStructure | undefined;

              if ( layoutStructureToUse ) {
                  const recursiveLookup = ( innerLayoutStructure : FormLayoutStructure | ContainerLayoutStructure | TabContainerLayoutStructure | FormFieldLayoutStructureAndDataBinding ) : FormLayoutStructure | ContainerLayoutStructure | TabContainerLayoutStructure | undefined => {
                      let innerRetVal : FormLayoutStructure | ContainerLayoutStructure | TabContainerLayoutStructure | undefined;

                      if ( innerLayoutStructure.type === 'FORM' || innerLayoutStructure.type === 'CONTAINER' || innerLayoutStructure.type === 'TABCONTAINER' ) {
                          for ( const childLayoutStructure of innerLayoutStructure.children ) {
                              if ( childLayoutStructure.layoutElementId === layoutElementId ) {
                                  innerRetVal = innerLayoutStructure;
                                  break;
                              }
                              else {
                                  innerRetVal = recursiveLookup( childLayoutStructure );
                                  if ( innerRetVal ) {
                                      break;
                                  }
                              }
                          }
                      }

                      return innerRetVal;
                  };

                  retVal = recursiveLookup( layoutStructureToUse );
              }

              return retVal;
          },
          getTopMostLayoutElementIdAtPoint                     = ( subLayoutStructure : ( ContainerLayoutStructure | TabContainerLayoutStructure | FormFieldLayoutStructureAndDataBinding )[], x : number, y : number, dragLayoutElementId? : number ) => {
              if ( debug ) {
                  console.groupCollapsed( `%c${`%cgetTopMostLayoutElementIdAtPoint%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.helperFunction}; font-weight: bold;`, 'font-weight: normal;' );
                  console.log( '    subLayoutStructure', subLayoutStructure );
                  console.log( '    x', x );
                  console.log( '    y', y );
                  console.log( '    dragLayoutElementId', dragLayoutElementId );
              }

              let retVal : number | undefined;

              for ( const childLayoutStructure of subLayoutStructure ) {
                  const element = document.getElementById( `leid_${childLayoutStructure.layoutElementId}_fieldWrapper` ) ?? document.getElementById( `leid_${childLayoutStructure.layoutElementId}` );

                  if ( element ) {
                      const elementBox = element.getBoundingClientRect();

                      if (
                          x >= elementBox.x && x <= ( elementBox.x + elementBox.width )
                          && y >= elementBox.y && y <= ( elementBox.y + elementBox.height )
                      ) {
                          let childRetVal : number | undefined;

                          // dig deeper but only if not the dragged layoutElement
                          if ( ( childLayoutStructure.type === 'CONTAINER' || childLayoutStructure.type === 'TABCONTAINER' ) && childLayoutStructure.layoutElementId !== dragLayoutElementId ) {
                              childRetVal = getTopMostLayoutElementIdAtPoint( childLayoutStructure.children, x, y, dragLayoutElementId );
                          }

                          retVal = childRetVal ?? childLayoutStructure.layoutElementId;
                          break;
                      }
                  }
              }

              if ( debug ) {
                  console.log( '    retVal', retVal );
                  console.groupEnd();
              }

              return retVal;
          },
          getInsertPosition                                    = ( layoutElementId : number, x : number, y : number ) : InsertPosition | undefined => {
              if ( debug ) {
                  console.groupCollapsed( `%c${`%cgetInsertPosition%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.helperFunction}; font-weight: bold;`, 'font-weight: normal;' );
                  console.log( '    layoutElementId', layoutElementId );
                  console.log( '    x', x );
                  console.log( '    y', y );
              }

              let retVal : InsertPosition | undefined;

              if ( dragInfo.current?.layoutStructure && dragInfo.current.layoutElements ) {
                  const targetElement = document.getElementById( `leid_${layoutElementId}_fieldWrapper` ) ?? document.getElementById( `leid_${layoutElementId}` );

                  if ( targetElement ) {
                      const targetElementBox    = targetElement.getBoundingClientRect(),
                            targetLayoutElement = dragInfo.current.layoutElements[ layoutElementId ] as LoadFormData | LoadContainerData | LoadTabContainerData | FormFieldData | undefined,
                            dragLayoutElement   = dragInfo.current.layoutElements[ dragInfo.current.dragLayoutElementId ] as LoadContainerData | LoadTabContainerData | FormFieldData | undefined;

                      if ( dragLayoutElement && targetLayoutElement ) {
                          retVal = 'before';

                          if ( targetLayoutElement.type === 'FORM' ) {
                              retVal = 'insideBefore';

                              if ( ( y - targetElementBox.y ) > ( -1 * ( targetElementBox.height / targetElementBox.width ) * ( x - targetElementBox.x ) + targetElementBox.height ) ) {
                                  retVal = 'insideAfter';
                              }
                          }
                          else if (
                              (
                                  targetLayoutElement.type === 'CONTAINER'
                                  || targetLayoutElement.type === 'TABCONTAINER'
                              )
                              && x > targetElementBox.x + Math.max( remWidth, Math.min( 2 * remWidth, .1 * targetElementBox.width ) )
                              && x < targetElementBox.x + targetElementBox.width - Math.max( remWidth, Math.min( 2 * remWidth, .1 * targetElementBox.width ) )
                              && y > targetElementBox.y + Math.max( remWidth, Math.min( 2 * remWidth, .1 * targetElementBox.height ) )
                              && y < targetElementBox.y + targetElementBox.height - Math.max( remWidth, Math.min( 2 * remWidth, .1 * targetElementBox.height ) )
                          ) {
                              retVal = 'insideBefore';

                              if ( ( y - targetElementBox.y ) > ( -1 * ( targetElementBox.height / targetElementBox.width ) * ( x - targetElementBox.x ) + targetElementBox.height ) ) {
                                  retVal = 'insideAfter';
                              }
                          }
                          else if ( ( y - targetElementBox.y ) > ( -1 * ( targetElementBox.height / targetElementBox.width ) * ( x - targetElementBox.x ) + targetElementBox.height ) ) {
                              retVal = 'after';
                          }
                      }
                  }
              }

              if ( debug ) {
                  console.log( '    retVal', retVal );
                  console.groupEnd();
              }

              return retVal;
          },
          getRealLayoutElementIdFromLayoutElementIdAndParentId = ( layoutElementId : number, parentId? : number ) : number | string => {
              let retVal : number | string = layoutElementId;

              if ( parentId !== undefined ) {
                  retVal = `${layoutElementId}_${parentId}`;
              }

              return retVal;
          },
          getLayoutElementIdAndParentIdFromRealLayoutElementId = ( realLayoutElementId : number | string ) : {
              layoutElementId : number;
              parentId : number | undefined;
          } => {
              const retVal : {
                        layoutElementId : number;
                        parentId : number | undefined;
                    }              = {
                        layoutElementId: 0,
                        parentId       : undefined
                    },
                    delimiterIndex = String( realLayoutElementId ).indexOf( '_' );

              if ( typeof realLayoutElementId === 'number' ) {
                  retVal.layoutElementId = realLayoutElementId;
              }
              else if ( delimiterIndex === -1 ) {
                  retVal.layoutElementId = Number( realLayoutElementId );
              }
              else {
                  retVal.layoutElementId = Number( realLayoutElementId.substring( 0, delimiterIndex ) );
                  retVal.parentId = Number( realLayoutElementId.substring( delimiterIndex + 1 ) );
              }

              return retVal;
          },
          /*getRealLayoutElementDataDescriptorAliasFromLayoutElementDataDescriptorAliasAndParentId = ( layoutElementDataDescriptorAlias : string, parentId? : number ) => {
              let retVal : string = layoutElementDataDescriptorAlias;

              if ( parentId ) {
                  retVal = `${layoutElementDataDescriptorAlias}_${parentId}`;
              }

              return retVal;
          },
          getLayoutElementDataDescriptorAliasAndParentIdFromRealLayoutElementDataDescriptorAlias = ( realLayoutElementDataDescriptorAlias : string ) : { layoutElementDataDescriptorAlias : string; parentId : number | undefined; } => {
              const retVal : {
                        layoutElementDataDescriptorAlias : string;
                        parentId : number | undefined;
                    }              = {
                        layoutElementDataDescriptorAlias: realLayoutElementDataDescriptorAlias,
                        parentId                        : undefined
                    },
                    delimiterIndex = String( realLayoutElementDataDescriptorAlias ).indexOf( '_' );

              if ( delimiterIndex !== -1 ) {
                  retVal.layoutElementDataDescriptorAlias = realLayoutElementDataDescriptorAlias.substring( 0, delimiterIndex );
                  retVal.parentId = Number( realLayoutElementDataDescriptorAlias.substring( delimiterIndex + 1 ) );
              }

              return retVal;
          },*/
          resolveMessage                                       = ( message : string | Message, resolvedDataObjects : ResolvedDataObjectChain = {}, additionalResolverParameters : {
              [ p : string ] : any
          }                                                                                                                                  = {}, parentItemId? : number ) => {
              resolvedDataObjects = { ...resolvedDataObjects };
              additionalResolverParameters = { ...additionalResolverParameters };

              if ( !resolvedDataObjects.rootObject ) {
                  const rootItem = global.data[ dataContext ].items[ rootItemId ?? 0 ];

                  resolvedDataObjects.rootObject = {
                      item: rootItem
                  };
              }

              if (
                  !additionalResolverParameters.variables
                  || !additionalResolverParameters.treeVariables
                  || !viewKey
                  || !rootItemId
                  || !rootItemKey
                  || !rootItemType
                  || !configHash
              ) {
                  additionalResolverParameters = { ...additionalResolverParameters };

                  if ( !additionalResolverParameters.variables ) {
                      additionalResolverParameters.variables = {
                          ...variables,
                          [ Symbol.toPrimitive ]: ( hint : 'default' | 'number' | 'string' ) => {
                              // toString
                              if ( hint === 'string' ) {
                                  return nl2br( JSON.stringify( variables, null, '\u00a0\u00a0\u00a0\u00a0' ) );
                              }
                              // all other
                              else {
                                  return null;
                              }
                          }
                      };
                  }

                  if ( !additionalResolverParameters.treeVariables ) {
                      additionalResolverParameters.treeVariables = {};
                  }

                  if (
                      !viewKey
                      || !rootItemId
                      || !rootItemKey
                      || !rootItemType
                      || !configHash
                  ) {
                      additionalResolverParameters.viewKey = viewKey;
                      additionalResolverParameters.rootItemId = rootItemId;
                      additionalResolverParameters.rootItemKey = rootItemKey;
                      additionalResolverParameters.rootItemType = rootItemType;
                      additionalResolverParameters.configHash = configHash;
                  }
              }

              let messageKey : string;

              if ( typeof message !== 'string' ) {
                  messageKey = message.key;

                  if ( dataStructure ) {
                      for ( const dataObjectLabel of Object.keys( message.dataObjects ) ) {
                          const dataObject         = message.dataObjects[ dataObjectLabel ],
                                resolvedDataObject = dataUtils.getItemsAndReferencesForDataObject( dataObject, dataStructure, dataContext, rootItemId, parentItemId );

                          if ( resolvedDataObject ) {
                              resolvedDataObjects[ dataObjectLabel ] = {
                                  item     : resolvedDataObject?.items?.[ 0 ],
                                  reference: resolvedDataObject?.references?.[ 0 ]
                              };
                          }
                          else {
                              additionalResolverParameters[ dataObjectLabel ] = {};
                          }
                      }
                  }
              }
              else {
                  messageKey = message;
              }

              const messageTemplateStringResolver = messageUtils.getMessageTemplateStringResolverForResolvedDataObjects(
                  resolvedDataObjects.rootObject.item,
                  undefined,
                  resolvedDataObjects,
                  additionalResolverParameters
              );

              return messageTemplateStringResolver?.( __( messageKey ).replaceAll( /[\u200B-\u200E\uFEFF]/g, '' ) ) ?? '';
          },
          showDropZones                                        = () => {
              const scrollElement    = document.getElementById( `${uid}_form` )?.parentElement,
                    scrollElementBox = scrollElement?.getBoundingClientRect();

              if ( scrollElement && scrollElementBox ) {
                  // mask delete drop zone
                  let maskElement = document.createElement( 'div' );

                  maskElement.id = `${uid}_formdesigner_delete_zone`;
                  maskElement.className = 'yn-formdesigner-delete-zone';
                  maskElement.style.top = `${scrollElementBox.y}px`;
                  maskElement.style.width = `${scrollElementBox.x}px`;
                  maskElement.style.height = `${scrollElementBox.height}px`;
                  document.body.appendChild( maskElement );

                  // mask cancel drop zone
                  maskElement = document.createElement( 'div' );

                  maskElement.id = `${uid}_formdesigner_cancel_zone`;
                  maskElement.className = 'yn-formdesigner-cancel-zone';
                  maskElement.style.top = `${scrollElementBox.y}px`;
                  maskElement.style.width = `${document.body.clientWidth - scrollElementBox.x - scrollElementBox.width}px`;
                  maskElement.style.height = `${scrollElementBox.height}px`;
                  document.body.appendChild( maskElement );
              }
          },
          hideDropZones                                        = () => {
              const deleteZone = document.getElementById( `${uid}_formdesigner_delete_zone` ),
                    cancelZone = document.getElementById( `${uid}_formdesigner_cancel_zone` );

              // unmask delete zone
              if ( deleteZone ) {
                  document.body.removeChild( deleteZone );
              }

              // unmask cancel zone
              if ( cancelZone ) {
                  document.body.removeChild( cancelZone );
              }
          },
          onDragToDelete                                       = () => {
              if ( debug ) {
                  console.groupCollapsed( `%c${`%conDragToDelete%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              const innerStartTime = debug ? performance.now() : 0;

              if ( dragInfo.current?.layoutStructure && dragInfo.current.layoutElements ) {
                  const newLayoutStructure  = JSON.parse( JSON.stringify( dragInfo.current.layoutStructure ) ) as FormLayoutStructure,
                        newLayoutElements   = JSON.parse( JSON.stringify( dragInfo.current.layoutElements ) ) as LayoutElements,
                        dragLayoutStructure = dragInfo.current?.dragLayoutStructure ?? newDragInfo.current?.layoutStructure ?? getLayoutStructureForLayoutElement( dragInfo.current.dragLayoutElementId, newLayoutStructure ) as ContainerLayoutStructure | FormFieldLayoutStructureAndDataBinding | undefined,
                        dragLayoutElement   = dragInfo.current.layoutElements[ dragInfo.current.dragLayoutElementId ] as LoadContainerData | FormFieldData | undefined;

                  if ( dragLayoutElement && dragLayoutStructure ) {
                      const parentLayoutStructure = getParentLayoutStructureForLayoutElement( dragInfo.current.dragLayoutElementId, newLayoutStructure );

                      if ( parentLayoutStructure ) {
                          const children : ( LoadContainerData | FormFieldData )[] = [];

                          let index : number = parentLayoutStructure.children.findIndex( childLayoutStructure => childLayoutStructure.layoutElementId === dragLayoutStructure.layoutElementId );

                          // remove from parentLayoutStructure
                          if ( index !== -1 ) {
                              parentLayoutStructure.children.splice( index, 1 );
                          }

                          // reorder layoutElements in parentLayoutStructure
                          for ( const childLayoutStructure of parentLayoutStructure.children ) {
                              const childLayoutElement = newLayoutElements[ childLayoutStructure.layoutElementId ] as LoadContainerData | FormFieldData;

                              if ( childLayoutElement.data.id !== dragLayoutElement.data.id ) {
                                  children.push( childLayoutElement );
                              }
                          }

                          children.sort( orderLayoutElements );

                          for ( index = 0; index < children.length; index++ ) {
                              ( newLayoutElements[ children[ index ].data.id ] as LoadContainerData | FormFieldData )!.data.order = 10 * ( index + 1 );
                          }

                          showDropZones();

                          // mark drag element to be removed
                          dragInfo.current?.dragElement.classList.add( 'yn-formdesigner-drag-delete' );

                          // update dragInfo
                          dragInfo.current.doNothing = false;

                          const layoutElementsPropAndInheritedProps = buildLayoutElementsPropAndInheritedProps( undefined, newLayoutStructure, newLayoutElements );

                          // update elusiveData
                          dispatch( setElusiveData( uid, {
                              layoutStructure             : newLayoutStructure,
                              layoutElements              : newLayoutElements,
                              layoutElementsProp          : layoutElementsPropAndInheritedProps.props,
                              layoutElementsInheritedProps: layoutElementsPropAndInheritedProps.inheritedProps
                          } ) );
                      }
                  }
              }

              if ( debug ) {
                  console.log( `%c${Math.round( 1000 * ( performance.now() - innerStartTime ) ) / 1000} ms%c`.asLogLine( 100 ), `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: unset;' );
                  console.groupEnd();
              }
          },
          onDragOut                                            = () => {
              if ( dragInfo.current ) {
                  showDropZones();

                  // mark drag element to not changed
                  dragInfo.current.dragElement.classList.add( 'yn-formdesigner-drag-nothing' );

                  // update dragInfo
                  dragInfo.current.doNothing = true;

                  // cleanup
                  dispatch( clearElusiveData( uid ) );
              }
          },
          onDragOver                                           = ( layoutElementId : number, position : InsertPosition ) : void => {
              if ( debug ) {
                  console.groupCollapsed( `%c${`%conDragOver - ${layoutElements?.[ layoutElementId ?? 0 ]?.data?.key ?? layoutElementId} - ${position}%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              const innerStartTime = debug ? performance.now() : 0;

              if ( dragInfo.current?.layoutStructure && dragInfo.current.layoutElements ) {
                  const newLayoutStructure  = JSON.parse( JSON.stringify( dragInfo.current.layoutStructure ) ) as FormLayoutStructure,
                        newLayoutElements   = JSON.parse( JSON.stringify( dragInfo.current.layoutElements ) ) as LayoutElements,
                        dragLayoutStructure = dragInfo.current?.dragLayoutStructure ?? newDragInfo.current?.layoutStructure ?? getLayoutStructureForLayoutElement( dragInfo.current.dragLayoutElementId, newLayoutStructure ) as ContainerLayoutStructure | FormFieldLayoutStructureAndDataBinding | undefined,
                        dragLayoutElement   = dragInfo.current.layoutElements[ dragInfo.current.dragLayoutElementId ] as LoadContainerData | LoadTabContainerData | FormFieldData | undefined;

                  if ( dragLayoutElement && dragLayoutStructure ) {
                      const oldParentLayoutStructure = getParentLayoutStructureForLayoutElement( dragInfo.current.dragLayoutElementId, newLayoutStructure ),
                            newParentLayoutStructure = position === 'insideBefore' || position === 'insideAfter'
                                ? getLayoutStructureForLayoutElement( layoutElementId, newLayoutStructure ) as ContainerLayoutStructure | undefined
                                : getParentLayoutStructureForLayoutElement( layoutElementId, newLayoutStructure );

                      let repeatedLayoutElementDropAllowed : boolean = !dragInfo.current.isRepeatedForDataObjectId;

                      if ( dragInfo.current.isRepeatedForDataObjectId && repeatedDataObjects?.includes( dragInfo.current.isRepeatedForDataObjectId ) ) {
                          let repeatedParentLayoutStructure = newParentLayoutStructure;

                          while ( repeatedParentLayoutStructure && !repeatedLayoutElementDropAllowed ) {
                              const dataDescriptorAlias = ( newLayoutElements[ repeatedParentLayoutStructure.layoutElementId ] as LoadContainerData | undefined )?.isRepeatedForDataDescriptorAlias,
                                    dataDescriptor      = dataDescriptors?.[ dataDescriptorAlias ?? '' ];

                              if ( dataDescriptor?.dataObject?.origId === dragInfo.current.isRepeatedForDataObjectId ) {
                                  repeatedLayoutElementDropAllowed = true;
                              }
                              else {
                                  repeatedParentLayoutStructure = getParentLayoutStructureForLayoutElement( repeatedParentLayoutStructure.layoutElementId, newLayoutStructure );
                              }
                          }
                      }

                      if ( newParentLayoutStructure && repeatedLayoutElementDropAllowed ) {
                          let nearestNonLayoutWrapperParentType : 'FORM' | 'CONTAINER' | 'TABCONTAINER' | undefined;

                          if ( dragLayoutStructure.type !== 'CONTAINER' && newParentLayoutStructure.type === 'CONTAINER' ) {
                              let nextParentLayoutStructure : FormLayoutStructure | ContainerLayoutStructure | TabContainerLayoutStructure | undefined = newParentLayoutStructure;

                              do {
                                  if ( nextParentLayoutStructure.type === 'FORM' ) {
                                      nearestNonLayoutWrapperParentType = 'FORM';
                                  }
                                  else {
                                      const {
                                                isLayoutWrapper
                                            } = ( newLayoutElements?.[ nextParentLayoutStructure.layoutElementId ]?.data ?? {} ) as LoadContainerProperties;

                                      if ( !isLayoutWrapper ) {
                                          nearestNonLayoutWrapperParentType = 'CONTAINER';
                                      }
                                  }

                                  nextParentLayoutStructure = getParentLayoutStructureForLayoutElement( nextParentLayoutStructure.layoutElementId, layoutStructure! );
                              } while ( nextParentLayoutStructure && !nearestNonLayoutWrapperParentType )

                              if ( !nearestNonLayoutWrapperParentType ) {
                                  nearestNonLayoutWrapperParentType = 'FORM';
                              }
                          }
                          else {
                              nearestNonLayoutWrapperParentType = newParentLayoutStructure.type;
                          }

                          if (
                              // couldn't drop a layoutWrapper into tabContainer
                              ( dragLayoutElement.type === 'CONTAINER' && ( !dragLayoutElement.data.isLayoutWrapper || newParentLayoutStructure.type !== 'TABCONTAINER' ) )
                              // couldn't drop a tabContainer into form or into layoutWrapper (cascaded up over layoutWrapper) located in form nor into tabContainer
                              || ( dragLayoutStructure.type === 'TABCONTAINER' && nearestNonLayoutWrapperParentType !== 'FORM' && newParentLayoutStructure.type !== 'TABCONTAINER' )
                              // couldn't drop a field into form nor into layoutWrapper (cascaded up over layoutWrapper) located in form nor into tabContainer
                              || ( dragLayoutStructure.type !== 'CONTAINER' && dragLayoutStructure.type !== 'TABCONTAINER' && nearestNonLayoutWrapperParentType !== 'FORM' && newParentLayoutStructure.type !== 'TABCONTAINER' )
                          ) {
                              let children : ( LoadContainerData | FormFieldData )[],
                                  index : number;

                              if ( oldParentLayoutStructure && oldParentLayoutStructure.layoutElementId !== newParentLayoutStructure.layoutElementId ) {
                                  children = [];
                                  index = oldParentLayoutStructure.children.findIndex( childLayoutStructure => childLayoutStructure.layoutElementId === dragLayoutStructure.layoutElementId );

                                  // remove from old parentLayoutStructure (if in)
                                  if ( index !== -1 ) {
                                      if ( debug ) {
                                          console.log( '    -> remove from oldParentLayoutStructure' );
                                      }

                                      oldParentLayoutStructure.children.splice( index, 1 );

                                      // reorder layoutElements in old parentLayoutStructure
                                      for ( const childLayoutStructure of oldParentLayoutStructure.children ) {
                                          const childLayoutElement = newLayoutElements[ childLayoutStructure.layoutElementId ] as LoadContainerData | FormFieldData;

                                          if ( childLayoutElement.data.id !== dragLayoutElement.data.id ) {
                                              children.push( childLayoutElement );
                                          }
                                      }

                                      children.sort( orderLayoutElements );

                                      for ( index = 0; index < children.length; index++ ) {
                                          ( newLayoutElements[ children[ index ].data.id ] as LoadContainerData | FormFieldData )!.data.order = 10 * ( index + 1 );
                                      }
                                  }
                              }

                              children = [];
                              index = newParentLayoutStructure.children.findIndex( childLayoutStructure => childLayoutStructure.layoutElementId === dragLayoutStructure.layoutElementId );

                              // insert into new parentLayoutStructure (if not in)
                              if ( index === -1 ) {
                                  if ( debug ) {
                                      console.log( '    -> add to newParentLayoutStructure' );
                                  }

                                  // drag validation done before
                                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                  // @ts-ignore
                                  newParentLayoutStructure.children.push( dragLayoutStructure );
                              }

                              // reorder layoutElements in new parentLayoutStructure
                              for ( const childLayoutStructure of newParentLayoutStructure.children ) {
                                  const childLayoutElement = newLayoutElements![ childLayoutStructure.layoutElementId ] as LoadContainerData | FormFieldData;

                                  if ( childLayoutElement.data.id !== dragLayoutElement.data.id ) {
                                      children.push( childLayoutElement );
                                  }
                              }

                              children.sort( orderLayoutElements );

                              index = children.findIndex( child => child.data.id === layoutElementId );

                              switch ( position ) {
                                  case 'insideBefore':
                                      // drag validation done before
                                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                      // @ts-ignore
                                      children.unshift( dragLayoutElement );
                                      break;
                                  case 'insideAfter':
                                      // drag validation done before
                                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                      // @ts-ignore
                                      children.push( dragLayoutElement );
                                      break;
                                  case 'before':
                                      // drag validation done before
                                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                      // @ts-ignore
                                      children.splice( index, 0, dragLayoutElement );
                                      break;
                                  default:
                                      // drag validation done before
                                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                      // @ts-ignore
                                      children.splice( index + 1, 0, dragLayoutElement );
                              }

                              for ( index = 0; index < children.length; index++ ) {
                                  ( newLayoutElements[ children[ index ].data.id ] as LoadContainerData | FormFieldData )!.data.order = 10 * ( index + 1 );
                              }

                              // TODO: insertPositionMarker enable if required
                              /*const targetElement    = document.getElementById( `leid_${layoutElementId}_fieldWrapper` ) ?? document.getElementById( `leid_${layoutElementId}` ) ?? document.getElementById( `leid_${layoutElementId}_tab` ),
                                    targetElementBox = targetElement?.getBoundingClientRect();

                              if ( targetElementBox ) {
                                  dragInfo.current.insertMarkerElement.classList.remove(
                                      'yn-insert-position-after',
                                      'yn-insert-position-before',
                                      'yn-insert-position-inside-after',
                                      'yn-insert-position-inside-before'
                                  );
                                  dragInfo.current.insertMarkerElement.classList.add( `yn-insert-position-${position}` );
                                  dragInfo.current.insertMarkerElement.setAttribute( 'data-label', __( `YN.base.frontend.formDesigner.insertPosition.${position}` ) );
                              }*/

                              // mark drag element ok
                              dragInfo.current?.dragElement.classList.add( 'yn-formdesigner-drag-ok' );

                              // update dragInfo
                              dragInfo.current.layoutStructure = newLayoutStructure;
                              dragInfo.current.layoutElements = newLayoutElements;
                              dragInfo.current.doNothing = false;
                              if ( newDragInfo.current?.layoutStructure ) {
                                  // use only for the first drop
                                  delete newDragInfo.current.layoutStructure;
                              }

                              const layoutElementsPropAndInheritedProps = buildLayoutElementsPropAndInheritedProps( undefined, newLayoutStructure, newLayoutElements, dragInfo.current?.dataDescriptors, dragInfo.current?.dataDescriptorsMetaModel );

                              // update elusiveData
                              dispatch( setElusiveData( uid, {
                                  layoutStructure             : newLayoutStructure,
                                  layoutElements              : newLayoutElements,
                                  dataDescriptors             : dragInfo.current?.dataDescriptors ?? lastParams.current?.dataDescriptors ?? dataDescriptors,
                                  dataDescriptorsMetaModel    : dragInfo.current?.dataDescriptorsMetaModel ?? lastParams.current?.dataDescriptorsMetaModel ?? dataDescriptorsMetaModel,
                                  layoutElementsProp          : layoutElementsPropAndInheritedProps.props,
                                  layoutElementsInheritedProps: layoutElementsPropAndInheritedProps.inheritedProps
                              } ) );
                          }
                          else if ( debug ) {
                              console.log( 'drop element not valid for drag element' );
                          }
                      }
                  }
              }

              if ( debug ) {
                  console.log( `%c${Math.round( 1000 * ( performance.now() - innerStartTime ) ) / 1000} ms%c`.asLogLine( 100 ), `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: unset;' );
                  console.groupEnd();
              }
          },
          onDrop                                               = () : void => {
              if ( debug ) {
                  console.groupCollapsed( `%c${`%conDrop%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              const innerStartTime = debug ? performance.now() : 0;

              if ( layoutStructure && layoutElements && dragInfo.current?.layoutStructure && dragInfo.current.layoutElements && !dragInfo.current.doNothing ) {
                  const oldParentLayoutStructure = getParentLayoutStructureForLayoutElement( dragInfo.current.dragLayoutElementId, layoutStructure ),
                        newParentLayoutStructure = getParentLayoutStructureForLayoutElement( dragInfo.current.dragLayoutElementId, dragInfo.current.layoutStructure ),
                        oldToNewItemIdMapping : {
                            [ oldId : string ] : string
                        }                        = {};

                  // insert
                  if ( dragInfo.current.lastDragOverLayoutElementId && newParentLayoutStructure ) {
                      if ( debug ) {
                          console.log( '-> processing insert' );
                      }

                      const newParentItem        = global.data[ configDataContext ].items[ newParentLayoutStructure.layoutElementId ],
                            propertyTypeIdString = String( metaModel[ 'CFG' ].getPropertyType( 'Order' )?.id ?? 0 );

                      let referenceSetId : number | undefined,
                          newReference : Reference | undefined;

                      if ( newParentItem ) {
                          let dragItem = global.data[ configDataContext ].items[ dragInfo.current.dragLayoutElementId ];

                          // add given drag data
                          if ( newDragInfo.current ) {
                              if ( debug ) {
                                  console.log( '-> adding new data' );
                              }

                              for ( const item of Object.values( newDragInfo.current.data.items ) ) {
                                  const itemAttributes = item.toJSON( 'all' ) as ItemAttributes;

                                  // delete id
                                  if ( item.id < 0 ) {
                                      delete itemAttributes.id;
                                  }
                                  // delete new ids for create properties
                                  for ( const property of itemAttributes.properties ?? [] ) {
                                      if ( ( property.id ?? 0 ) < 0 ) {
                                          delete property.id;
                                      }
                                  }
                                  // delete new ids for create enumProperties
                                  for ( const enumProperty of itemAttributes.enumProperties ?? [] ) {
                                      for ( const property of enumProperty.properties ?? [] ) {
                                          if ( ( property.id ?? 0 ) < 0 ) {
                                              delete property.id;
                                          }
                                      }
                                  }

                                  if ( debug ) {
                                      console.log( '    -> adding new item', itemAttributes );
                                  }

                                  const newItem = dispatch( addItem(
                                      configDataContext,
                                      itemAttributes as ItemAttributesForCreation,
                                      'CFG',
                                      additionalElusiveDataContexts
                                  ) );

                                  if ( newItem ) {
                                      // update dragItem with newItem
                                      if ( item.id === dragInfo.current.dragLayoutElementId ) {
                                          dragItem = newItem;
                                      }

                                      if ( item.id < 0 ) {
                                          oldToNewItemIdMapping[ String( item.id ) ] = String( newItem.id );
                                      }
                                  }
                              }
                              if ( newDragInfo.current.data.references ) {
                                  for ( const reference of Object.values( newDragInfo.current.data.references ) ) {
                                      const referenceAttributes = reference.toJSON() as ReferenceAttributes;

                                      // remap item ids
                                      if ( oldToNewItemIdMapping[ String( reference.srcItemId ) ] ) {
                                          referenceAttributes.srcItemId = Number( oldToNewItemIdMapping[ String( reference.srcItemId ) ] );
                                      }
                                      if ( oldToNewItemIdMapping[ String( reference.destItemId ) ] ) {
                                          referenceAttributes.destItemId = Number( oldToNewItemIdMapping[ String( reference.destItemId ) ] );
                                      }

                                      // delete new ids for create reference
                                      if ( reference.id < 0 ) {
                                          delete referenceAttributes.id;
                                      }
                                      // delete new ids for create referenceProperties
                                      for ( const property of referenceAttributes.properties ?? [] ) {
                                          if ( ( property.id ?? 0 ) < 0 ) {
                                              delete property.id;
                                          }
                                      }
                                      // delete new ids for create enumReferenceProperties
                                      for ( const enumProperty of referenceAttributes.enumProperties ?? [] ) {
                                          for ( const property of enumProperty.properties ?? [] ) {
                                              if ( ( property.id ?? 0 ) < 0 ) {
                                                  delete property.id;
                                              }
                                          }
                                      }

                                      if ( debug ) {
                                          console.log( '    -> adding new reference', referenceAttributes );
                                      }

                                      dispatch( addReference(
                                          configDataContext,
                                          referenceAttributes as ReferenceAttributesForCreation,
                                          'CFG',
                                          additionalElusiveDataContexts
                                      ) );
                                  }
                              }
                          }

                          if ( dragItem ) {
                              const oldOrders : number[][] = [],
                                    newOrders : number[][] = [];

                              let oldIndex : number = -1,
                                  newIndex : number = -1;

                              // delete reference to old parent layoutStructure (if and changed)
                              if ( oldParentLayoutStructure && oldParentLayoutStructure.layoutElementId !== newParentLayoutStructure.layoutElementId ) {
                                  const oldParentItem = global.data[ configDataContext ].items[ oldParentLayoutStructure.layoutElementId ];

                                  if ( oldParentItem ) {
                                      referenceSetId = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( dragItem.itemTypeId, oldParentItem.itemTypeId, 'belongsTo', 'src' )?.[ 0 ]?.id;
                                      newReference = referenceSetId ? dataUtils.findReferences( configDataContext, referenceSetId, dragItem.id, oldParentLayoutStructure.layoutElementId )?.[ 0 ] : undefined;

                                      if ( newReference ) {
                                          if ( debug ) {
                                              console.log( '    -> removing reference to old parent' );
                                          }

                                          dispatch( deleteReference(
                                              configDataContext,
                                              newReference.id,
                                              undefined,
                                              additionalElusiveDataContexts
                                          ) );
                                      }
                                  }
                              }

                              // add reference to new parent layoutStructure
                              referenceSetId = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( dragItem.itemTypeId, newParentItem.itemTypeId, 'belongsTo', 'src' )?.[ 0 ]?.id;
                              newReference = referenceSetId ? dataUtils.findReferences( configDataContext, referenceSetId, dragItem.id, newParentLayoutStructure.layoutElementId )?.[ 0 ] : undefined;

                              if ( !newReference && referenceSetId ) {
                                  if ( debug ) {
                                      console.log( '    -> adding reference to new parent' );
                                  }

                                  dispatch( addReference(
                                      configDataContext,
                                      {
                                          referenceSetId,
                                          srcItemId : dragItem.id,
                                          destItemId: newParentLayoutStructure.layoutElementId
                                      } as ReferenceAttributesForCreation,
                                      'CFG',
                                      additionalElusiveDataContexts
                                  ) );
                              }
                              else if ( newReference?.isDeleted() ) {
                                  if ( debug ) {
                                      console.log( '    -> updating reference to new parent' );
                                  }

                                  dispatch( updateReference(
                                      configDataContext,
                                      newReference.id,
                                      {
                                          statusId: metaModel[ 'CFG' ].getStatus( 'active' )!.id
                                      },
                                      additionalElusiveDataContexts
                                  ) );
                              }

                              // set orders
                              if ( oldParentLayoutStructure && oldParentLayoutStructure.layoutElementId !== newParentLayoutStructure.layoutElementId ) {
                                  if ( debug ) {
                                      console.log( '    -> reordering layout elements in old parent' );
                                  }

                                  for ( const childLayoutStructure of oldParentLayoutStructure.children ) {
                                      const childItem          = global.data[ configDataContext ].items[ childLayoutStructure.layoutElementId ],
                                            childLayoutElement = dragInfo.current.layoutElements![ childLayoutStructure.layoutElementId ] as LoadContainerData | FormFieldData;

                                      if ( childItem && childLayoutElement ) {
                                          const data : ItemAttributesSettable = {
                                              properties: {
                                                  [ propertyTypeIdString ]: {
                                                      value: childLayoutElement.data.order
                                                  }
                                              }
                                          };

                                          dispatch( updateItem(
                                              configDataContext,
                                              childItem.id,
                                              data,
                                              additionalElusiveDataContexts
                                          ) );
                                      }
                                  }
                              }

                              if ( debug ) {
                                  console.log( '    -> reordering layout elements in new parent' );
                              }

                              for ( const childLayoutStructure of newParentLayoutStructure.children ) {
                                  const childItem          = global.data[ configDataContext ].items[ childLayoutStructure.layoutElementId ] ?? global.data[ configDataContext ].items[ Number( oldToNewItemIdMapping[ String( childLayoutStructure.layoutElementId ) ] ) ],
                                        childLayoutElement = dragInfo.current.layoutElements![ childLayoutStructure.layoutElementId ] as LoadContainerData | FormFieldData;

                                  if ( childItem && childLayoutElement ) {
                                      const data : ItemAttributesSettable = {
                                          properties: {
                                              [ propertyTypeIdString ]: {
                                                  value: childLayoutElement.data.order
                                              }
                                          }
                                      };

                                      dispatch( updateItem(
                                          configDataContext,
                                          childItem.id,
                                          data,
                                          additionalElusiveDataContexts
                                      ) );

                                      oldOrders.push( [ childItem.properties.get( Number( propertyTypeIdString ) )?.value as number ?? 100, childItem.id ] );
                                      newOrders.push( [ childLayoutElement.data.order!, childItem.id ] );
                                  }
                              }

                              // if parent not changed compute old and new indexes
                              if ( oldParentLayoutStructure?.layoutElementId === newParentLayoutStructure.layoutElementId ) {
                                  oldIndex = oldOrders.sort( ( o1, o2 ) => {
                                          if ( o1[ 0 ] > o2[ 0 ] ) {
                                              return 1;
                                          }
                                          else if ( o2[ 0 ] > o1[ 0 ] ) {
                                              return -1;
                                          }
                                          else {
                                              return 0;
                                          }
                                      } )
                                      .findIndex( o => o[ 1 ] === dragItem.id );
                                  newIndex = newOrders.sort( ( o1, o2 ) => {
                                          if ( o1[ 0 ] > o2[ 0 ] ) {
                                              return 1;
                                          }
                                          else if ( o2[ 0 ] > o1[ 0 ] ) {
                                              return -1;
                                          }
                                          else {
                                              return 0;
                                          }
                                      } )
                                      .findIndex( o => o[ 1 ] === dragItem.id );
                              }

                              // if something has really changed (parent, position within) apply drop effect
                              if ( oldParentLayoutStructure?.layoutElementId !== newParentLayoutStructure.layoutElementId || oldIndex !== newIndex ) {
                                  droppedLayoutElementId.current = dragItem.id;
                              }

                              // select dropped item
                              lastParams.current?.onSelectLayoutElement?.( dragItem.id );
                          }
                      }
                  }

                  // remove
                  else if ( !dragInfo.current.lastDragOverLayoutElementId && oldParentLayoutStructure ) {
                      if ( debug ) {
                          console.log( '-> processing delete' );
                      }

                      const dragLayoutStructure  = dragInfo.current?.dragLayoutStructure ?? newDragInfo.current?.layoutStructure ?? getLayoutStructureForLayoutElement( dragInfo.current.dragLayoutElementId, layoutStructure ) as ContainerLayoutStructure | FormFieldLayoutStructureAndDataBinding | undefined,
                            dragItem             = global.data[ configDataContext ].items[ dragInfo.current.dragLayoutElementId ],
                            parentItem           = global.data[ configDataContext ].items[ oldParentLayoutStructure.layoutElementId ],
                            propertyTypeIdString = String( metaModel[ 'CFG' ].getPropertyType( 'Order' )?.id ?? 0 );

                      if ( dragItem && dragLayoutStructure && parentItem ) {
                          const recursiveDelete = ( oldParentItem : Item, layoutStructureToDelete : ContainerLayoutStructure | TabContainerLayoutStructure | FormFieldLayoutStructureAndDataBinding ) => {
                              const oldItem = global.data[ configDataContext ].items[ layoutStructureToDelete.layoutElementId ];

                              if ( oldItem && oldParentItem ) {
                                  const referenceSetId = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( oldItem.itemTypeId, oldParentItem.itemTypeId, 'belongsTo', 'src' )?.[ 0 ]?.id,
                                        oldReference   = referenceSetId ? dataUtils.findReferences( configDataContext, referenceSetId, oldItem.id, oldParentItem.id )?.[ 0 ] : undefined;

                                  if ( oldReference ) {
                                      if ( debug ) {
                                          console.log( '    -> deleting item', oldItem.toJSON() );
                                          console.log( '    -> deleting reference', oldReference.toJSON() );
                                      }

                                      dispatch( deleteItem(
                                          configDataContext,
                                          oldItem.id,
                                          undefined,
                                          additionalElusiveDataContexts
                                      ) );

                                      dispatch( deleteReference(
                                          configDataContext,
                                          oldReference.id,
                                          undefined,
                                          additionalElusiveDataContexts
                                      ) );

                                      // delete all other references
                                      for ( const referenceId of oldItem.references ) {
                                          dispatch( deleteReference(
                                              configDataContext,
                                              referenceId,
                                              undefined,
                                              additionalElusiveDataContexts
                                          ) );
                                      }

                                      if ( layoutStructureToDelete.type === 'CONTAINER' || layoutStructureToDelete.type === 'TABCONTAINER' ) {
                                          for ( const childLayoutStructure of layoutStructureToDelete.children ) {
                                              if ( debug ) {
                                                  console.log( '    -> deleting child layout structure' );
                                              }

                                              recursiveDelete( oldItem, childLayoutStructure );
                                          }
                                      }
                                  }
                              }
                          };

                          // delete layoutStructure recursive
                          if ( debug ) {
                              console.log( '    -> deleting drag layout structure' );
                          }

                          recursiveDelete( parentItem, dragLayoutStructure );

                          // set orders
                          if ( debug ) {
                              console.log( '    -> reordering layout elements in parent' );
                          }

                          for ( const childLayoutStructure of oldParentLayoutStructure.children ) {
                              const childItem          = global.data[ configDataContext ].items[ childLayoutStructure.layoutElementId ],
                                    childLayoutElement = dragInfo.current.layoutElements![ childLayoutStructure.layoutElementId ] as LoadContainerData | FormFieldData;

                              if ( childItem && childLayoutElement ) {
                                  const data : ItemAttributesSettable = {
                                      properties: {
                                          [ propertyTypeIdString ]: {
                                              value: childLayoutElement.data.order
                                          }
                                      }
                                  };

                                  dispatch( updateItem(
                                      configDataContext,
                                      childItem.id,
                                      data,
                                      additionalElusiveDataContexts
                                  ) );
                              }
                          }

                          // unselect dropped item
                          lastParams.current?.onSelectLayoutElement?.( undefined );
                      }
                  }
              }

              // cleanup
              dispatch( clearElusiveData( uid ) );

              if ( debug ) {
                  console.log( `%c${Math.round( 1000 * ( performance.now() - innerStartTime ) ) / 1000} ms%c`.asLogLine( 100 ), `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: unset;' );
                  console.groupEnd();
              }
          },
          onMouseEnter                                         = ( layoutElementId : number, e : React.MouseEvent<HTMLDivElement> ) : void => {
              const layoutElementsToUse = lastParams.current?.layoutElements,
                    layoutElement       = layoutElementsToUse[ layoutElementId ];

              if ( debug ) {
                  console.groupCollapsed( `%c${`%conMouseEnter - ${layoutElements?.[ layoutElementId ?? 0 ]?.data?.key ?? layoutElementId}%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.mouseFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              e.stopPropagation();

              if ( lastParams.current?.hoverLayoutElementId !== layoutElementId ) {
                  if ( debug ) {
                      console.log( '-> set hoverLayoutElementId', layoutElementId );
                  }

                  setHoverLayoutElementId( layoutElementId );
                  lastParams.current?.onMarkFieldsForAlias?.( layoutElement?.dataDescriptorAlias );
              }

              if ( debug ) {
                  console.groupEnd();
              }
          },
          onMouseLeave                                         = ( layoutElementId : number, e : React.MouseEvent<HTMLDivElement> ) : void => {
              const layoutStructureToUse = lastParams.current?.layoutStructure,
                    layoutElementsToUse  = lastParams.current?.layoutElements,
                    layoutElement        = layoutElementsToUse[ layoutElementId ];

              if ( debug ) {
                  console.groupCollapsed( `%c${`%conMouseLeave - ${layoutElement?.data?.key ?? layoutElementId}%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.mouseFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              if ( lastParams.current?.hoverLayoutElementId === layoutElementId ) {
                  // check if layoutElement has parent(s) and is mouse within them -> set hover to parent
                  let parentLayoutStructure = layoutStructureToUse ? getParentLayoutStructureForLayoutElement( layoutElementId, layoutStructureToUse ) : undefined,
                      newHoverLayoutElementId : number | undefined;

                  while ( parentLayoutStructure ) {
                      const element : Element | null | undefined = document.getElementById( `leid_${parentLayoutStructure.layoutElementId}` ),
                            elementBox                           = element?.getBoundingClientRect();

                      if (
                          elementBox
                          && e.pageX >= elementBox.x && e.pageX <= ( elementBox.x + elementBox.width )
                          && e.pageY >= elementBox.y && e.pageY <= ( elementBox.y + elementBox.height )
                      ) {
                          newHoverLayoutElementId = parentLayoutStructure.layoutElementId;
                          parentLayoutStructure = undefined;
                      }
                      else {
                          parentLayoutStructure = layoutStructureToUse ? getParentLayoutStructureForLayoutElement( parentLayoutStructure.layoutElementId, layoutStructureToUse ) : undefined;
                      }
                  }

                  if ( !newHoverLayoutElementId ) {
                      if ( debug ) {
                          console.log( '-> clear hoverLayoutElementId' );
                      }

                      setHoverLayoutElementId( undefined );
                      lastParams.current?.onMarkFieldsForAlias?.( undefined );
                  }
                  else {
                      if ( debug ) {
                          console.log( '-> set hoverLayoutElementId', newHoverLayoutElementId );
                      }

                      setHoverLayoutElementId( newHoverLayoutElementId );
                      lastParams.current?.onMarkFieldsForAlias?.( layoutElement?.dataDescriptorAlias );
                  }
              }

              if ( debug ) {
                  console.groupEnd();
              }
          },
          onMouseMove                                          = ( e : MouseEvent ) : void => {
              if ( debug ) {
                  console.groupCollapsed( `%c${`%conMouseMove%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              const innerStartTime = debug ? performance.now() : 0;

              let layoutElementId : number | undefined,
                  position : InsertPosition | undefined,
                  endUp : boolean = false;

              if ( dragInfo.current ) {
                  // set position of drag ghost
                  dragInfo.current.dragElement.style.left = ( e.pageX - 50 ) + 'px';
                  dragInfo.current.dragElement.style.top = ( e.pageY - 50 ) + 'px';

                  // scroll form - end up on scrolling
                  const scrollElement    = document.getElementById( `${uid}_form` )?.parentElement,
                        scrollElementBox = scrollElement?.getBoundingClientRect();

                  if ( scrollIntervallId.current ) {
                      clearInterval( scrollIntervallId.current );
                  }

                  if ( scrollElement && scrollElementBox ) {
                      if ( scrollElement.scrollHeight > scrollElement.clientHeight || scrollElement.scrollWidth > scrollElement.clientWidth ) {
                          if (
                              e.pageX > scrollElementBox.x && e.pageX < ( scrollElementBox.x + scrollElementBox.width )
                              && e.pageY < ( scrollElementBox.y + .1 * scrollElement.clientHeight ) && currentScrollTop.current > 1
                          ) {
                              if ( debug ) {
                                  console.log( '    -> scroll form up' );
                              }

                              dispatch( setParam( uid, 'scrollTop', Math.max( 0, currentScrollTop.current - 25 ) ) );

                              scrollIntervallId.current = setInterval(
                                  () => {
                                      dispatch( setParam( uid, 'scrollTop', Math.max( 0, currentScrollTop.current - 25 ) ) );
                                  },
                                  25
                              );

                              endUp = true;
                          }
                          else if (
                              e.pageX > scrollElementBox.x && e.pageX < ( scrollElementBox.x + scrollElementBox.width )
                              && e.pageY > ( scrollElementBox.y + .9 * scrollElement.clientHeight ) && currentScrollTop.current < ( scrollElement.scrollHeight - scrollElement.clientHeight ) - 1
                          ) {
                              if ( debug ) {
                                  console.log( '    -> scroll form down' );
                              }

                              dispatch( setParam( uid, 'scrollTop', Math.min( scrollElement.scrollHeight - scrollElement.clientHeight, currentScrollTop.current + 25 ) ) );

                              scrollIntervallId.current = setInterval(
                                  () => {
                                      dispatch( setParam( uid, 'scrollTop', Math.min( scrollElement.scrollHeight - scrollElement.clientHeight, currentScrollTop.current + 25 ) ) );
                                  },
                                  25
                              );

                              endUp = true;
                          }
                          if (
                              e.pageY > scrollElementBox.y && e.pageY < ( scrollElementBox.y + scrollElementBox.height )
                              && e.pageX < ( scrollElementBox.x + .1 * scrollElement.clientWidth ) && currentScrollLeft.current > 1
                          ) {
                              if ( debug ) {
                                  console.log( '    -> scroll form left' );
                              }

                              dispatch( setParam( uid, 'scrollLeft', Math.max( 0, currentScrollLeft.current - 25 ) ) );

                              scrollIntervallId.current = setInterval(
                                  () => {
                                      dispatch( setParam( uid, 'scrollLeft', Math.max( 0, currentScrollLeft.current - 25 ) ) );
                                  },
                                  25
                              );

                              endUp = true;
                          }
                          else if (
                              e.pageY > scrollElementBox.y && e.pageY < ( scrollElementBox.y + scrollElementBox.height )
                              && e.pageX > ( scrollElementBox.x + .9 * scrollElement.clientWidth ) && currentScrollLeft.current < ( scrollElement.scrollWidth - scrollElement.clientWidth ) - 1
                          ) {
                              if ( debug ) {
                                  console.log( '    -> scroll form right' );
                              }

                              dispatch( setParam( uid, 'scrollLeft', Math.min( scrollElement.scrollWidth - scrollElement.clientWidth, currentScrollLeft.current + 25 ) ) );

                              scrollIntervallId.current = setInterval(
                                  () => {
                                      dispatch( setParam( uid, 'scrollLeft', Math.min( scrollElement.scrollWidth - scrollElement.clientWidth, currentScrollLeft.current + 25 ) ) );
                                  },
                                  25
                              );

                              endUp = true;
                          }
                      }
                  }

                  // get drag over layoutElement
                  if ( !endUp && dragInfo.current.layoutStructure && dragInfo.current.layoutElements ) {

                      // drag over tab - end up on over (if not a container)
                      if ( e.target ) {
                          const tabElements = document.querySelectorAll( `#${uid}_form .p-tabview-nav .yn-form-tabpanel` );

                          for ( let i = 0; i < tabElements.length; i++ ) {
                              const tabElement     = tabElements[ i ],
                                    tabBoundingBox = tabElement.getBoundingClientRect();

                              if (
                                  e.pageX > tabBoundingBox.x && e.pageX < ( tabBoundingBox.x + tabBoundingBox.width )
                                  && e.pageY > tabBoundingBox.y && e.pageY < ( tabBoundingBox.y + tabBoundingBox.height )
                              ) {
                                  const tabLayoutElementId       = Number( tabElement?.getAttribute( 'data-layoutelementid' ) ?? 0 ),
                                        tabParentLayoutElementId = getParentLayoutStructureForLayoutElement( tabLayoutElementId, dragInfo.current.layoutStructure )?.layoutElementId;

                                  if ( tabElement && tabParentLayoutElementId ) {
                                      const tabLayoutElementIndex : number = Array.from( tabElement.parentElement?.children ?? [] ).findIndex( childElement => childElement === tabElement );

                                      if ( tabLayoutElementIndex !== -1 ) {
                                          const dragLayoutElement = dragInfo.current.layoutElements[ dragInfo.current.dragLayoutElementId ] as LoadContainerData | LoadTabContainerData | FormFieldData | undefined;

                                          if ( dragLayoutElement?.type !== 'CONTAINER' ) {
                                              // mark drag over element (hover)
                                              if ( lastParams.current?.hoverLayoutElementId !== tabLayoutElementId ) {
                                                  if ( debug ) {
                                                      console.log( '    -> set hoverLayoutElementId' );
                                                  }

                                                  setHoverLayoutElementId( tabLayoutElementId );
                                              }

                                              endUp = true;
                                          }
                                          else {
                                              layoutElementId = tabLayoutElementId;

                                              position = 'before';
                                              if ( ( e.pageY - tabBoundingBox.y ) > ( -1 * ( tabBoundingBox.height / tabBoundingBox.width ) * ( e.pageX - tabBoundingBox.x ) + tabBoundingBox.height ) ) {
                                                  position = 'after';
                                              }
                                          }

                                          if ( debug ) {
                                              console.log( '    -> set activeTabContainerIndexes' );
                                              console.log( '        tabContainerLayoutElementId', tabParentLayoutElementId );
                                              console.log( '        index', tabLayoutElementIndex );
                                          }

                                          if ( lastParams.current.activeTabContainerIndexes[ tabParentLayoutElementId ] !== tabLayoutElementIndex ) {
                                              dispatch( setParam( uid, 'activeTabContainerIndexes', { ...lastParams.current.activeTabContainerIndexes, [ tabParentLayoutElementId ]: tabLayoutElementIndex } ) );
                                          }

                                          break;
                                      }
                                  }
                              }
                          }
                      }

                      if ( !endUp ) {
                          const cleanMarker = () => {
                              hideDropZones();

                              // unmark drag element
                              dragInfo.current?.dragElement.classList.remove( 'yn-formdesigner-drag-ok', 'yn-formdesigner-drag-nothing', 'yn-formdesigner-drag-delete' );
                          };

                          // no layoutElementId -> get the topmost layoutElement under pointer
                          if ( !layoutElementId ) {
                              layoutElementId = getTopMostLayoutElementIdAtPoint( dragInfo.current.layoutStructure?.children, e.pageX, e.pageY, dragInfo.current.dragLayoutElementId );
                          }

                          // no layoutElementId and pointer within form -> set to form layoutElementId
                          if (
                              !layoutElementId && scrollElement && scrollElementBox
                              && e.pageX > scrollElementBox.x && e.pageX < ( scrollElementBox.x + scrollElementBox.width )
                              && e.pageY > scrollElementBox.y && e.pageY < ( scrollElementBox.y + scrollElementBox.height )
                          ) {
                              layoutElementId = dragInfo.current.layoutStructure?.layoutElementId ?? lastParams.current?.layoutStructure?.layoutElementId;
                          }

                          if ( debug ) {
                              console.log( '    layoutElementId', layoutElementId );
                          }

                          // drag over layoutElement found
                          if ( layoutElementId ) {
                              // mark drag over element (hover)
                              if ( lastParams.current?.hoverLayoutElementId !== layoutElementId ) {
                                  if ( debug ) {
                                      console.log( '    -> set hoverLayoutElementId' );
                                  }

                                  setHoverLayoutElementId( layoutElementId );
                              }

                              // dragged layoutElement not the drag over layoutElement
                              if ( dragInfo.current.dragLayoutElementId !== layoutElementId ) {
                                  if ( !position ) {
                                      position = getInsertPosition( layoutElementId, e.pageX, e.pageY );
                                  }

                                  if ( debug ) {
                                      console.log( '    position', position );
                                  }

                                  // drop target layoutElement or position changed
                                  if ( position && ( layoutElementId !== dragInfo.current.lastDragOverLayoutElementId || position !== dragInfo.current.position ) ) {
                                      dragInfo.current.lastDragOverLayoutElementId = layoutElementId;
                                      dragInfo.current.position = position;

                                      if ( timeoutId.current ) {
                                          clearTimeout( timeoutId.current );
                                      }
                                      timeoutId.current = setTimeout(
                                          () => {
                                              clearTimeout( timeoutId.current );

                                              cleanMarker();

                                              // handle drag over
                                              onDragOver( layoutElementId!, position! );
                                          },
                                          100
                                      );
                                  }
                              }
                          }
                          else if ( !layoutElementId ) {
                              delete dragInfo.current.lastDragOverLayoutElementId;
                              delete dragInfo.current.position;

                              if ( timeoutId.current ) {
                                  clearTimeout( timeoutId.current );
                              }

                              // dragged to drag out (delete) zone
                              if (
                                  scrollElementBox
                                  && e.pageX < scrollElementBox.x
                                  && e.pageY > scrollElementBox.y
                              ) {
                                  timeoutId.current = setTimeout(
                                      () => {
                                          clearTimeout( timeoutId.current );

                                          cleanMarker();

                                          // handle delete
                                          onDragToDelete();
                                      },
                                      100
                                  );
                              }
                              else {
                                  timeoutId.current = setTimeout(
                                      () => {
                                          clearTimeout( timeoutId.current );

                                          cleanMarker();

                                          // handle drag out
                                          onDragOut();
                                      },
                                      100
                                  );
                              }
                          }
                      }
                  }
              }

              if ( debug ) {
                  console.log( `%c${Math.round( 1000 * ( performance.now() - innerStartTime ) ) / 1000} ms%c`.asLogLine( 100 ), `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: unset;' );
                  console.groupEnd();
              }
          },
          onMouseUp                                            = ( e : MouseEvent | React.MouseEvent<HTMLDivElement> ) : void => {
              if ( debug ) {
                  console.log( `%c${`%conMouseUp%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              // remove listeners for drag control
              document.querySelector( 'html' )?.removeEventListener( 'mouseup', onMouseUp );
              document.querySelector( 'html' )?.removeEventListener( 'mousemove', onMouseMove );

              // destroy drag ghost
              if ( dragInfo.current?.dragElement ) {
                  document.body.removeChild( dragInfo.current.dragElement );
              }

              setTimeout(
                  () => {
                      hideDropZones();
                  },
                  1
              );

              // TODO: insertPositionMarker enable if required
              // destroy insertPositionMarker
              /*if ( dragInfo.current?.insertMarkerElement ) {
                  document.body.removeChild( dragInfo.current.insertMarkerElement );
              }*/

              onDrop();

              // clean drag info
              dragInfo.current = null;
              // clean new drag info
              newDragInfo.current = null;

              setHoverLayoutElementId( undefined );
              lastParams.current.onMarkFieldsForAlias?.( undefined );
          },
          onFormClick                                          = ( e : React.MouseEvent<HTMLDivElement> ) => {
              lastParams.current?.onSelectLayoutElement?.( undefined );
          },
          onFormLeave                                          = ( e : React.MouseEvent<HTMLDivElement> ) : void => {
              setHoverLayoutElementId( undefined );
              lastParams.current?.onMarkFieldsForAlias?.( undefined );
          },
          onClick                                              = ( layoutElementId : number | undefined, e : React.MouseEvent<HTMLDivElement> ) : void => {
              const layoutStructureToUse = lastParams.current?.layoutStructure,
                    layoutElementsToUse  = lastParams.current?.layoutElements;

              if ( debug ) {
                  console.log( `%c${`%conClick - ${layoutElementsToUse?.[ layoutElementId ?? 0 ]?.data?.key ?? layoutElementId}%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.mouseFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              // select layoutelement if not selected
              if ( lastParams.current?.selectedLayoutElementId !== layoutElementId ) {
                  if ( debug ) {
                      console.log( '-> set selected' );
                  }

                  lastParams.current?.onSelectLayoutElement?.( layoutElementId );
              }

              // check if delete is clicked
              else if ( layoutElementId && layoutStructureToUse ) {
                  const target             = e.target as HTMLElement,
                        targetBoundingBox  = target.getBoundingClientRect(),
                        deleteElementStyle = window.getComputedStyle( target, ':before' ),
                        deleteElementBox   = targetBoundingBox && deleteElementStyle
                            ? {
                                x     : targetBoundingBox.x + parseFloat( deleteElementStyle.left ),
                                width : parseFloat( deleteElementStyle.width ) + parseFloat( deleteElementStyle.paddingLeft ) + parseFloat( deleteElementStyle.paddingRight ) + parseFloat( deleteElementStyle.marginLeft ) + parseFloat( deleteElementStyle.marginRight ),
                                y     : targetBoundingBox.y + parseFloat( deleteElementStyle.top ),
                                height: parseFloat( deleteElementStyle.height ) + parseFloat( deleteElementStyle.paddingTop ) + parseFloat( deleteElementStyle.paddingBottom ) + parseFloat( deleteElementStyle.marginTop ) + parseFloat( deleteElementStyle.marginBottom )
                            }
                            : undefined;

                  if (
                      deleteElementBox
                      && e.pageX > deleteElementBox.x
                      && e.pageX < ( deleteElementBox.x + deleteElementBox.width )
                      && e.pageY > deleteElementBox.y
                      && e.pageY < ( deleteElementBox.y + deleteElementBox.height )
                  ) {
                      const itemToDelete          = global.data[ configDataContext ].items[ layoutElementId ],
                            itemLayoutStructure   = getLayoutStructureForLayoutElement( layoutElementId, layoutStructureToUse ) as ContainerLayoutStructure | TabContainerLayoutStructure | FormFieldLayoutStructureAndDataBinding | undefined,
                            parentLayoutStructure = JSON.parse( JSON.stringify( getParentLayoutStructureForLayoutElement( layoutElementId, layoutStructureToUse ) ) ) as ContainerLayoutStructure | TabContainerLayoutStructure | undefined,
                            parentItem            = parentLayoutStructure
                                ? global.data[ configDataContext ].items[ parentLayoutStructure.layoutElementId ]
                                : undefined;

                      if ( itemToDelete && itemLayoutStructure && parentItem && parentLayoutStructure ) {
                          if ( debug ) {
                              console.log( '-> delete' );
                          }

                          const propertyTypeIdString                               = String( metaModel[ 'CFG' ].getPropertyType( 'Order' )?.id ?? 0 ),
                                children : ( LoadContainerData | FormFieldData )[] = [],
                                recursiveDelete                                    = ( oldParentItem : Item, layoutStructureToDelete : ContainerLayoutStructure | TabContainerLayoutStructure | FormFieldLayoutStructureAndDataBinding ) => {
                                    const oldItem = global.data[ configDataContext ].items[ layoutStructureToDelete.layoutElementId ];

                                    if ( oldItem && oldParentItem ) {
                                        const referenceSetId = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( oldItem.itemTypeId, oldParentItem.itemTypeId, 'belongsTo', 'src' )?.[ 0 ]?.id,
                                              oldReference   = referenceSetId ? dataUtils.findReferences( configDataContext, referenceSetId, oldItem.id, oldParentItem.id )?.[ 0 ] : undefined;

                                        if ( oldReference ) {
                                            if ( debug ) {
                                                console.log( '    -> deleting item', oldItem.toJSON() );
                                                console.log( '    -> deleting reference', oldReference.toJSON() );
                                            }

                                            dispatch( deleteItem(
                                                configDataContext,
                                                oldItem.id,
                                                undefined,
                                                additionalElusiveDataContexts
                                            ) );

                                            dispatch( deleteReference(
                                                configDataContext,
                                                oldReference.id,
                                                undefined,
                                                additionalElusiveDataContexts
                                            ) );

                                            // delete all other references
                                            for ( const referenceId of oldItem.references ) {
                                                dispatch( deleteReference(
                                                    configDataContext,
                                                    referenceId,
                                                    undefined,
                                                    additionalElusiveDataContexts
                                                ) );
                                            }

                                            if ( layoutStructureToDelete.type === 'CONTAINER' || layoutStructureToDelete.type === 'TABCONTAINER' ) {
                                                for ( const childLayoutStructure of layoutStructureToDelete.children ) {
                                                    if ( debug ) {
                                                        console.log( '    -> deleting child layout structure' );
                                                    }

                                                    recursiveDelete( oldItem, childLayoutStructure );
                                                }
                                            }
                                        }
                                    }
                                };

                          let index : number;

                          // delete layoutStructure recursive
                          if ( debug ) {
                              console.log( '    -> deleting drag layout structure' );
                          }

                          recursiveDelete( parentItem, itemLayoutStructure );

                          // reorder layoutElements in parentLayoutStructure
                          if ( debug ) {
                              console.log( '    -> reordering layout elements in parent' );
                          }
                          for ( const childLayoutStructure of parentLayoutStructure.children ) {
                              if ( childLayoutStructure.layoutElementId !== itemToDelete.id ) {
                                  const childLayoutElement = layoutElementsToUse[ childLayoutStructure.layoutElementId ] as LoadContainerData | FormFieldData | undefined;

                                  if ( childLayoutElement ) {
                                      children.push( childLayoutElement );
                                  }
                              }
                          }

                          children.sort( orderLayoutElements );

                          for ( index = 0; index < children.length; index++ ) {
                              const childItem = global.data[ configDataContext ].items[ children[ index ].data.id ];

                              if ( childItem ) {
                                  const data : ItemAttributesSettable = {
                                      properties: {
                                          [ propertyTypeIdString ]: {
                                              value: 10 * ( index + 1 )
                                          }
                                      }
                                  };

                                  dispatch( updateItem(
                                      configDataContext,
                                      childItem.id,
                                      data,
                                      additionalElusiveDataContexts
                                  ) );
                              }
                          }


                          // update elusiveData
                          dispatch( setElusiveData( uid, {
                              layoutStructure: undefined,
                              layoutElements : undefined
                          } ) );

                          // unselect dropped item
                          lastParams.current?.onSelectLayoutElement?.( undefined );
                      }
                  }
              }

              e.preventDefault();
              e.stopPropagation();
          },
          onDragStart                                          = ( layoutElementId : number, e : React.DragEvent<HTMLElement>, options? : DragOptions ) : void => {
              const innerStartTime          = debug ? performance.now() : 0,
                    layoutStructureToUse    = lastParams.current?.layoutStructure,
                    layoutElementsToUse     = lastParams.current?.layoutElements,
                    layoutElementsPropToUse = lastParams.current?.layoutElementsProp,
                    dataDescriptorsToUse    = lastParams.current?.dataDescriptors,
                    tabElements             = document.querySelectorAll( `#${uid}_form .p-tabview-nav .yn-form-tabpanel` );

              // no drag handling if a dataTable header column or its children is dragged
              if ( ( e.target as HTMLElement ).classList.contains( 'yn-header-column' ) ) {
                  return;
              }

              for ( let i = 0; i < tabElements.length; i++ ) {
                  const tabElement     = tabElements[ i ],
                        tabBoundingBox = tabElement.getBoundingClientRect();

                  if (
                      e.pageX > tabBoundingBox.x && e.pageX < ( tabBoundingBox.x + tabBoundingBox.width )
                      && e.pageY > tabBoundingBox.y && e.pageY < ( tabBoundingBox.y + tabBoundingBox.height )
                  ) {
                      const tabLayoutElementId = Number( tabElement?.getAttribute( 'data-layoutelementid' ) ?? 0 );
                      if ( tabLayoutElementId ) {
                          layoutElementId = tabLayoutElementId;
                      }
                      break;
                  }
              }

              if ( debug ) {
                  console.groupCollapsed( `%c${`%conDragStart - ${layoutElementsToUse?.[ layoutElementId ?? 0 ]?.data?.key ?? layoutElementId}%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.preventDefault();

              // init drag info
              dragInfo.current = {
                  dragLayoutElementId      : layoutElementId,
                  dragLayoutStructure      : layoutStructureToUse ? getLayoutStructureForLayoutElement( layoutElementId, layoutStructureToUse ) as ContainerLayoutStructure | FormFieldLayoutStructureAndDataBinding | undefined : undefined,
                  dragParentLayoutElementId: ( layoutStructureToUse ? getParentLayoutStructureForLayoutElement( layoutElementId, layoutStructureToUse ) : undefined )?.layoutElementId ?? 0,
                  layoutStructure          : layoutStructureToUse,
                  layoutElements           : { ...layoutElementsToUse, ...newDragInfo.current?.layoutElements },
                  dataDescriptors          : newDragInfo.current?.dataDescriptors ? { ...dataDescriptorsToUse, ...newDragInfo.current.dataDescriptors } : undefined,
                  dataDescriptorsMetaModel : newDragInfo.current?.dataDescriptors ? buildDataDescriptorsMetaModel( undefined, { ...dataDescriptorsToUse, ...newDragInfo.current.dataDescriptors } ) : undefined,
                  isRepeatedForDataObjectId: newDragInfo.current?.data.isRepeatedForDataObjectId,
                  dragElement              : document.createElement( 'div' ),
                  insertMarkerElement      : document.createElement( 'div' ),
                  doNothing                : true
              };

              if ( newDragInfo.current ) {
                  // use only for init drag info
                  delete newDragInfo.current.layoutElements;
                  delete newDragInfo.current.dataDescriptors;
              }

              // create drag ghost
              const fieldPreview       = document.createElement( 'div' ),
                    layoutElement      = dragInfo.current.layoutElements![ layoutElementId ],
                    layoutElementProps = layoutElementsPropToUse?.[ layoutElementId ],
                    layoutElementLabel = options?.label
                        ?? ( layoutElementProps?.label
                                ? getMessageTemplateStringResolver()( layoutElementProps.label )
                                : ( layoutElementProps?.messages?.title
                                        ? getMessageTemplateStringResolver()( layoutElementProps.messages.title )
                                        : layoutElementTypeToItemTypeMapping[ layoutElement.type ]
                                )
                        ),
                    layoutElementIcon  = getIconStringSpan( options?.icon ?? `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( layoutElementTypeToItemTypeMapping[ layoutElement.type ] )?.icon}` );

              dragInfo.current.dragElement.id = 'drag';
              dragInfo.current.dragElement.className = 'yn-formdesigner-drag';
              dragInfo.current.dragElement.style.left = ( e.pageX - 50 ) + 'px';
              dragInfo.current.dragElement.style.top = ( e.pageY - 50 ) + 'px';
              document.body.appendChild( dragInfo.current.dragElement );

              fieldPreview.id = 'drag_preview';
              fieldPreview.className = 'yn-formdesigner-drag-preview p-component yn-fieldlike';
              fieldPreview.innerHTML = `${layoutElementIcon}${layoutElementLabel}`;
              dragInfo.current.dragElement.appendChild( fieldPreview );

              // TODO: insertPositionMarker enable if required
              /*dragInfo.current.insertMarkerElement.id = 'insertMarker';
                        dragInfo.current.insertMarkerElement.className = 'yn-formdesigner-insert-marker';
                        document.body.appendChild( dragInfo.current.insertMarkerElement );*/

              // add listeners for drag control
              document.querySelector( 'html' )?.addEventListener( 'mousemove', onMouseMove );
              document.querySelector( 'html' )?.addEventListener( 'mouseup', onMouseUp );

              if ( debug ) {
                  console.log( `%c${Math.round( 1000 * ( performance.now() - innerStartTime ) ) / 1000} ms%c`.asLogLine( 100 ), `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: unset;' );
                  console.groupEnd();
              }
          },
          onTabContainerTabChange                              = ( realLayoutElementId : number | string, referenceToTabIndexMapping : {
              [ referenceId : number ] : number;
          } | undefined, event : TabViewTabChangeEvent ) => {
              const layoutElementsToUse = lastParams.current?.layoutElements,
                    { layoutElementId } = getLayoutElementIdAndParentIdFromRealLayoutElementId( realLayoutElementId );

              let element : HTMLElement | undefined | null = event.originalEvent.target as HTMLElement,
                  tabLayoutElementId : string | null       = null,
                  layoutElementTabIndex                    = event.index;

              // map the real index given by event to referenceId if
              // (so visible tab stays on the reference even information changing the tab sort is changed)
              if ( referenceToTabIndexMapping ) {
                  for ( const index of Object.keys( referenceToTabIndexMapping ) ) {
                      if ( referenceToTabIndexMapping[ index ] === event.index ) {
                          layoutElementTabIndex = index;
                          break;
                      }
                  }
              }

              if ( debug ) {
                  console.groupCollapsed( `%c${`%conTabContainerTabChange - ${layoutElementsToUse?.[ layoutElementId ?? 0 ]?.data?.key ?? layoutElementId}%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.mouseFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              dispatch( setParam( uid, 'activeTabContainerIndexes', { ...activeTabContainerIndexes, [ realLayoutElementId ]: layoutElementTabIndex } ) );

              // activate container belonging to clicked header
              while ( element ) {
                  tabLayoutElementId = element.getAttribute( 'data-layoutelementid' );
                  if ( !tabLayoutElementId ) {
                      element = element.parentElement;
                  }
                  else {
                      element = undefined;
                  }
              }

              if ( tabLayoutElementId ) {
                  if ( debug ) {
                      console.log( 'tabLayoutElementId', tabLayoutElementId );
                  }

                  lastParams.current?.onSelectLayoutElement?.( Number( tabLayoutElementId ) );
              }
              else if ( debug ) {
                  console.log( 'no tabLayoutElementId to select found' );
              }

              event.originalEvent.stopPropagation();

              if ( debug ) {
                  console.groupEnd();
              }
          },
          onValueChange                                        = ( layoutElementId : number, value : AttributeValue | EnumUpdateValue | ReferenceValue | ReferenceValue[] | undefined, originalEvent : any ) : void => {

          },
          onIntermediateValueChange                            = ( layoutElementId : number, value : AttributeValue | EnumUpdateValue | ReferenceValue | ReferenceValue[] | undefined, originalEvent : any ) : void => {

          },
          onVariableChange                                     = ( layoutElementId : number, value : AttributeValue | EnumUpdateValue | ReferenceValue | ReferenceValue[] | {
              start : string;
              end : string;
          } | undefined, originalEvent : any ) : void => {

          },
          onIntermediateVariableChange                         = ( layoutElementId : number, value : AttributeValue | EnumUpdateValue | ReferenceValue | ReferenceValue[] | {
              start : string;
              end : string;
          } | undefined, originalEvent : any ) : void => {

          },
          onTriggerAction                                      = async ( layoutElementId : number, originalEvent : any ) : Promise<void> => {

          },
          onLayoutElementPropertyChange                        = useCallback(
              ( layoutElementId : number, propertyTypeName : string, value : AttributeValue | undefined ) => {
                  const item                          = global.data[ configDataContext ]?.items?.[ layoutElementId ],
                        propertySet                   = item
                            ? metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( item.itemType.id, propertyTypeName )
                            : undefined,
                        propertyTypeIdString          = String( propertySet?.propertyTypeId ?? '' ),
                        data : ItemAttributesSettable = {
                            properties: {
                                [ propertyTypeIdString ]: {}
                            }
                        };

                  if ( propertyTypeIdString ) {
                      // delete
                      if ( value === '' || value === null || value === undefined ) {
                          data.properties![ propertyTypeIdString ]!.statusId = metaModel[ 'CFG' ].getStatus( 'deleted' )!.id;
                      }
                      // set
                      else {
                          data.properties![ propertyTypeIdString ]!.statusId = metaModel[ 'CFG' ].getStatus( 'active' )!.id;
                          data.properties![ propertyTypeIdString ]!.value = value;
                      }

                      dispatch( updateItem(
                          configDataContext,
                          layoutElementId,
                          data,
                          additionalElusiveDataContexts
                      ) );
                  }
              },
              [
                  additionalElusiveDataContexts,
                  configDataContext
              ]
          ),
          startDrag                                            = ( rootLayoutElementId : number, data : DragData, options : DragOptions, e : React.DragEvent<HTMLElement> ) : void => {
              // wrap given structure in a container
              const rootItem                      = data.items[ rootLayoutElementId ],
                    containerItemTypeId           = metaModel[ 'CFG' ].getItemType( 'Container' )?.id,
                    containerItem                 = containerItemTypeId
                        ? new Item(
                            {
                                itemTypeId: containerItemTypeId
                            },
                            undefined,
                            'CFG',
                            true
                        )
                        : undefined,
                    rootToContainerReferenceSetId = rootItem && containerItemTypeId
                        ? metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( rootItem.itemTypeId, containerItemTypeId, 'belongsTo', 'src' )?.[ 0 ]?.id
                        : undefined,
                    rootToContainerReference      = containerItem && rootToContainerReferenceSetId
                        ? new Reference(
                            {
                                referenceSetId: rootToContainerReferenceSetId,
                                srcItemId     : rootLayoutElementId,
                                destItemId    : containerItem.id
                            },
                            undefined,
                            'CFG',
                            true
                        )
                        : undefined;

              if ( containerItem && rootToContainerReference ) {
                  containerItem.registerReference( rootToContainerReference );

                  const containerData : LoadContainerData = {
                            type       : 'CONTAINER',
                            data       : {
                                id  : containerItem.id,
                                key : '',
                                name: ''
                            },
                            permissions: {}
                        },
                        configDataContextForExtract       = 'temp_formDesigner_startDrag';

                  global.data[ configDataContextForExtract ] = {
                      items     : {
                          ...data.items,
                          [ containerItem.id ]: containerItem
                      },
                      references: {
                          ...data.references,
                          [ rootToContainerReference.id ]: rootToContainerReference
                      },
                      schema    : 'CFG'
                  };

                  const {
                            layoutStructure: newLayoutStructure,
                            layoutElements : newLayoutElements,
                            dataDescriptors: newDataDescriptors
                        } = extractLayoutStructure( containerData, configDataContextForExtract );

                  delete global.data[ configDataContextForExtract ];

                  newDragInfo.current = {
                      data,
                      layoutStructure: newLayoutStructure[ 0 ],
                      layoutElements : newLayoutElements,
                      dataDescriptors: newDataDescriptors
                  };

                  onDragStart( rootLayoutElementId, e, options );
              }
          },
          markLayoutElementsForAlias                           = ( alias : string | string[] | undefined ) : void => {
              if ( typeof alias === 'string' ) {
                  alias = [ alias ];
              }

              setAliasToMark( alias );
          },
          clearRootItemData                                    = () => {
              dispatch( unsetParam( uid, 'rootItemId' ) );
              dispatch( unsetParam( uid, 'rootItemKey' ) );
              dispatch( unsetParam( uid, 'rootItemType' ) );
              dispatch( setElusiveData( uid, { data: undefined } ) );
              delete global.data[ dataContext ];
          },
          setScrollPosition                                    = () => {
              const scrollElement = document.getElementById( `${uid}_form` )?.parentElement;

              if ( scrollElement ) {
                  scrollElement.scrollLeft = currentScrollLeft.current;
                  scrollElement.scrollTop = currentScrollTop.current;
              }
          },
          getScrollPosition                                    = () => {
              const scrollElement = document.getElementById( `${uid}_form` )?.parentElement;

              if ( scrollElement ) {
                  dispatch( setParams( uid, {
                      scrollTop : scrollElement.scrollTop ?? 0,
                      scrollLeft: scrollElement.scrollLeft ?? 0
                  } ) );
              }
          },
          debouncedGetScrollPosition                           = _.debounce( getScrollPosition.bind( null ), 250 ),
          getObjectFromDataAndMetaModelMapping                 = ( dataDescriptorData : DataMapping, dataDescriptorMetaModel : MetaModelMapping ) : Item | Item[] | Property | Property[] | EnumProperty | EnumProperty[] | Reference | Reference[] | ReferenceProperty | ReferenceProperty[] | EnumReferenceProperty | EnumReferenceProperty[] | undefined => {
              let retVal : Item | Item[] | Property | Property[] | EnumProperty | EnumProperty[] | Reference | Reference[] | ReferenceProperty | ReferenceProperty[] | EnumReferenceProperty | EnumReferenceProperty[] | undefined = [],
                  parentObject : Item | Reference | undefined,
                  propertySet : PropertySet | ReferencePropertySet | undefined;

              if ( dataDescriptorData.object ) {
                  switch ( dataDescriptorData.type ) {
                      case 'item':
                          retVal = global.data[ dataContext ].items[ dataDescriptorData.object ];
                          break;

                      case 'property':
                          parentObject = global.data[ dataContext ].items[ dataDescriptorData.object ];
                          propertySet = parentObject
                              ? metaModel[ parentObject.schema ].getPropertySet( dataDescriptorMetaModel.metaModels[ 0 ] ?? 0 )
                              : undefined;
                          retVal = propertySet
                              ? parentObject.properties.get( propertySet?.propertyTypeId ?? 0 )
                              : undefined;
                          break;

                      case 'enumProperty':
                          parentObject = global.data[ dataContext ].items[ dataDescriptorData.object ];
                          propertySet = parentObject
                              ? metaModel[ parentObject.schema ].getPropertySet( dataDescriptorMetaModel.metaModels[ 0 ] ?? 0 )
                              : undefined;
                          retVal = propertySet
                              ? parentObject.enumProperties.get( propertySet?.propertyTypeId ?? 0 )
                              : undefined;
                          break;

                      case 'reference':
                          if ( Array.isArray( dataDescriptorData.object ) ) {
                              retVal = [] as Reference[];

                              for ( const referenceId of dataDescriptorData.object ) {
                                  retVal.push( global.data[ dataContext ].references[ referenceId ] );
                              }
                          }
                          else {
                              retVal = global.data[ dataContext ].references[ dataDescriptorData.object ];
                          }
                          break;

                      case 'referenceProperty':
                          parentObject = global.data[ dataContext ].references[ dataDescriptorData.object ];
                          propertySet = parentObject
                              ? metaModel[ parentObject.schema ].getReferencePropertySet( dataDescriptorMetaModel.metaModels[ 0 ] ?? 0 )
                              : undefined;
                          retVal = propertySet
                              ? parentObject.properties.get( propertySet?.propertyTypeId ?? 0 )
                              : undefined;
                          break;

                      case 'enumReferenceProperty':
                          parentObject = global.data[ dataContext ].references[ dataDescriptorData.object ];
                          propertySet = parentObject
                              ? metaModel[ parentObject.schema ].getReferencePropertySet( dataDescriptorMetaModel.metaModels[ 0 ] ?? 0 )
                              : undefined;
                          retVal = propertySet
                              ? parentObject.enumProperties.get( propertySet?.propertyTypeId ?? 0 )
                              : undefined;
                          break;
                  }
              }

              return retVal;
          },
          getMessageTemplateStringResolver                     = ( additionalResolverParameters? : {
              [ name : string ] : any
          } ) : ( templateString : string, rawMode? : boolean ) => string => {
              let retVal : ( ( templateString : string, rawMode? : boolean ) => string ) | undefined;

              if ( rootItemId ) {
                  const item = global.data[ dataContext ]?.items[ rootItemId ];

                  // TODO: add default for treeParentObject and treeRootObject same as rootObject
                  // TODO: add resolver for all dataObject items and references if needed (see getResolvedDataObjects - key => label)

                  if ( item ) {
                      retVal = messageUtils.getMessageTemplateStringResolverForResolvedDataObjects(
                          item,
                          undefined,
                          {
                              rootObject: {
                                  item
                              }
                          },
                          {
                              variables: {
                                  ...elusiveData?.variables,
                                  [ Symbol.toPrimitive ]: ( hint : 'default' | 'number' | 'string' ) => {
                                      if ( hint === 'string' ) {
                                          return nl2br( JSON.stringify( variables, null, '\u00a0\u00a0\u00a0\u00a0' ) );
                                      }
                                      else {
                                          return null;
                                      }
                                  }
                              },
                              ...additionalResolverParameters
                          }
                      );
                  }
              }

              if ( !retVal ) {
                  retVal = messageUtils.getMessageTemplateStringResolverForData(
                      {},
                      {
                          variables: {
                              ...variables,
                              [ Symbol.toPrimitive ]: ( hint : 'default' | 'number' | 'string' ) => {
                                  if ( hint === 'string' ) {
                                      return nl2br( JSON.stringify( variables, null, '\u00a0\u00a0\u00a0\u00a0' ) );
                                  }
                                  else {
                                      return null;
                                  }
                              }
                          },
                          ...additionalResolverParameters
                      }
                  );
              }

              return retVal;
          },
          buildContent                                         = ( parent : Parent, parentLayoutStructure : FormLayoutStructure | ContainerLayoutStructure | TabContainerLayoutStructure ) : GetContainerContent => {
              if ( debug ) {
                  console.groupCollapsed( `%c${`%cbuild content - ${parent.type}: ${layoutElements?.[ parentLayoutStructure.layoutElementId ]?.data?.key}%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.cacheFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              const innerStartTime                                                = debug ? performance.now() : 0,
                    retVal : GetContainerContent                                  = {
                        isDirty  : false,
                        isInvalid: false,
                        content  : []
                    },
                    containerChildren                                             = parentLayoutStructure.children?.length ? ( [] as ( FormFieldLayoutStructureAndDataBinding | ContainerLayoutStructure | TabContainerLayoutStructure )[] ).concat( parentLayoutStructure.children ) : undefined,
                    rootItem                                                      = global.data[ dataContext ]?.items[ rootItemId ?? 0 ],
                    commonResolvedDataObjectsForMessage : ResolvedDataObjectChain = {
                        rootObject: {
                            item: rootItem
                        }
                    },
                    commonAdditionalResolverParametersForMessage                  = {
                        variables    : {
                            ...variables,
                            [ Symbol.toPrimitive ]: ( hint : 'default' | 'number' | 'string' ) => {
                                // toString
                                if ( hint === 'string' ) {
                                    return nl2br( JSON.stringify( variables, null, '\u00a0\u00a0\u00a0\u00a0' ) );
                                }
                                // all other
                                else {
                                    return null;
                                }
                            }
                        },
                        treeVariables: {},
                        viewKey,
                        rootItemId,
                        rootItemKey,
                        rootItemType,
                        configHash
                    },
                    messageTemplateStringResolver                                 = getMessageTemplateStringResolver();

              let repeatingContainer : boolean = false;

              if ( layoutElements && layoutElementsProp && containerChildren ) {
                  containerChildren.sort( orderLayoutStructure );

                  let containerElementsFlexWidthSum : number     = 0,
                      containerElementsGridUnitWidthSum : number = 0,
                      containerElementsPixelWidthSum : number    = 0,
                      containerElementsRemWidthSum : number      = 0,
                      containerElementsPercentWidthSum : number  = 0,
                      fieldCacheChanged                          = false
                      /*, isPreviousElementFullWidth : boolean       = false*/;

                  for ( const layoutElementStructure of containerChildren ) {
                      const layoutElementProps = layoutElementsProp?.[ layoutElementStructure.layoutElementId ] as ContainerProperties | FormFieldProperties | undefined;

                      if ( layoutElementProps?.width ) {
                          if ( layoutElementProps.width < 0 ) {
                              containerElementsFlexWidthSum += -1 * layoutElementProps.width;
                          }
                          else {
                              switch ( layoutElementProps.widthUnit ) {
                                  case 'gridunit':
                                      containerElementsGridUnitWidthSum += layoutElementProps.width;
                                      break;
                                  case 'pixel':
                                      containerElementsPixelWidthSum += layoutElementProps.width;
                                      break;
                                  case 'rem':
                                      containerElementsRemWidthSum += layoutElementProps.width;
                                      break;
                                  case 'percent':
                                      containerElementsPercentWidthSum += layoutElementProps.width;
                                      break;
                              }
                          }
                      }
                  }

                  const containerElementsFlexWidthFactor = (
                                ( formGridUnitWidth * parent.gridSize
                                    - formGridUnitWidth * .01 * parent.gridSize * containerElementsPercentWidthSum
                                    - formGridUnitWidth * containerElementsGridUnitWidthSum
                                    - remWidth * containerElementsRemWidthSum
                                    - containerElementsPixelWidthSum )
                                / formGridUnitWidth )
                            / containerElementsFlexWidthSum,
                        newFieldCache                    = { ...fieldCache };

                  for ( const layoutElementStructure of containerChildren ) {
                      const layoutElementId   = layoutElementStructure.layoutElementId,
                            layoutElement     = layoutElements[ layoutElementId ],
                            layoutElementData = layoutElementsProp[ layoutElementId ];

                      let {
                              visibility
                          }        = layoutElementData,
                          isHidden = false;

                      let childrenGridSize : number          = parent.gridSize,
                          elementStyle : React.CSSProperties = {};

                      if ( layoutElementData.width ) {
                          if ( layoutElementData.width < 0 ) {
                              childrenGridSize = Math.min( parent.gridSize, Math.floor( -1 * layoutElementData.width * containerElementsFlexWidthFactor ) );
                          }
                          else {
                              switch ( layoutElementData.widthUnit ) {
                                  case 'gridunit':
                                      childrenGridSize = Math.min( parent.gridSize, layoutElementData.width );
                                      break;
                                  case'pixel':
                                      childrenGridSize = Math.min( parent.gridSize, layoutElementData.width / formGridUnitWidth );
                                      break;
                                  case'rem':
                                      childrenGridSize = Math.min( parent.gridSize, remWidth * layoutElementData.width / formGridUnitWidth );
                                      break;
                                  case 'percent':
                                      childrenGridSize = Math.min( parent.gridSize, parent.gridSize / 100 * layoutElementData.width );
                              }
                          }
                      }
                      elementStyle = getFormLayoutStyle( layoutElementData, parent.gridSize );

                      // avoid rendering of hidden components
                      if ( visibility !== 'hidden' || ( mode === 'design' && showHiddenLayoutElements ) ) {
                          if ( visibility !== 'visible' && mode === 'design' && showHiddenLayoutElements ) {
                              visibility = 'visible';
                              isHidden = true;
                          }

                          // containers
                          if ( layoutElementStructure.type === 'CONTAINER' ) {
                              const containerLayoutElementData = layoutElementData as ContainerProperties,
                                    {
                                        messages,
                                        isLayoutWrapper,
                                        titleAlignment
                                    }                          = containerLayoutElementData,
                                    titleVisibility            = messages?.title ? containerLayoutElementData.titleVisibility : 'hidden',
                                    designAttributes           = {
                                        [ 'data-layoutelementid' ]   : layoutElementId,
                                        [ 'data-layoutelementlabel' ]: `${layoutElementData.name ? layoutElementData.name : layoutElementData.key}`,
                                        draggable                    : !readOnly,
                                        onMouseOver                  : onMouseEnter.bind( null, layoutElementId ),
                                        onMouseLeave                 : onMouseLeave.bind( null, layoutElementId ),
                                        onClick                      : onClick.bind( null, layoutElementId ),
                                        onDragStart                  : readOnly
                                            ? undefined
                                            : onDragStart.bind( null, layoutElementId )
                                    };

                              // TODO: implement repeated container
                              /*if ( !parent.isRepeating && repeatConfig ) {
                                  const referenceSet       = metaModel[ schema ].getReferenceSet( repeatConfig.referenceSetId ),
                                        referenceField     = ( () : FormFieldData | undefined => {
                                            const recursiveFindFirstReferenceField = ( children : ( FormFieldLayoutStructureAndDataBinding | ContainerLayoutStructure | TabContainerLayoutStructure )[] ) : FormFieldData | undefined => {
                                                let innerRetVal : FormFieldData | undefined = undefined;

                                                for ( const child of children ) {
                                                    if ( child.type === 'CONTAINER' || child.type === 'TABCONTAINER' ) {
                                                        innerRetVal = recursiveFindFirstReferenceField( child.children );
                                                        if ( innerRetVal ) {
                                                            break;
                                                        }
                                                    }
                                                    else {
                                                        if ( ( layoutElements![ child.layoutElementId ] as FormFieldData ).dataDescriptorAlias === repeatConfig.dataDescriptorAlias ) {
                                                            innerRetVal = layoutElements![ child.layoutElementId ] as FormFieldData;
                                                            break;
                                                        }
                                                    }
                                                }

                                                return innerRetVal;
                                            };

                                            return recursiveFindFirstReferenceField( layoutElementStructure.children );
                                        } )(),
                                        referenceFieldData = referenceField ? layoutElementsProp[ referenceField.data.id ] : undefined,
                                        cardinality        = referenceSet?.cardinality.split( ':' ),
                                        deletedStatusIds   = metaModel[ schema ].getDeletedStatus().map( s => s.id );

                                  let dataAndMetaModel = elusiveData?.dataDescriptorMappings?.dataAndMetaModel?.[ repeatConfig.dataDescriptorAlias ],
                                      repeatIndex      = 0;

                                  // build referenceId to tabIndex mapping
                                  // (so visible tab stays on the reference even information changing the tab sort is changed)
                                  retVal.tabIndexMapping = {};

                                  // chained repeat
                                  if ( dataDescriptors && parent.dataDescriptorAlias ) {
                                      const dataDescriptor       = dataDescriptors[ repeatConfig.dataDescriptorAlias ],
                                            parentDataDescriptor = dataDescriptors[ parent.dataDescriptorAlias ];

                                      if ( dataAndMetaModel && dataDescriptor && parentDataDescriptor ) {
                                          dataAndMetaModel = { ...dataAndMetaModel };

                                          // recompute dataAndModel.objects
                                          if ( parentDataDescriptor.dataObject ) {
                                              const subDataDescriptors = {
                                                        [ repeatConfig.dataDescriptorAlias ]: dataDescriptor
                                                    } as DataDescriptors,
                                                    subDataStructure   = getDataStructure( parentDataDescriptor.dataObject.id, dataStructure );

                                              let dataDescriptorMappings : DataDescriptorMappings | undefined;

                                              if ( subDataStructure ) {
                                                  if ( parent.itemId ) {
                                                      dataDescriptorMappings = subDataStructure
                                                          ? {
                                                              dataAndMetaModel: this.buildDataDescriptorToMetaModelMapping(
                                                                  subDataDescriptors,
                                                                  dataContext,
                                                                  schema,
                                                                  subDataStructure,
                                                                  parent.itemId,
                                                                  user
                                                              )
                                                          } as DataDescriptorMappings
                                                          : undefined;

                                                      if ( dataDescriptorMappings ) {
                                                          dataDescriptorMappings.dataAndMetaModel = this.buildDataDescriptorToDataMapping(
                                                              subDataDescriptors,
                                                              dataDescriptorMappings,
                                                              dataContext,
                                                              schema,
                                                              subDataStructure,
                                                              parent.itemId
                                                          );

                                                          dataAndMetaModel.objects = dataDescriptorMappings.dataAndMetaModel[ repeatConfig.dataDescriptorAlias ].objects;
                                                      }
                                                  }
                                                  else {
                                                      dataAndMetaModel.objects = Array.isArray( dataAndMetaModel.objects )
                                                          ? []
                                                          : {};
                                                  }
                                              }
                                          }
                                      }
                                  }

                                  // empty container if a new reference could be added
                                  if (
                                      referenceField
                                      && referenceSet
                                      && (
                                          referenceFieldData?.addNew
                                          || referenceFieldData?.addExisting
                                      )
                                      && (
                                          ( repeatConfig.referenceDirection === 'SrcToDest' && cardinality?.[ 1 ] !== '1' )
                                          || ( repeatConfig.referenceDirection === 'DestToSrc' && cardinality?.[ 0 ] !== '1' )
                                      )
                                  ) {
                                      const repeatedContainerContent = buildContent(
                                          {
                                              type    : parent.type,
                                              id      : containerLayoutElementData.id,
                                              gridSize: parent.gridSize,

                                              dataDescriptorAlias: repeatConfig.dataDescriptorAlias,
                                              referenceId        : 0,
                                              itemId             : 0,
                                              parentItemId       : parent.itemId,
                                              isRepeating        : true,
                                              index,
                                              hasNoParent        : parent.referenceId === 0,
                                              idSuffix           : parent.idSuffix
                                                  ? `${parent.idSuffix}_${repeatIndex + 1}`
                                                  : `_${repeatIndex + 1}`
                                          },
                                          [ layoutElementStructure ]
                                      );

                                      retVal.isDirty = retVal.isDirty || repeatedContainerContent.isDirty;
                                      retVal.isInvalid = retVal.isInvalid || repeatedContainerContent.isInvalid;
                                      retVal.content = retVal.content.concat( repeatedContainerContent.content );
                                      retVal.tabIndexMapping[ 0 ] = repeatIndex;

                                      repeatIndex++;
                                  }

                                  // repeat container as often as references given
                                  if ( dataAndMetaModel?.type === 'reference' && dataAndMetaModel.objects?.length ) {
                                      // sort references (like ReferenceField does)
                                      const referenceSortMapping : ReferenceSortMapping[] = [],
                                            orderByDefinitions : OrderBy[]                = [],
                                            sortFnc                                       = ( value1 : ReferenceSortMapping, value2 : ReferenceSortMapping, orderByDefinition : OrderBy ) : -1 | 0 | 1 => {
                                                let orderValue1 = value1.item
                                                        ? messageUtils.getMessageTemplateStringResolver( value1.item )( `\${${orderByDefinition.key}}`, true ) ?? ''
                                                        : '',
                                                    orderValue2 = value2.item
                                                        ? messageUtils.getMessageTemplateStringResolver( value2.item )( `\${${orderByDefinition.key}}`, true ) ?? ''
                                                        : '';

                                                // order strings case independent
                                                if ( orderValue1 && typeof orderValue1 === 'string' ) {
                                                    orderValue1 = orderValue1.toLowerCase();
                                                }
                                                if ( orderValue2 && typeof orderValue2 === 'string' ) {
                                                    orderValue2 = orderValue2.toLowerCase();
                                                }

                                                if ( orderValue1 > orderValue2 ) {
                                                    return orderByDefinition.direction === 'asc' ? 1 : -1;
                                                }
                                                else if ( orderValue1 < orderValue2 ) {
                                                    return orderByDefinition.direction === 'asc' ? -1 : 1;
                                                }
                                                else {
                                                    return 0;
                                                }
                                            };

                                      let references : Reference[] = [];

                                      for ( const referenceId of dataAndMetaModel.objects ) {
                                          const reference = global.data[ dataContext ].references[ referenceId ],
                                                itemId    = reference[ repeatConfig.referenceDirection === 'SrcToDest' ? 'destItemId' : 'srcItemId' ],
                                                item      = global.data[ dataContext ].items[ itemId ];

                                          if ( item ) {
                                              let sortLabel : string;

                                              if ( referenceFieldData?.itemLabel ) {
                                                  const labelMessageResolver = messageUtils.getMessageTemplateStringResolver( item, reference ?? referenceSet );

                                                  sortLabel = labelMessageResolver( __( referenceFieldData.itemLabel ) );
                                              }
                                              else {
                                                  sortLabel = item.displayAttributeValue;
                                              }

                                              referenceSortMapping.push( {
                                                  reference,
                                                  item,
                                                  sortLabel
                                              } );
                                          }
                                      }

                                      if ( referenceFieldData?.orderBy ) {
                                          const orderByParts = referenceFieldData.orderBy.split( ',' );

                                          for ( const orderByPart of orderByParts ) {
                                              const orderByPartParts = ( orderByPart.trim().replaceAll( /\s+/g, ' ' ) ).split( ' ' );

                                              if ( orderByPartParts.length && orderByPartParts.length < 3 ) {
                                                  const direction = ( orderByPartParts[ 1 ] ?? 'asc' ).toLowerCase();

                                                  if ( direction === 'asc' || direction === 'desc' ) {
                                                      orderByDefinitions.push( {
                                                          key: orderByPartParts[ 0 ],
                                                          direction
                                                      } );
                                                  }
                                              }
                                          }
                                      }

                                      // sort by ReferenceField:orderBy
                                      if ( orderByDefinitions.length ) {
                                          referenceSortMapping.sort( ( value1, value2 ) => {
                                              let innerRetVal : number,
                                                  orderByCount : number = 0;

                                              do {
                                                  innerRetVal = sortFnc( value1, value2, orderByDefinitions[ orderByCount++ ] );
                                              } while ( innerRetVal === 0 && orderByCount < orderByDefinitions.length )

                                              return innerRetVal;
                                          } );
                                      }
                                      // sort by sortLabel
                                      else {
                                          referenceSortMapping.sort( ( value1, value2 ) => {
                                              if ( value1.sortLabel.toLowerCase() > value2.sortLabel.toLowerCase() ) {
                                                  return 1;
                                              }
                                              else if ( value1.sortLabel.toLowerCase() < value2.sortLabel.toLowerCase() ) {
                                                  return -1;
                                              }
                                              else {
                                                  return 0;
                                              }
                                          } );
                                      }

                                      if ( referenceSortMapping.length ) {
                                          references = referenceSortMapping.map( rSM => rSM.reference );
                                      }
                                      else {
                                          references = dataAndMetaModel.objects.map( referenceId => global.data[ dataContext ].references[ referenceId ] );
                                      }

                                      // process references
                                      for ( let index = 0; index < references.length; index++ ) {
                                          const reference                       = references[ index ],
                                                realTabContainerLayoutElementId = this.getRealLayoutElementIdFromLayoutElementIdAndParentId( parent.id, parent.referenceId );

                                          if ( !deletedStatusIds.includes( reference.statusId ) ) {
                                              const repeatedContainerContent = buildContent(
                                                  {
                                                      type    : parent.type,
                                                      id      : containerLayoutElementData.id,
                                                      gridSize: parent.gridSize,

                                                      dataDescriptorAlias: repeatConfig.dataDescriptorAlias,
                                                      referenceId        : reference.id,
                                                      itemId             : repeatConfig.referenceDirection === 'SrcToDest'
                                                          ? reference.destItemId
                                                          : reference.srcItemId,
                                                      parentItemId       : parent.itemId,
                                                      isRepeating        : true,
                                                      idSuffix           : parent.idSuffix
                                                          ? `${parent.idSuffix}-${repeatIndex + 1}`
                                                          : `-${repeatIndex + 1}`
                                                  },
                                                  [ layoutElementStructure ]
                                              );

                                              retVal.isDirty = retVal.isDirty || repeatedContainerContent.isDirty;
                                              retVal.isInvalid = retVal.isInvalid || repeatedContainerContent.isInvalid;
                                              retVal.content = retVal.content.concat( repeatedContainerContent.content );
                                              retVal.tabIndexMapping[ reference.id ] = repeatIndex;

                                              // if the reference was just added - activate tab therefor
                                              if (
                                                  parent.type === 'tabContainer'
                                                  && reference.id === repeatConfig.newReferenceId
                                                  && activeTabContainerIndexes[ realTabContainerLayoutElementId ] !== reference.id
                                              ) {
                                                  this.props.setParam(
                                                      uid,
                                                      'activeTabContainerIndexes',
                                                      {
                                                          ...activeTabContainerIndexes,
                                                          [ realTabContainerLayoutElementId ]: reference.id
                                                      }
                                                  );
                                              }

                                              repeatIndex++;
                                          }
                                      }
                                  }
                              }
                              else {*/
                              let containerContent : GetContainerContent               = {
                                      isDirty  : false,
                                      isInvalid: false,
                                      content  : []
                                  },
                                  title : React.ReactNode                              = '',
                                  className                                            = [ `yn-visibility-${visibility}${isHidden ? ' yn-disabled' : ''}${readOnly ? ' yn-readonly' : ''}` ].concat( layoutElementData.elementClassName ? layoutElementData.elementClassName : [] ),
                                  style                                                = { ...layoutElementData.elementStyle, ...elementStyle },
                                  nearestNonLayoutWrapperParentType : 'form' | 'container' | undefined,
                                  commonAdditionalResolverParametersForMessageEnhanced = commonAdditionalResolverParametersForMessage;


                              if ( parent.dataDescriptorAlias ) {
                                  repeatingContainer = true;
                              }

                              if ( messages?.title ) {
                                  const resolvedDataObjectsForMessage : ResolvedDataObjectChain = {
                                      ...commonResolvedDataObjectsForMessage
                                  };

                                  if ( parent.isRepeating ) {
                                      const repeatedReference = global.data[ dataContext ].references[ parent.referenceId ?? 0 ],
                                            repeatedItem      = global.data[ dataContext ].items[ parent.itemId ?? 0 ];

                                      resolvedDataObjectsForMessage[ 'repeatedObject' ] = {
                                          item     : repeatedItem,
                                          reference: repeatedReference
                                      };

                                      if ( parent.index !== undefined ) {
                                          commonAdditionalResolverParametersForMessageEnhanced = {
                                              ...commonAdditionalResolverParametersForMessageEnhanced,
                                              variables: {
                                                  ...commonAdditionalResolverParametersForMessageEnhanced.variables,
                                                  // @ts-expect-error fulfills the type definition
                                                  repeatIndex: parent.index
                                              }
                                          };
                                      }
                                  }

                                  title = resolveMessage( messages.title, resolvedDataObjectsForMessage, commonAdditionalResolverParametersForMessageEnhanced, parent.isRepeating ? parent.itemId : undefined );

                                  if ( !isInvalidHTML( title as string ) ) {
                                      title = parse( title as string );
                                  }
                              }

                              // style
                              if ( containerLayoutElementData.styles?.style ) {
                                  className = className.concat( resolveStyleToClassNames( containerLayoutElementData.styles.style, {} ) );
                                  style = {
                                      ...style,
                                      ...resolveStyle( containerLayoutElementData.styles.style, {} )
                                  };
                              }

                              if ( parent.type === 'layoutWrapper' ) {
                                  let nextParentLayoutStructure : FormLayoutStructure | ContainerLayoutStructure | TabContainerLayoutStructure | undefined = parentLayoutStructure;

                                  do {
                                      nextParentLayoutStructure = getParentLayoutStructureForLayoutElement( nextParentLayoutStructure.layoutElementId, layoutStructure! );

                                      if ( nextParentLayoutStructure ) {
                                          if ( nextParentLayoutStructure.type === 'FORM' ) {
                                              nearestNonLayoutWrapperParentType = 'form';
                                          }
                                          else {
                                              const {
                                                        isLayoutWrapper: nextParentIsLayoutWrapper
                                                    } = layoutElementsProp[ nextParentLayoutStructure.layoutElementId ] ?? {};

                                              if ( !nextParentIsLayoutWrapper ) {
                                                  nearestNonLayoutWrapperParentType = 'container';
                                              }
                                          }
                                      }
                                  } while ( nextParentLayoutStructure && !nearestNonLayoutWrapperParentType )
                              }

                              // layout wrapper
                              if ( isLayoutWrapper ) {
                                  containerContent = buildContent(
                                      {
                                          ...parent,
                                          type       : parent.type === 'form' || parent.type === 'layoutWrapper' ? 'layoutWrapper' : 'container',
                                          id         : containerLayoutElementData.id,
                                          gridSize   : childrenGridSize,
                                          isRepeating: false
                                      },
                                      layoutElementStructure
                                  );

                                  retVal.isDirty = retVal.isDirty || containerContent.isDirty;
                                  retVal.isInvalid = retVal.isInvalid || containerContent.isInvalid;
                                  retVal.content.push(
                                      <div
                                          id={`leid_${layoutElementId}`}
                                          key={`${containerLayoutElementData.key}`}
                                          className={`yn-form-layout-wrapper ${className.join( ' ' )}`}
                                          style={style}
                                          {...( mode === 'design' ? designAttributes : {} )}
                                      >
                                          {containerContent.content}
                                      </div>
                                  );
                              }

                              // card
                              else if ( parent.type === 'form' || ( parent.type === 'layoutWrapper' && nearestNonLayoutWrapperParentType === 'form' ) ) {
                                  containerContent = buildContent(
                                      {
                                          ...parent,
                                          type       : 'card',
                                          id         : containerLayoutElementData.id,
                                          gridSize   : childrenGridSize,
                                          isRepeating: false
                                      },
                                      layoutElementStructure
                                  );

                                  retVal.isDirty = retVal.isDirty || containerContent.isDirty;
                                  retVal.isInvalid = retVal.isInvalid || containerContent.isInvalid;
                                  retVal.content.push(
                                      <div
                                          id={`leid_${layoutElementId}`}
                                          key={`${containerLayoutElementData.key}`}
                                          className={`yn-form-card ${className.join( ' ' )}`}
                                          style={style}
                                          {...( mode === 'design' ? designAttributes : {} )}
                                      >
                                          <Card
                                              title={<span style={layoutElementData.titleStyle}>{title ?? ' '}</span>}
                                              className={`yn-title-align-${titleAlignment} yn-title-visibility-${titleVisibility}`}
                                          >
                                              {containerContent.content}
                                          </Card>
                                      </div>
                                  );
                              }

                              // tabPanel
                              else if ( parent.type === 'tabContainer' ) {
                                  containerContent = buildContent(
                                      {
                                          ...parent,
                                          type       : 'container',
                                          id         : containerLayoutElementData.id,
                                          gridSize   : childrenGridSize,
                                          isRepeating: false
                                      },
                                      layoutElementStructure
                                  );

                                  retVal.isDirty = retVal.isDirty || containerContent.isDirty;
                                  retVal.isInvalid = retVal.isInvalid || containerContent.isInvalid;
                                  retVal.content.push(
                                      <TabPanel
                                          key={`${containerLayoutElementData.key}_tabPanel`}

                                          header={title}

                                          headerClassName={containerContent.isInvalid
                                              ? 'yn-error'
                                              : ( containerContent.isDirty
                                                      ? 'yn-dirty'
                                                      : ''
                                              )}
                                          className={`yn-form-tabpanel ${className.join( ' ' )} yn-title-align-${titleAlignment} ${containerLayoutElementData.disableTab ? 'yn-form-tabpanel-disabled' : ''}`}
                                          style={layoutElementData.titleStyle}
                                          pt={{
                                              header: {
                                                  id                        : `leid_${layoutElementId}_tab`,
                                                  [ 'data-layoutelementid' ]: designAttributes[ 'data-layoutelementid' ],
                                                  onMouseOver               : designAttributes.onMouseOver,
                                                  onMouseLeave              : designAttributes.onMouseLeave,
                                                  onClick                   : designAttributes.onClick
                                              }
                                          }}
                                      >
                                          <div className="yn-form-container-content">
                                              <div
                                                  id={`leid_${layoutElementId}`}
                                                  key={`${containerLayoutElementData.key}`}
                                                  className={`yn-form-container ${className.join( ' ' )}`}
                                                  style={style}
                                                  {...( mode === 'design' ? designAttributes : {} )}
                                              >
                                                  {containerContent.content}
                                              </div>
                                          </div>
                                      </TabPanel>
                                  );
                              }

                              // all other
                              else {
                                  containerContent = buildContent(
                                      {
                                          ...parent,
                                          type       : 'container',
                                          id         : containerLayoutElementData.id,
                                          gridSize   : childrenGridSize,
                                          isRepeating: false
                                      },
                                      layoutElementStructure
                                  );

                                  retVal.isDirty = retVal.isDirty || containerContent.isDirty;
                                  retVal.isInvalid = retVal.isInvalid || containerContent.isInvalid;
                                  retVal.content.push(
                                      <div
                                          id={`leid_${layoutElementId}`}
                                          key={`${containerLayoutElementData.key}`}
                                          className={`yn-form-container ${className.join( ' ' )}`}
                                          style={style}
                                          {...( mode === 'design' ? designAttributes : {} )}
                                      >
                                          {( title || titleVisibility === 'placeholder' ) &&
                                              <div
                                                  className={`yn-form-container-title yn-title-align-${titleAlignment} yn-title-visibility-${titleVisibility}`}
                                                  style={layoutElementData.titleStyle}>{title ?? ' '}</div>
                                          }
                                          {containerContent.content}
                                      </div>
                                  );
                              }
                              //}
                          }

                          // tabView
                          else if ( layoutElementStructure.type === 'TABCONTAINER' ) {
                              const tabContainerLayoutElementData = layoutElementData as TabContainerProperties,
                                    {
                                        messages,
                                        titleAlignment
                                    }                             = tabContainerLayoutElementData,
                                    titleVisibility               = messages?.title ? tabContainerLayoutElementData.titleVisibility : 'hidden',
                                    className                     = [ `yn-visibility-${visibility}${isHidden ? ' yn-disabled' : ''}${readOnly ? ' yn-readonly' : ''}` ].concat( layoutElementData.elementClassName ? layoutElementData.elementClassName : [] ),
                                    style                         = { ...layoutElementData.elementStyle, ...elementStyle },
                                    designAttributes              = {
                                        [ 'data-layoutelementid' ]   : layoutElementId,
                                        [ 'data-layoutelementlabel' ]: `${layoutElementData.name ? layoutElementData.name : layoutElementData.key})`,
                                        draggable                    : !readOnly,
                                        onMouseOver                  : onMouseEnter.bind( null, layoutElementId ),
                                        onMouseLeave                 : onMouseLeave.bind( null, layoutElementId ),
                                        onClick                      : onClick.bind( null, layoutElementId ),
                                        onDragStart                  : readOnly
                                            ? undefined
                                            : onDragStart.bind( null, layoutElementId )
                                    },
                                    tabContainerContent           = buildContent(
                                        {
                                            ...parent,
                                            type       : 'tabContainer',
                                            id         : tabContainerLayoutElementData.id,
                                            gridSize   : childrenGridSize,
                                            isRepeating: false
                                        },
                                        layoutElementStructure
                                    ),
                                    realLayoutElementId           = getRealLayoutElementIdFromLayoutElementIdAndParentId( layoutElementId, parent.referenceId );

                              let activeIndex             = tabContainerContent.tabIndexMapping?.[ activeTabContainerIndexes[ realLayoutElementId ] ] ?? activeTabContainerIndexes[ realLayoutElementId ] ?? tabContainerLayoutElementData.activeTabIndex ?? 0,
                                  title : React.ReactNode = '';

                              if ( activeIndex < 0 ) {
                                  activeIndex = 0;
                              }

                              if ( messages?.title ) {
                                  try {
                                      title = __( messages.title ).replaceAll( /[\u200B-\u200E\uFEFF]/g, '' );
                                      title = messageTemplateStringResolver?.( title as string );
                                      if ( !isInvalidHTML( title as string ) ) {
                                          title = parse( title as string );
                                      }
                                  }
                                  catch ( e ) {
                                      // do nothing
                                  }
                              }

                              if ( tabContainerLayoutElementData.distributeTabsEvenly ) {
                                  className.push( 'yn-distribute-tabs-evenly' );
                              }

                              retVal.isDirty = retVal.isDirty || tabContainerContent.isDirty;
                              retVal.isInvalid = retVal.isInvalid || tabContainerContent.isInvalid;
                              retVal.content.push(
                                  <div
                                      id={`leid_${layoutElementId}`}
                                      key={`${tabContainerLayoutElementData.key}`}
                                      className={`yn-form-tabcontainer ${className.join( ' ' )}`}
                                      style={style}
                                      {...( mode === 'design' ? designAttributes : {} )}
                                  >
                                      {( title || titleVisibility === 'placeholder' ) &&
                                          <div
                                              className={`yn-form-tabcontainer-title yn-title-align-${titleAlignment} yn-title-visibility-${titleVisibility}`}
                                              style={layoutElementData.titleStyle}>{title ?? ' '}</div>
                                      }
                                      <TabView
                                          key={`${tabContainerLayoutElementData.key}_tabView`}

                                          activeIndex={activeIndex}
                                          onTabChange={onTabContainerTabChange.bind( null, realLayoutElementId, tabContainerContent.tabIndexMapping )}

                                          className="yn-form-tabview"
                                      >
                                          {tabContainerContent.content}
                                      </TabView>
                                  </div>
                              );
                          }

                          // FormFields
                          else {
                              const formFieldLayoutElement     = layoutElement as FormFieldData,
                                    formFieldLayoutElementData = layoutElementData as FormFieldProperties | VariableFieldProperties,
                                    {
                                        required,
                                        tabOrder,
                                        type
                                    }                          = formFieldLayoutElementData,
                                    formFieldProps             = layoutElementData.props,
                                    dataDescriptorAlias        = formFieldLayoutElement.dataDescriptorAlias ?? '',
                                    dataDescriptor             = dataDescriptors?.[ dataDescriptorAlias ],
                                    dataDescriptorMetaModel    = dataDescriptorsMetaModel?.[ dataDescriptorAlias ],
                                    dataDescriptorData         = dataDescriptorsData?.[ dataDescriptorAlias ];

                              let dynamicFormFieldProps : {
                                      id : string;
                                      //
                                      value? : AttributeValue | EnumUpdateValue | ReferenceValue | ReferenceValue[];
                                      originalValue? : AttributeValue | EnumUpdateValue | ReferenceValue | ReferenceValue[];
                                      displayValue? : string | JSX.Element | JSX.Element[] | ( string | JSX.Element )[];
                                      //
                                      disabled : boolean;
                                      visibility : string;
                                      classNames : string[];
                                      style : React.CSSProperties;
                                      displayClassNames : string[];
                                      displayStyle : React.CSSProperties;
                                      inputClassNames : string[];
                                      inputStyle : React.CSSProperties;
                                      labelClassNames : string[];
                                      labelStyle : React.CSSProperties;
                                      messageClassNames : string[];
                                      messageStyle : React.CSSProperties;
                                      displayMode? : 'card' | 'field';
                                      mode : 'design' | 'normal';
                                      //
                                      appendToElementId? : string;
                                      tabIndex? : number;
                                      required? : boolean;
                                      errorMessage? : string;
                                      markDirty? : boolean;
                                      //
                                      messageTemplateStringResolver? : ( templateString : string ) => string;
                                      //
                                      additionalParameters? : { [ parameter : string ] : any };
                                      additionalQueryParameters? : Required<LoadReferenceFieldItemsParameters>[ 'query' ][ 'parameters' ];
                                      //
                                      variables? : Variables;
                                      //
                                      filters? : string;
                                      fixFilters? : string;
                                  } | undefined = undefined,
                                  formFieldListeners : {
                                      onChange? : ( value : AttributeValue | EnumValue | ReferenceValue | ReferenceValue[] | undefined, originalEvent : any ) => void;
                                      onIntermediateChange? : ( value : AttributeValue | EnumValue | ReferenceValue | ReferenceValue[] | undefined, originalEvent : any ) => void;
                                      onTriggerAction? : ( originalEvent : any ) => void;

                                      onVariableChange? : ( value : AttributeValue | EnumValue | ReferenceValue | ReferenceValue[] | {
                                          start : string;
                                          end : string;
                                      } | undefined, originalEvent : any ) => void;
                                      onIntermediateVariableChange? : ( value : AttributeValue | EnumValue | ReferenceValue | ReferenceValue[] | {
                                          start : string;
                                          end : string;
                                      } | undefined, originalEvent : any ) => void;

                                      onPropertyChange? : ( propertyTypeName : string, value : AttributeValue | undefined ) => void;

                                      // design mode only
                                      pt? : {
                                          root? : React.HTMLAttributes<HTMLDivElement> & { [ key : `data-${string}` ] : string };
                                          fieldWrapper? : FieldWrapperProps[ 'pt' ];
                                      };
                                  } | undefined = undefined;

                              // TODO: implement repeated container
                              /*let formFieldLayoutElementId : number | string = layoutElementId,
                                  value                                      = dataDescriptorsValue?.[ dataDescriptorAlias ],
                                  originalValue                              = dataDescriptorData?.originalValue,
                                  parentId : number | undefined              = undefined;

                              // handle dataAndModel.objects, value, originalValue, setValueErrors and errors for reference repeating container fields
                              if ( parent.dataDescriptorAlias ) {
                                  const parentDataDescriptor = dataDescriptors?.[ parent.dataDescriptorAlias ];

                                  // recompute dataAndModel.objects, value and originalValue
                                  if ( dataDescriptorsMetaModel && dataDescriptorsData && dataDescriptor && parentDataDescriptor?.dataObject ) {

                                      // fields handling the reference or the referenced item
                                      if (
                                          dataDescriptor.dataObject
                                          && dataDescriptor.dataObject.id === parentDataDescriptor.dataObject.id
                                      ) {
                                          if ( dataDescriptor.type === 'reference' || dataDescriptor.type === 'referenceProperty' ) {
                                              parentId = parent.referenceId;
                                          }
                                          else if ( dataDescriptor.type === 'item' || dataDescriptor.type === 'property' ) {
                                              parentId = parent.itemId;
                                          }
                                      }

                                      // all other fields (referenced to the referenced item)
                                      else if ( parent.itemId !== undefined ) {
                                          const subDataDescriptors = {
                                                    [ dataDescriptorAlias ]: dataDescriptor
                                                } as DataDescriptors,
                                                subDataStructure   = getDataStructure( parentDataDescriptor.dataObject.id, dataStructure );

                                          if ( subDataStructure ) {
                                              const dataDescriptorMappings = subDataStructure
                                                  ? {
                                                      dataAndMetaModel: this.buildDataDescriptorToMetaModelMapping(
                                                          subDataDescriptors,
                                                          dataContext,
                                                          schema,
                                                          subDataStructure,
                                                          parent.itemId,
                                                          user
                                                      )
                                                  } as DataDescriptorMappings
                                                  : undefined;

                                              if ( dataDescriptorMappings ) {
                                                  dataDescriptorMappings.dataAndMetaModel = this.buildDataDescriptorToDataMapping(
                                                      subDataDescriptors,
                                                      dataDescriptorMappings,
                                                      dataContext,
                                                      schema,
                                                      subDataStructure,
                                                      parent.itemId
                                                  );

                                                  if ( dataDescriptorMappings.dataAndMetaModel[ dataDescriptorAlias ].objects ) {
                                                      if ( Array.isArray( dataDescriptorMappings.dataAndMetaModel[ dataDescriptorAlias ].objects ) ) {
                                                          parentId = ( dataDescriptorMappings.dataAndMetaModel[ dataDescriptorAlias ].objects as number[] )[ 0 ];
                                                      }
                                                      else if ( Object.keys( dataDescriptorMappings.dataAndMetaModel[ dataDescriptorAlias ].objects ).length ) {
                                                          parentId = Number( Object.keys( dataDescriptorMappings.dataAndMetaModel[ dataDescriptorAlias ].objects )[ 0 ] );
                                                      }
                                                  }
                                              }
                                          }
                                      }

                                      if ( parentId ) {
                                          const realDataDescriptorAlias = getRealLayoutElementDataDescriptorAliasFromLayoutElementDataDescriptorAliasAndParentId( dataDescriptorAlias, parentId ),
                                                newDataAndMetaModel     = elusiveData?.dataDescriptorMappings?.dataAndMetaModel?.[ realDataDescriptorAlias ];

                                          if ( newDataAndMetaModel ) {
                                              dataAndMetaModel = newDataAndMetaModel;
                                          }
                                          else if ( dataAndMetaModel.type !== 'static' ) {
                                              dataAndMetaModel = {
                                                  ...dataAndMetaModel,
                                                  values        : {},
                                                  originalValues: {},
                                                  hasNoParent   : false
                                              } as ObjectDataAndMetaModelMapping;

                                              if ( dataAndMetaModel.type === 'item' || dataAndMetaModel.type === 'reference' ) {
                                                  dataAndMetaModel.objects = [ parentId ];
                                              }
                                              else {
                                                  let propertySet : PropertySet | ReferencePropertySet | undefined;

                                                  dataAndMetaModel.objects = {};

                                                  if ( dataAndMetaModel.type === 'property' || dataAndMetaModel.type === 'enumProperty' ) {
                                                      propertySet = metaModel[ schema ].getPropertySet( dataAndMetaModel.metaModels[ 0 ] );
                                                  }
                                                  else {
                                                      propertySet = metaModel[ schema ].getReferencePropertySet( dataAndMetaModel.metaModels[ 0 ] );
                                                  }

                                                  if ( propertySet ) {
                                                      dataAndMetaModel.objects[ parentId ] = [ propertySet.propertyTypeId ];
                                                  }
                                              }
                                          }

                                          value = elusiveData?.dataDescriptorMappings?.value?.[ realDataDescriptorAlias ];
                                          originalValue = elusiveData?.dataDescriptorMappings?.originalValue?.[ realDataDescriptorAlias ];
                                      }
                                      else {
                                          dataAndMetaModel = { ...dataAndMetaModel };

                                          if (
                                              dataDescriptor.type === 'item'
                                              || dataDescriptor.type === 'reference'
                                          ) {
                                              dataAndMetaModel.objects = [];
                                          }
                                          else {
                                              dataAndMetaModel.objects = {};
                                          }

                                          dataAndMetaModel.values = {};
                                          dataAndMetaModel.originalValues = {};

                                          value = undefined;
                                          originalValue = undefined;
                                      }

                                      formFieldLayoutElementId = getRealLayoutElementIdFromLayoutElementIdAndParentId( layoutElementId, parentId );
                                  }
                              }*/

                              try {
                                  // get dynamic formField props
                                  dynamicFormFieldProps = {
                                      id               : `leid_${layoutElementId}`,
                                      disabled         : formFieldProps.disabled || parent.hasNoParent || ( type !== 'REFERENCE' && type !== 'BUTTON' && parent.dataDescriptorAlias && !parent.referenceId ),
                                      visibility,
                                      classNames       : ( [ `${isHidden ? ' yn-disabled' : ''}${readOnly ? ' yn-readonly' : ''}` ] ).concat( layoutElementData.elementClassNames ? layoutElementData.elementClassNames : [] ),
                                      style            : { ...layoutElementData.elementStyle, ...elementStyle },
                                      displayClassNames: layoutElementData.displayClassNames ? ( [] as string[] ).concat( layoutElementData.displayClassNames ) : [],
                                      displayStyle     : { ...layoutElementData.displayStyle },
                                      inputClassNames  : layoutElementData.inputClassNames ? ( [] as string[] ).concat( layoutElementData.inputClassNames ) : [],
                                      inputStyle       : { ...layoutElementData.inputStyle },
                                      labelClassNames  : layoutElementData.labelClassNames ? ( [] as string[] ).concat( layoutElementData.labelClassNames ) : [],
                                      labelStyle       : { ...layoutElementData.labelStyle },
                                      messageClassNames: layoutElementData.messageClassNames ? ( [] as string[] ).concat( layoutElementData.messageClassNames ) : [],
                                      messageStyle     : { ...layoutElementData.messageStyle },
                                      displayMode      : parent.type !== 'form' && parent.type !== 'layoutWrapper'
                                          ? 'field'
                                          : 'card',
                                      mode             : mode === 'design' ? 'design' : 'normal',
                                      //
                                      appendToElementId: undefined, //mode === 'popup' || mode === 'standalonePopup' ? 'document' : undefined,
                                      tabIndex         : type !== 'STATIC'
                                          ? ( tabOrder ?? 0 ) === -1
                                              ? tabOrder
                                              : tabIndexOffset + ( ( tabOrder ?? tabIndex ) * tabIndexStep )
                                          : undefined,
                                      required,
                                      errorMessage     : '',
                                      markDirty        : type !== 'VARIABLE',
                                      //
                                      messageTemplateStringResolver,
                                      //
                                      additionalQueryParameters: {
                                          formParentItemId: String( formParentItemId ),
                                          treeRootItemId  : String( treeRootItemId ),
                                          treeParentItemId: String( treeParentItemId )
                                      },
                                      //
                                      variables: {
                                          //...treeVariables,
                                          ...elusiveData?.variables,
                                          formDataContext : dataContext,
                                          fieldItemId     : layoutElementData.id,
                                          formRootItemId  : rootItemId,
                                          formParentItemId: formParentItemId,
                                          treeRootItemId  : treeRootItemId,
                                          treeParentItemId: treeParentItemId,
                                          propertyTypeName: dataDescriptor?.propertyTypeName
                                      }
                                  };

                                  // count tabIndex up
                                  tabIndex += 1;

                                  formFieldListeners = {
                                      onChange            : type === 'VARIABLE'
                                          ? onVariableChange.bind( null, layoutElementId )
                                          : onValueChange.bind( null, layoutElementId ),
                                      onIntermediateChange: type === 'VARIABLE'
                                          ? onIntermediateVariableChange.bind( null, layoutElementId )
                                          : onIntermediateValueChange.bind( null, layoutElementId ),
                                      onTriggerAction     : ( originalEvent : any ) => {
                                          void onTriggerAction( layoutElementId, originalEvent );
                                      },

                                      onVariableChange            : onVariableChange.bind( null, layoutElementId ),
                                      onIntermediateVariableChange: onIntermediateVariableChange.bind( null, layoutElementId )
                                  };

                                  if ( mode === 'design' ) {
                                      if ( type === 'CALENDAR' || type === 'CHART' || type === 'GRAPH' || type === 'MAP' || type === 'OVERVIEW' ) {
                                          formFieldListeners = {
                                              onPropertyChange: onLayoutElementPropertyChange.bind( null, layoutElementId ),

                                              pt: {
                                                  root: {
                                                      [ 'data-layoutelementid' ]    : String( layoutElementId ),
                                                      [ 'data-layoutelementlabel' ] : `${layoutElementData.name ? layoutElementData.name : layoutElementData.key}`,
                                                      [ 'data-datadescriptoralias' ]: `${dataDescriptorAlias ?? ''}`,
                                                      draggable                     : !readOnly,
                                                      onMouseOver                   : onMouseEnter.bind( null, layoutElementId ),
                                                      onMouseLeave                  : onMouseLeave.bind( null, layoutElementId ),
                                                      onClick                       : onClick.bind( null, layoutElementId ),
                                                      onDragStart                   : readOnly
                                                          ? undefined
                                                          : onDragStart.bind( null, layoutElementId )
                                                  }
                                              }
                                          };
                                      }
                                      else {
                                          formFieldListeners.pt = {
                                              fieldWrapper: {
                                                  root: {
                                                      [ 'data-layoutelementid' ]    : String( layoutElementId ),
                                                      [ 'data-layoutelementlabel' ] : `${layoutElementData.name ? layoutElementData.name : layoutElementData.key}`,
                                                      [ 'data-datadescriptoralias' ]: `${dataDescriptorAlias ?? ''}`,
                                                      draggable                     : !readOnly,
                                                      onMouseOver                   : onMouseEnter.bind( null, layoutElementId ),
                                                      onMouseLeave                  : onMouseLeave.bind( null, layoutElementId ),
                                                      onClick                       : onClick.bind( null, layoutElementId ),
                                                      onDragStart                   : readOnly
                                                          ? undefined
                                                          : onDragStart.bind( null, layoutElementId )
                                                  }
                                              }
                                          };
                                      }
                                  }
                                  else {
                                      dynamicFormFieldProps.additionalQueryParameters = {
                                          formParentItemId: String( formParentItemId ),
                                          treeRootItemId  : String( treeRootItemId ),
                                          treeParentItemId: String( treeParentItemId )
                                      };

                                      if ( type === 'ENUM' ) {
                                          if ( formFieldProps.keyEditorParams?.autoCompleteRequestParameters ) {
                                              formFieldProps.keyEditorParams.autoCompleteRequestParameters.additionalParameters = {
                                                  formParentItemId: String( formParentItemId ),
                                                  treeRootItemId  : String( treeRootItemId ),
                                                  treeParentItemId: String( treeParentItemId )
                                              };
                                          }
                                          if ( formFieldProps.valueEditorParams?.autoCompleteRequestParameters ) {
                                              formFieldProps.valueEditorParams.autoCompleteRequestParameters.additionalParameters = {
                                                  formParentItemId: String( formParentItemId ),
                                                  treeRootItemId  : String( treeRootItemId ),
                                                  treeParentItemId: String( treeParentItemId )
                                              };
                                          }
                                      }
                                  }

                                  // value, originalValue and status coloring
                                  if ( dataStructure && dataDescriptor && dataDescriptorMetaModel && dataDescriptorData && dataDescriptor.type !== 'expression' ) {
                                      const object = getObjectFromDataAndMetaModelMapping( dataDescriptorData, dataDescriptorMetaModel ) as Item | Property | Reference | ReferenceProperty | undefined;

                                      // value and originalValue
                                      dynamicFormFieldProps.value = dataDescriptorsValue?.[ dataDescriptorAlias ];
                                      dynamicFormFieldProps.originalValue = dataDescriptorData?.originalValue;

                                      // status coloring
                                      if ( object && !object.isDeleted?.() ) {
                                          dynamicFormFieldProps.inputStyle = { ...dynamicFormFieldProps.inputStyle, ...getStatusColorStyleObject( object.statusId, schema ) };
                                      }
                                      else if ( rootItemId && dataDescriptorMetaModel.type === 'property' ) {
                                          const item = dataUtils.getData(
                                              buildDataDescriptor( { attributePath: 'item.id' }, dataDescriptor.dataObject ) as ObjectDataDescriptor,
                                              dataStructure,
                                              dataContext,
                                              rootItemId
                                          )?.[ 0 ].object as Item;

                                          if ( item ) {
                                              dynamicFormFieldProps.inputStyle = { ...dynamicFormFieldProps.inputStyle, ...getStatusColorStyleObject( item.statusId, schema ) };
                                          }
                                      }
                                      else if ( rootItemId && dataDescriptorMetaModel.type === 'referenceProperty' ) {
                                          const reference = dataUtils.getData(
                                              buildDataDescriptor( { attributePath: 'reference.id' }, dataDescriptor.dataObject ) as ObjectDataDescriptor,
                                              dataStructure,
                                              dataContext,
                                              rootItemId
                                          )?.[ 0 ].object as Reference;

                                          if ( reference ) {
                                              dynamicFormFieldProps.inputStyle = { ...dynamicFormFieldProps.inputStyle, ...getStatusColorStyleObject( reference.statusId, schema ) };
                                          }
                                      }
                                  }
                                  // value and originalValue
                                  else if ( type === 'VARIABLE' ) {
                                      dynamicFormFieldProps.value = variablesEditValues?.[ formFieldLayoutElementData.variableName ] ?? variables?.[ formFieldLayoutElementData.variableName ];
                                      dynamicFormFieldProps.originalValue = variables?.[ `${formFieldLayoutElementData.variableName}_originalValue` ] ?? variables?.[ formFieldLayoutElementData.variableName ];
                                      dynamicFormFieldProps.markDirty = !dynamicFormFieldProps.disabled && ( variablesEditValues?.[ `${formFieldLayoutElementData.variableName}` ] ?? variables?.[ `${formFieldLayoutElementData.variableName}_originalValue` ] ) !== undefined;
                                  }

                                  // value and originalValue for dataDescriptor.type === 'reference' and type !== 'REFERENCE' and type !== 'FILE'
                                  if ( dataDescriptor && dataDescriptor.type === 'reference' && type !== 'REFERENCE' && type !== 'FILE' ) {
                                      if ( dynamicFormFieldProps.value ) {
                                          let value : ReferenceValue | undefined;

                                          if ( Array.isArray( dynamicFormFieldProps.value ) ) {
                                              value = ( dynamicFormFieldProps.value as ReferenceValue[] )[ 0 ] as ReferenceValue;
                                          }
                                          else {
                                              value = dynamicFormFieldProps.value as ReferenceValue;
                                          }

                                          if ( isReferenceAttribute( dataDescriptor.attribute ) ) {
                                              dynamicFormFieldProps.value = value?.[ dataDescriptor.attribute ];
                                          }
                                          else {
                                              dynamicFormFieldProps.value = undefined;
                                          }
                                      }

                                      if ( dynamicFormFieldProps.originalValue ) {
                                          let value : ReferenceValue | undefined;

                                          if ( Array.isArray( dynamicFormFieldProps.originalValue ) ) {
                                              value = ( dynamicFormFieldProps.originalValue as ReferenceValue[] )[ 0 ] as ReferenceValue;
                                          }
                                          else {
                                              value = dynamicFormFieldProps.originalValue as ReferenceValue;
                                          }

                                          if ( isReferenceAttribute( dataDescriptor.attribute ) ) {
                                              dynamicFormFieldProps.originalValue = value?.[ dataDescriptor.attribute ];
                                          }
                                          else {
                                              dynamicFormFieldProps.originalValue = undefined;
                                          }
                                      }
                                  }

                                  // TODO: check if this can be done after cacheParameters check
                                  //  -> add to cacheParameters:
                                  //  - dataDescriptorData?.isDefault
                                  //  - formFieldLayoutElementData.format
                                  //  - user?.language
                                  //  - layoutElementData.valueUnit
                                  // defaultValue and/or format
                                  if (
                                      dataDescriptor
                                      && type !== 'BUTTON'
                                      && type !== 'ENUM'
                                      && type !== 'FILE'
                                      && type !== 'REFERENCE'
                                      && dynamicFormFieldProps.value !== undefined
                                      && (
                                          type === 'STATIC'
                                          || dataDescriptorData?.isDefault === true
                                          || formFieldLayoutElementData.format !== undefined
                                      )
                                  ) {
                                      if ( formFieldLayoutElementData.format && dynamicFormFieldProps.value !== '' ) {
                                          const valueType     = type === 'VARIABLE' ? formFieldLayoutElementData.variableType : type,
                                                valueToFormat = isPropertyValueType( valueType )
                                                    ? convertValueToString( dynamicFormFieldProps.value as AttributeValue, valueType )
                                                    : String( dynamicFormFieldProps.value );

                                          if ( type !== 'STATIC' ) {
                                              dynamicFormFieldProps.displayValue = formatRenderer( valueToFormat, formFieldLayoutElementData.format );
                                          }
                                          else {
                                              dynamicFormFieldProps.displayValue = valueToFormat;
                                          }
                                      }
                                      else if (
                                          type === 'STATIC'
                                          || (
                                              type !== 'BOOLEAN'
                                              && dataDescriptorData?.isDefault
                                          )
                                      ) {
                                          const dataValueType           = dataUtils.getDataValueType( dataDescriptor, schema, dataStructure ) ?? 'STRING',
                                                renderValueParams : any = {
                                                    language : user?.language,
                                                    valueUnit: layoutElementData.valueUnit
                                                };

                                          dynamicFormFieldProps.displayValue = renderValue( dataValueType, dynamicFormFieldProps.value, renderValueParams );
                                      }
                                      else {
                                          dynamicFormFieldProps.displayValue = String( dynamicFormFieldProps.value );
                                      }

                                      // clear value if default
                                      if ( dataDescriptorData?.isDefault ) {
                                          dynamicFormFieldProps.value = undefined;
                                      }

                                      // clear displayValue if not default value and equal to value
                                      else if ( type !== 'STATIC' && !dataDescriptorData?.isDefault && dynamicFormFieldProps.displayValue === String( dynamicFormFieldProps.value ) ) {
                                          dynamicFormFieldProps.displayValue = undefined;
                                      }

                                      // mark default value
                                      if ( dataDescriptorData?.isDefault ) {
                                          dynamicFormFieldProps.displayClassNames.push( 'property-default' );
                                      }
                                  }

                                  // resolve dynamic additional parameter values
                                  if ( formFieldProps.additionalParameters ) {
                                      dynamicFormFieldProps.additionalParameters = {};

                                      for ( const parameter of Object.keys( formFieldProps.additionalParameters ) as string[] ) {
                                          if ( layoutElementData.additionalParametersDataDescriptors?.[ parameter ] ) {
                                              const stringData : { [ dataDescriptorAlias : string ] : string } = {};

                                              for ( const parameterDataDescriptor of layoutElementData.additionalParametersDataDescriptors[ parameter ] as ObjectDataDescriptor[] ) {
                                                  const parameterValueType = dataUtils.getDataValueType(
                                                            parameterDataDescriptor,
                                                            schema,
                                                            dataStructure
                                                        ) as PropertyValueType,
                                                        parameterData      = dataStructure
                                                            ? dataUtils.getData(
                                                                parameterDataDescriptor,
                                                                dataStructure,
                                                                dataContext,
                                                                rootItemId
                                                            )?.[ 0 ]
                                                            : undefined,
                                                        parameterDataValue = parameterData
                                                            ? dataUtils.getValueFromData(
                                                                parameterDataDescriptor,
                                                                parameterData as Parameters<typeof dataUtils.getValueFromData>[ 1 ]
                                                            ) as AttributeValue
                                                            : undefined;

                                                  if ( parameterDataValue && parameterValueType ) {
                                                      stringData[ `${parameterDataDescriptor.dataObject.item.key}.${parameterDataDescriptor.attributePath}` ] = convertValueToString( parameterDataValue, parameterValueType );
                                                  }
                                              }

                                              dynamicFormFieldProps.additionalParameters[ parameter ] = __( formFieldProps.additionalParameters[ parameter ], stringData );
                                          }
                                          else {
                                              dynamicFormFieldProps.additionalParameters[ parameter ] = messageTemplateStringResolver( formFieldProps.additionalParameters[ parameter ], true );
                                          }
                                      }
                                  }

                                  // resolve dynamic filters values
                                  if ( formFieldProps.filters ) {
                                      dynamicFormFieldProps.filters = '';

                                      if ( layoutElementData.additionalFiltersDataDescriptors ) {
                                          const stringData : { [ dataDescriptorAlias : string ] : string } = {};

                                          for ( const parameterDataDescriptor of layoutElementData.additionalFiltersDataDescriptors as ObjectDataDescriptor[] ) {
                                              const parameterValueType = dataUtils.getDataValueType(
                                                        parameterDataDescriptor,
                                                        schema,
                                                        dataStructure
                                                    ) as PropertyValueType,
                                                    parameterData      = dataStructure
                                                        ? dataUtils.getData(
                                                            parameterDataDescriptor,
                                                            dataStructure,
                                                            dataContext,
                                                            rootItemId
                                                        )?.[ 0 ]
                                                        : undefined,
                                                    parameterDataValue = parameterData
                                                        ? dataUtils.getValueFromData(
                                                            parameterDataDescriptor,
                                                            parameterData as Parameters<typeof dataUtils.getValueFromData>[ 1 ]
                                                        ) as AttributeValue
                                                        : undefined;

                                              if ( parameterDataValue && parameterValueType ) {
                                                  stringData[ `${parameterDataDescriptor.dataObject.item.key}.${parameterDataDescriptor.attributePath}` ] = convertValueToString( parameterDataValue, parameterValueType );
                                              }
                                          }

                                          dynamicFormFieldProps.filters = __( formFieldProps.filters, stringData );
                                      }
                                      else {
                                          dynamicFormFieldProps.filters = messageTemplateStringResolver( formFieldProps.filters, true ) ?? formFieldProps.filters;
                                      }
                                  }

                                  // resolve dynamic fixFilters values
                                  if ( formFieldProps.fixFilters ) {
                                      dynamicFormFieldProps.fixFilters = '';

                                      if ( layoutElementData.additionalFixFiltersDataDescriptors ) {
                                          const stringData : { [ dataDescriptorAlias : string ] : string } = {};

                                          for ( const parameterDataDescriptor of layoutElementData.additionalFixFiltersDataDescriptors as ObjectDataDescriptor[] ) {
                                              const parameterValueType = dataUtils.getDataValueType(
                                                        parameterDataDescriptor,
                                                        schema,
                                                        dataStructure
                                                    ) as PropertyValueType,
                                                    parameterData      = dataStructure
                                                        ? dataUtils.getData(
                                                            parameterDataDescriptor,
                                                            dataStructure,
                                                            dataContext,
                                                            rootItemId
                                                        )?.[ 0 ]
                                                        : undefined,
                                                    parameterDataValue = parameterData
                                                        ? dataUtils.getValueFromData(
                                                            parameterDataDescriptor,
                                                            parameterData as Parameters<typeof dataUtils.getValueFromData>[ 1 ]
                                                        ) as AttributeValue
                                                        : undefined;

                                              if ( parameterDataValue && parameterValueType ) {
                                                  stringData[ `${parameterDataDescriptor.dataObject.item.key}.${parameterDataDescriptor.attributePath}` ] = convertValueToString( parameterDataValue, parameterValueType );
                                              }
                                          }

                                          dynamicFormFieldProps.fixFilters = __( formFieldProps.fixFilters, stringData );
                                      }
                                      else {
                                          dynamicFormFieldProps.fixFilters = messageTemplateStringResolver( formFieldProps.fixFilters, true ) ?? formFieldProps.fixFilters;
                                      }
                                  }

                                  // style
                                  if ( formFieldLayoutElementData.styles?.style && !layoutElementData.hasValueSet ) {
                                      const dynStyle = resolveStyle( formFieldLayoutElementData.styles.style, dataDescriptorsValue ?? {} );

                                      for ( const key of Object.keys( dynStyle ) as string[] ) {
                                          const camelCaseKey = _.camelCase( key );

                                          if ( key !== camelCaseKey ) {
                                              dynStyle[ camelCaseKey ] = dynStyle[ key ];
                                              delete dynStyle[ key ];
                                          }
                                      }

                                      dynamicFormFieldProps.inputClassNames = dynamicFormFieldProps.inputClassNames.concat( resolveStyleToClassNames( formFieldLayoutElementData.styles.style, dataDescriptorsValue ?? {}, undefined, undefined, true ) );
                                      dynamicFormFieldProps.style = {
                                          ...dynamicFormFieldProps.style,
                                          ...dynStyle
                                      };
                                  }

                                  // labelStyle
                                  if ( formFieldLayoutElementData.styles?.labelStyle ) {
                                      dynamicFormFieldProps.labelClassNames = dynamicFormFieldProps.labelClassNames.concat( resolveStyleToClassNames( formFieldLayoutElementData.styles.labelStyle, dataDescriptorsValue ?? {} ) );
                                  }

                                  // copy styles from input into display
                                  dynamicFormFieldProps.displayClassNames = dynamicFormFieldProps.inputClassNames.concat( dynamicFormFieldProps.displayClassNames );
                                  dynamicFormFieldProps.displayStyle = { ...dynamicFormFieldProps.inputStyle, ...dynamicFormFieldProps.displayStyle };

                                  // TODO: implement
                                  // errors
                                  /*if ( setValueErrors ) {
                                                dynamicFormFieldProps.errorMessage += setValueErrors.join( ' ' );
                                            }
                                            if ( errors ) {
                                                if ( dynamicFormFieldProps.errorMessage ) {
                                                    dynamicFormFieldProps.errorMessage += ' ';
                                                }
                                                dynamicFormFieldProps.errorMessage += errors.join( ' ' );
                                            }
                                            if ( !dynamicFormFieldProps.errorMessage ) {
                                                delete dynamicFormFieldProps.errorMessage;
                                            }*/

                                  // TODO: implement
                                  // autofocus first (erroneous if not new) field
                                  /*if (
                                                (
                                                    ( rootItemId ?? 0 ) < 1
                                                    || !elusiveData?.layoutElementMappings?.errors
                                                    || errors
                                                )
                                                && (
                                                    dynamicFormFieldProps.tabIndex
                                                    && dynamicFormFieldProps.tabIndex > 0
                                                    && (
                                                        !this.autoFocusField
                                                        || dynamicFormFieldProps.tabIndex < this.autoFocusField.tabIndex
                                                    )
                                                )
                                            ) {
                                                this.autoFocusField = {
                                                    tabIndex         : dynamicFormFieldProps.tabIndex,
                                                    layoutElementName: formFieldProps.name
                                                };
                                            }*/

                                  // get dataValueType, data and metaModel mapping and checks
                                  if ( dataDescriptor ) {
                                      const dataValueType = ( dataUtils.getDataValueType( dataDescriptor, dataSchema, dataStructure ) ?? 'STRING' ) as PropertyValueType;

                                      // check metaModel exists
                                      if ( !dataDescriptorMetaModel?.metaModels?.length ) {
                                          throw ( new Error( `'${layoutElementData.key}' (${layoutElementData.name}): couldn't get metaModel` ) );
                                      }

                                      // check type mismatch between data type and possible field types
                                      if (
                                          type !== 'BUTTON' && type !== 'CALENDAR' && type !== 'CHART' && type !== 'GRAPH' && type !== 'MAP' && type !== 'OVERVIEW' && type !== 'STATIC' && type !== 'VARIABLE'
                                          && dataDescriptorMetaModel.type !== 'enumProperty' && dataDescriptorMetaModel.type !== 'enumReferenceProperty' && dataDescriptorMetaModel.type !== 'reference'
                                          && !fieldUtils.checkValueTypeForFieldType( dataValueType, type )
                                      ) {
                                          throw ( new Error( `'${layoutElementData.key}' (${layoutElementData.name}): ${fieldTypeToItemTypeMapping[ type ]} is not allowed for data type ${dataValueType} (allowed: ${fieldUtils.getAllowedFieldTypesForValueType( dataValueType )?.join( ', ' )})` ) );
                                      }

                                      // check metaModels count
                                      else if ( dataDescriptorMetaModel.metaModels.length > 1 ) {
                                          throw ( new Error( `'${layoutElementData.key}' (${layoutElementData.name}): more than one matching metaModel` ) );
                                      }

                                      // enumerated (reference) property requires EnumField check
                                      if (
                                          (
                                              dataDescriptorMetaModel.type === 'enumProperty'
                                              || dataDescriptorMetaModel.type === 'enumReferenceProperty'
                                          )
                                          && type !== 'ENUM'
                                      ) {
                                          throw ( new Error( `'${layoutElementData.key}' (${layoutElementData.name}): wrong field type '${type}' for enumerated property` ) );
                                      }
                                  }
                                  else if (
                                      type !== 'BUTTON'
                                      && type !== 'CALENDAR'
                                      && type !== 'CHART'
                                      && type !== 'GRAPH'
                                      && type !== 'MAP'
                                      && type !== 'OVERVIEW'
                                      && type !== 'STATIC'
                                      && type !== 'VARIABLE'
                                  ) {
                                      throw ( new Error( `'${layoutElementData.key}' (${layoutElementData.name}): no DataObject` ) );
                                  }

                                  const fieldType       = type === 'VARIABLE' ? formFieldLayoutElementData.variableType : type,
                                        cacheParameters = {
                                            ...formFieldProps,
                                            ...dynamicFormFieldProps
                                        };

                                  retVal.isDirty = retVal.isDirty || JSON.stringify( dynamicFormFieldProps.value ?? '' ) !== JSON.stringify( dynamicFormFieldProps.originalValue ?? '' );
                                  retVal.isInvalid = retVal.isInvalid || !!dynamicFormFieldProps.errorMessage;

                                  if ( layoutElementData.hasValueSet ) {
                                      cacheParameters.allowNone = !required;
                                      cacheParameters.valueStyle = formFieldLayoutElementData.styles?.style;
                                      cacheParameters.valuesForValueStyle = undefined;
                                      cacheParameters.dataDescriptorAlias = dataDescriptorAlias;
                                  }
                                  else if ( fieldType === 'FILE' ) {
                                      cacheParameters.dataContext = dataContext;
                                  }
                                  else if ( fieldType === 'REFERENCE' ) {
                                      cacheParameters.dataContext = dataContext;
                                      cacheParameters.formUid = uid;
                                      cacheParameters.tabIndexOffset = tabIndexOffset + ( maxTabIndex * tabIndexStep );
                                      cacheParameters.additionalAddItemFormProps = {
                                          formParentItemId: rootItemId,
                                          treeRootItemId,
                                          treeParentItemId
                                      };
                                  }
                                  else if ( fieldType === 'STATIC' ) {
                                      cacheParameters.format = formFieldLayoutElementData.format;
                                      cacheParameters.content = formFieldProps.content ?? ( typeof dynamicFormFieldProps.value === 'string' ? dynamicFormFieldProps.value : dynamicFormFieldProps.displayValue );
                                      cacheParameters.rawContent = formFieldProps.content ?? dynamicFormFieldProps.value ? String( dynamicFormFieldProps.value ) : undefined;
                                  }

                                  const cacheParameterDiff = objectUtils.getDiffKeys( newFieldCache[ layoutElementId ]?.cacheParameters ?? {}, cacheParameters );

                                  if ( cacheParameterDiff.length ) {
                                      let field : React.ReactNode;

                                      if ( layoutElementData.hasValueSet ) {
                                          field = <ValueSetField
                                              {...cacheParameters}
                                              {...formFieldListeners}
                                          />;
                                      }
                                      else {
                                          switch ( fieldType ) {
                                              case 'BOOLEAN':
                                                  field = <BooleanField
                                                      {...cacheParameters}
                                                      {...formFieldListeners}
                                                  />;
                                                  break;

                                              case 'BUTTON':
                                                  field = <ButtonField
                                                      {...cacheParameters}
                                                      {...formFieldListeners}
                                                  />;
                                                  break;

                                              case 'CALENDAR':
                                                  retVal.content.push(
                                                      <CalendarView
                                                          {...cacheParameters}
                                                          {...formFieldListeners}
                                                          dataContext={dataContext}
                                                      />
                                                  );
                                                  break;

                                              case 'CHART':
                                                  retVal.content.push(
                                                      <ChartView
                                                          {...cacheParameters}
                                                          {...formFieldListeners}
                                                          dataContext={dataContext}
                                                      />
                                                  );
                                                  break;

                                              case 'COLOR':
                                                  field = <ColorField
                                                      {...cacheParameters}
                                                      {...formFieldListeners}
                                                  />;
                                                  break;

                                              case 'COORDINATE':
                                                  field = <CoordinateField
                                                      {...cacheParameters}
                                                      {...formFieldListeners}
                                                  />;
                                                  break;

                                              case 'CURRENCY':
                                                  field = <CurrencyField
                                                      {...cacheParameters}
                                                      {...formFieldListeners}
                                                  />;
                                                  break;

                                              case 'DATE':
                                                  field = <DateField
                                                      {...cacheParameters}
                                                      {...formFieldListeners}
                                                  />;
                                                  break;

                                              case 'DOUBLE':
                                                  field = <DoubleField
                                                      {...cacheParameters}
                                                      {...formFieldListeners}
                                                  />;
                                                  break;

                                              case 'DURATION':
                                                  field = <DurationField
                                                      {...cacheParameters}
                                                      {...formFieldListeners}
                                                  />;
                                                  break;

                                              case 'EMAIL':
                                                  field = <EmailField
                                                      {...cacheParameters}
                                                      {...formFieldListeners}
                                                  />;
                                                  break;

                                              case 'GRAPH':
                                                  retVal.content.push(
                                                      <GraphView
                                                          {...cacheParameters}
                                                          {...formFieldListeners}
                                                          dataContext={dataContext}
                                                      />
                                                  );
                                                  break;

                                              case 'IBAN':
                                                  field = <IbanField
                                                      {...cacheParameters}
                                                      {...formFieldListeners}
                                                  />;
                                                  break;

                                              case 'IMAGEDATA':
                                                  field = <ImageDataField
                                                      {...cacheParameters}
                                                      {...formFieldListeners}
                                                  />;
                                                  break;

                                              case 'INTEGER':
                                                  field = <IntegerField
                                                      {...cacheParameters}
                                                      {...formFieldListeners}
                                                  />;
                                                  break;

                                              case 'JSON':
                                                  retVal.content.push(
                                                      <JsonField
                                                          {...cacheParameters}
                                                          {...formFieldListeners}
                                                      />
                                                  );
                                                  break;

                                              case 'LINK':
                                                  field = <LinkField
                                                      {...cacheParameters}
                                                      {...formFieldListeners}
                                                  />;
                                                  break;

                                              case 'MAP':
                                                  retVal.content.push(
                                                      <MapView
                                                          {...cacheParameters}
                                                          {...formFieldListeners}
                                                          dataContext={dataContext}
                                                      />
                                                  );
                                                  break;

                                              case 'OVERVIEW':
                                                  retVal.content.push(
                                                      <OverviewView
                                                          {...cacheParameters}
                                                          {...formFieldListeners}
                                                          dataContext={dataContext}
                                                      />
                                                  );
                                                  break;

                                              case 'PASSWORD':
                                                  field = <PasswordField
                                                      {...cacheParameters}
                                                      {...formFieldListeners}
                                                  />;
                                                  break;

                                              case 'PHONE':
                                                  field = <PhoneField
                                                      {...cacheParameters}
                                                      {...formFieldListeners}
                                                  />;
                                                  break;

                                              case 'STATIC':
                                                  field = <StaticContentField
                                                      {...cacheParameters}
                                                      {...formFieldListeners}
                                                  />;
                                                  break;

                                              case 'STRING':
                                                  field = <StringField
                                                      {...cacheParameters}
                                                      {...formFieldListeners}
                                                  />;
                                                  break;

                                              case 'TEXT':
                                                  field = <TextField
                                                      {...cacheParameters}
                                                      {...formFieldListeners}
                                                  />;
                                                  break;

                                              case 'TIME':
                                                  field = <TimeField
                                                      {...cacheParameters}
                                                      {...formFieldListeners}
                                                  />;
                                                  break;

                                              case 'TIMESTAMP':
                                                  field = <TimestampField
                                                      {...cacheParameters}
                                                      {...formFieldListeners}
                                                  />;
                                                  break;

                                              case 'ENUM':
                                                  field = <EnumField
                                                      {...cacheParameters}
                                                      {...formFieldListeners}
                                                  />;
                                                  break;

                                              case 'REFERENCE':
                                                  field = <ReferenceField
                                                      {...cacheParameters}
                                                      {...formFieldListeners}
                                                      rootItemId={parent.parentItemId ?? formFieldProps.rootItemId}
                                                      isRepeated={!!parent.dataDescriptorAlias}
                                                      formUid={uid}
                                                      /*
                                                       * TODO: implement
                                                       *
                                                      onGetData={( getDataConfig : ReferenceFieldGetDataConfig ) => {
                                                          return onGetDataAction( layoutElementId, getDataConfig ) as ReturnType<Required<ReferenceFieldComponentProps>[ 'onGetData' ]>;
                                                      }}
                                                       *
                                                      onCreateNewItemForReference={( item, event ) => onReferenceAction( layoutElementId, 'newReferencedItem', item, undefined, event )}
                                                      */
                                                      additionalAddItemFormProps={{
                                                          formParentItemId: rootItemId,
                                                          treeRootItemId,
                                                          treeParentItemId
                                                      }}
                                                      tabIndexOffset={tabIndexOffset + ( maxTabIndex * tabIndexStep )}
                                                  />;
                                                  break;

                                              case 'FILE':
                                                  field = <FileField
                                                      {...cacheParameters}
                                                      {...formFieldListeners}
                                                      rootItemId={parent.parentItemId ?? formFieldProps.rootItemId}
                                                  />;
                                                  break;

                                              default:
                                                  console.error( `FieldType '${fieldType}' not implemented: %o`, layoutElementData );
                                                  throw ( new Error( `'${formFieldLayoutElementData.key}' (${formFieldLayoutElementData.name}): type '${layoutElementStructure.type}' not implemented yet` ) );
                                          }
                                      }

                                      newFieldCache[ layoutElementId ] = {
                                          cacheParameters,
                                          field
                                      };

                                      fieldCacheChanged = true;
                                  }

                                  retVal.content.push( newFieldCache[ layoutElementId ].field );
                              }
                              catch ( error ) {
                                  let errorMessage : string = 'unknown error';

                                  if ( debug ) {
                                      console.error( error );
                                  }

                                  if ( typeof error === 'string' ) {
                                      errorMessage = error;
                                  }
                                  else if ( error instanceof Error ) {
                                      errorMessage = error.message;
                                  }

                                  // strip field
                                  errorMessage = errorMessage.substring( errorMessage.indexOf( '): ' ) + 3 );

                                  retVal.content.push(
                                      <StaticContentField
                                          {...formFieldProps}
                                          {...dynamicFormFieldProps}
                                          alignment="left"
                                          content={errorMessage}
                                          style={elementStyle}
                                          inputClassNames={[ 'yn-dummy-field', 'yn-error' ]}
                                          id={`leid_${layoutElementId}`}
                                          pt={mode === 'design'
                                              ? {
                                                  fieldWrapper: {
                                                      root: {
                                                          [ 'data-layoutelementid' ]    : String( layoutElementId ),
                                                          [ 'data-layoutelementlabel' ] : `${layoutElementData.name ? layoutElementData.name : layoutElementData.key}`,
                                                          [ 'data-datadescriptoralias' ]: `${dataDescriptorAlias ?? ''}`,
                                                          draggable                     : !readOnly,
                                                          onMouseOver                   : onMouseEnter.bind( null, layoutElementId ),
                                                          onMouseLeave                  : onMouseLeave.bind( null, layoutElementId ),
                                                          onClick                       : onClick.bind( null, layoutElementId ),
                                                          onDragStart                   : readOnly
                                                              ? undefined
                                                              : onDragStart.bind( null, layoutElementId )
                                                      }
                                                  }
                                              }
                                              : {}
                                          }
                                      />
                                  );
                              }
                          }
                      }

                      /*
                                // render horizontal placeholder - but only for full width containers
                                else if ( mode === 'design' && ( layoutElementStructure.type === 'CONTAINER' || layoutElementStructure.type === 'TABCONTAINER' ) && elementStyle.width === '100%' ) {
                                    retVal.content.push(
                                        <StaticContentField
                                            uid={uid}
                                            labelVisibility="hidden"
                                            infoTextLayout="hidden"
                                            content=""
                                            style={{}}
                                            classNames={[ 'yn-hidden-placeholder-horizontal' ]}
                                            id={`leid_${layoutElementId}`}
                                            pt={{
                                                fieldWrapper: {
                                                    root: {
                                                        [ 'data-layoutelementid' ]    : String( layoutElementId ),
                                                        [ 'data-layoutelementlabel' ] : `${layoutElementData.name ? layoutElementData.name : layoutElementData.key}`,
                                                        [ 'data-datadescriptoralias' ]: `${( layoutElement as FormFieldData ).dataDescriptorAlias ?? ''}`,
                                                        draggable                     : !readOnly,
                                                        onMouseOver                   : onMouseEnter.bind( null, layoutElementId ),
                                                        onMouseLeave                  : onMouseLeave.bind( null, layoutElementId ),
                                                        onClick                       : onClick.bind( null, layoutElementId ),
                                                        onDragStart                   : readOnly
                                                            ? undefined
                                                            : onDragStart.bind( null, layoutElementId )
                                                    }
                                                }
                                            }}
                                        />
                                    );
                                }

                                // vertical placeholder
                                else if ( mode === 'design' ) {
                                    if ( isPreviousElementFullWidth ) {
                                        retVal.content.push( <div className="yn-break"></div> );
                                    }

                                    retVal.content.push(
                                        <StaticContentField
                                            uid={uid}
                                            labelVisibility="hidden"
                                            infoTextLayout="hidden"
                                            content=""
                                            style={{}}
                                            classNames={[ 'yn-hidden-placeholder-vertical' ]}
                                            id={`leid_${layoutElementId}`}
                                            pt={{
                                                fieldWrapper: {
                                                    root: {
                                                        [ 'data-layoutelementid' ]    : String( layoutElementId ),
                                                        [ 'data-layoutelementlabel' ] : `${layoutElementData.name ? layoutElementData.name : layoutElementData.key}`,
                                                        [ 'data-datadescriptoralias' ]: `${( layoutElement as FormFieldData ).dataDescriptorAlias ?? ''}`,
                                                        draggable                     : !readOnly,
                                                        onMouseOver                   : onMouseEnter.bind( null, layoutElementId ),
                                                        onMouseLeave                  : onMouseLeave.bind( null, layoutElementId ),
                                                        onClick                       : onClick.bind( null, layoutElementId ),
                                                        onDragStart                   : readOnly
                                                            ? undefined
                                                            : onDragStart.bind( null, layoutElementId )
                                                    }
                                                }
                                            }}
                                        />
                                    );
                                }

                                isPreviousElementFullWidth = elementStyle.width === '100%';
                                */
                  }

                  if ( fieldCacheChanged ) {
                      fieldCache = newFieldCache;
                  }
              }

              if ( debug ) {
                  console.log( `%c${Math.round( 1000 * ( performance.now() - innerStartTime ) ) / 1000} ms%c`.asLogLine( 100 ), `color: #${debugColorScheme.cacheFunction}; font-weight: bold;`, 'font-weight: unset;' );
                  console.groupEnd();
              }

              if ( parent.type !== 'tabContainer' && !repeatingContainer ) {
                  retVal.content = [
                      <div key={`container_${parent.id}${parent.idSuffix}`} className="yn-form-container-content">
                          {retVal.content}
                      </div>
                  ];
              }

              return retVal;
          },
          content                                              = useMemo(
              () => {
                  const classNames = [
                      'yn-form'
                  ];

                  let retVal = <></>;

                  if ( mode === 'design' ) {
                      classNames.push( 'yn-form-designer' );
                  }
                  if ( rootItemFormState === 'ERROR' ) {
                      classNames.push( 'yn-form-designer-error' );
                  }
                  else if ( rootItemFormState === 'DIRTY' ) {
                      classNames.push( 'yn-form-designer-dirty' );
                  }
                  else if ( selectedLayoutElementId === configRootItemId ) {
                      classNames.push( 'yn-form-designer-selected' );
                  }

                  if ( layoutStructure && layoutElementsProp?.[ layoutStructure.layoutElementId ] ) {
                      initialRendered.current = true;

                      /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
                      retVal = <div
                          className={mode === 'design' ? 'yn-flex-layout-parent yn-formdesigner-wrapper' : 'yn-content-panel-container yn-formview-panel'}
                          onScroll={debouncedGetScrollPosition}
                          onClick={mode === 'design' ? onFormClick : undefined}
                      >
                          <div
                              id={`${uid}_form`}
                              className={classNames.join( ' ' )}
                              style={layoutElementsProp[ layoutStructure.layoutElementId ].elementStyle}
                              onMouseLeave={mode === 'design' ? onFormLeave : undefined}
                          >
                              {buildContent(
                                  {
                                      type    : 'form',
                                      id      : layoutStructure.layoutElementId,
                                      gridSize: layoutElementsProp[ layoutStructure.layoutElementId ].gridSize
                                  },
                                  layoutStructure
                              ).content}
                          </div>
                      </div>;
                  }

                  return retVal;
              },
              [
                  activeTabContainerIndexes,
                  configDataContext,
                  configRootItemId,
                  dataDescriptors,
                  dataDescriptorsData,
                  dataDescriptorsEditValue,
                  dataDescriptorsMetaModel,
                  dataDescriptorsValue,
                  dataSchema,
                  dataStructure,
                  layoutElements,
                  layoutElementsProp,
                  layoutStructure,
                  messageCatalogTimestamp,
                  mode,
                  rootItemFormState,
                  selectedLayoutElementId,
                  showHiddenLayoutElements,
                  uid,
                  user,
                  variables,
                  variablesEditValues
              ]
          );

    currentScrollTop.current = scrollTop;
    currentScrollLeft.current = scrollLeft;


    // TODO:
    //  performance optimization


    // expose methods via reference
    useImperativeHandle(
        forwardedRef,
        () => ( {
            dataStructure,
            layoutElements,
            repeatedDataObjects,
            layoutElementsProps         : lastParams.current?.layoutElementsProp,
            layoutElementsInheritedProps: lastParams.current?.layoutElementsInheritedProps,
            hasHiddenLayoutElements     : lastParams.current?.hasHiddenLayoutElements,
            clearRootItemData,
            markLayoutElementsForAlias,
            startDrag
        } )
    );

    // set scroll position (on initial render or change)
    useLayoutEffect(
        () => {
            setScrollPosition();
        },
        [
            scrollLeft,
            scrollTop,
            initialRendered.current
        ]
    );

    // compute internal data / load data (on update)
    useEffect(
        () => {
            if ( !isLoading.current ) {
                const innerStartTime                 = debug ? performance.now() : 0,
                      recompute                      = elusiveData?.layoutStructure === undefined,
                      elusiveDataToSet : ElusiveData = {};

                // dataStructure
                if (
                    recompute
                    || configDataContext !== lastParams.current?.configDataContext
                    || globalConfigDataVersions !== lastParams.current?.globalConfigDataVersions
                    || formItem !== lastParams.current?.formItem
                ) {
                    const newDataStructure = buildDataStructure();

                    if ( JSON.stringify( newDataStructure ?? {} ) !== JSON.stringify( dataStructure ?? {} ) ) {
                        dataStructure = newDataStructure;
                        elusiveDataToSet.dataStructure = newDataStructure;
                    }
                }

                // load data
                if (
                    (
                        JSON.stringify( dataStructure ?? '' ) !== JSON.stringify( lastParams.current?.dataStructure ?? '' )
                        || rootItemId !== lastParams.current?.rootItemId
                        || rootItemKey !== lastParams.current?.rootItemKey
                        || rootItemType !== lastParams.current?.rootItemType
                    )
                    &&
                    (
                        (
                            rootItemId !== undefined
                            && rootItemId > 0
                            //&& !JSON.parse( globalDataVersions ).items?.[ rootItemId ]
                            //&& !JSON.parse( globalDataVersions ).items?.[ String( rootItemId ) ]
                        )
                        || (
                            rootItemId === undefined
                            && rootItemType !== undefined
                            && rootItemKey !== undefined
                        )
                    )
                ) {
                    void load();
                }

                // layoutStructure, layoutElements, dataDescriptors
                if (
                    recompute
                    || dataStructure !== lastParams.current?.dataStructure
                    || configDataContext !== lastParams.current?.configDataContext
                    || globalConfigDataVersions !== lastParams.current?.globalConfigDataVersions
                    || formItem !== lastParams.current?.formItem
                ) {
                    const {
                              layoutStructure: newLayoutStructure,
                              layoutElements : newLayoutElements,
                              dataDescriptors: newDataDescriptors
                          } = buildLayoutStructureElementsAndDataDescriptors( dataStructure );

                    if ( JSON.stringify( newLayoutStructure ?? {} ) !== JSON.stringify( layoutStructure ?? {} ) ) {
                        layoutStructure = newLayoutStructure;
                        elusiveDataToSet.layoutStructure = newLayoutStructure;
                    }
                    if ( JSON.stringify( newLayoutElements ?? {} ) !== JSON.stringify( layoutElements ?? {} ) ) {
                        layoutElements = newLayoutElements;
                        elusiveDataToSet.layoutElements = newLayoutElements;
                    }
                    if ( JSON.stringify( newDataDescriptors ?? {} ) !== JSON.stringify( dataDescriptors ?? {} ) ) {
                        dataDescriptors = newDataDescriptors;
                        elusiveDataToSet.dataDescriptors = newDataDescriptors;
                    }
                }

                // dataDescriptorsMetaModel
                if (
                    recompute
                    || dataStructure !== lastParams.current?.dataStructure
                    || dataDescriptors !== lastParams.current?.dataDescriptors
                    || configDataContext !== lastParams.current?.configDataContext
                    || dataSchema !== lastParams.current?.dataSchema
                    || user !== lastParams.current?.user
                ) {
                    const newDataDescriptorsMetaModel = buildDataDescriptorsMetaModel( dataStructure, dataDescriptors );

                    if ( JSON.stringify( newDataDescriptorsMetaModel ?? {} ) !== JSON.stringify( dataDescriptorsMetaModel ?? {} ) ) {
                        dataDescriptorsMetaModel = newDataDescriptorsMetaModel;
                        elusiveDataToSet.dataDescriptorsMetaModel = newDataDescriptorsMetaModel;
                    }
                }

                // dataDescriptorsData
                if (
                    rootItemId
                    && (
                        recompute
                        || dataDescriptorsMetaModel !== lastParams.current?.dataDescriptorsMetaModel
                        || rootItemId !== lastParams.current?.rootItemId
                        || dataContext !== lastParams.current?.dataContext
                        || dataSchema !== lastParams.current?.dataSchema
                        || uid !== lastParams.current?.uid
                        || globalDataVersions !== lastParams.current?.globalDataVersions
                    )
                ) {
                    const newDataDescriptorsData = buildDataDescriptorsData( dataStructure, dataDescriptors, dataDescriptorsMetaModel );

                    if ( JSON.stringify( newDataDescriptorsData ?? {} ) !== JSON.stringify( dataDescriptorsData ?? {} ) ) {
                        dataDescriptorsData = newDataDescriptorsData;
                        elusiveDataToSet.dataDescriptorsData = newDataDescriptorsData;
                    }
                }
                else if ( !rootItemId && elusiveData?.dataDescriptorsData ) {
                    elusiveDataToSet.dataDescriptorsData = undefined;
                }

                // values
                if (
                    recompute
                    || dataDescriptorsData !== lastParams.current?.dataDescriptorsData
                    || dataDescriptorsEditValue !== lastParams.current?.dataDescriptorsEditValue
                ) {
                    const newDataDescriptorsValue = buildDataDescriptorsValue( dataDescriptorsData, dataDescriptorsEditValue );

                    if ( JSON.stringify( newDataDescriptorsValue ?? {} ) !== JSON.stringify( dataDescriptorsValue ?? {} ) ) {
                        dataDescriptorsValue = newDataDescriptorsValue;
                        elusiveDataToSet.dataDescriptorsValue = newDataDescriptorsValue;
                    }
                }

                // layoutElementsProp
                if (
                    recompute
                    || dataStructure !== lastParams.current?.dataStructure
                    || dataDescriptors !== lastParams.current?.dataDescriptors
                    || layoutStructure !== lastParams.current?.layoutStructure
                    || layoutElements !== lastParams.current?.layoutElements
                    || dataDescriptorsMetaModel !== lastParams.current?.dataDescriptorsMetaModel
                    || configDataContext !== lastParams.current?.configDataContext
                    || configRootItemId !== lastParams.current?.configRootItemId
                    || dataSchema !== lastParams.current?.dataSchema
                    || uid !== lastParams.current?.uid
                ) {
                    const {
                              props              : newLayoutElementsProp,
                              inheritedProps     : newLayoutElementsInheritedProps,
                              repeatedDataObjects: newRepeatedDataObjects
                          } = buildLayoutElementsPropAndInheritedProps( dataStructure, layoutStructure, layoutElements, dataDescriptors, dataDescriptorsMetaModel, dataDescriptorsData );

                    if ( JSON.stringify( newLayoutElementsProp ?? {} ) !== JSON.stringify( layoutElementsProp ?? {} ) ) {
                        layoutElementsProp = newLayoutElementsProp;
                        elusiveDataToSet.layoutElementsProp = newLayoutElementsProp;
                    }
                    if ( JSON.stringify( newLayoutElementsInheritedProps ?? {} ) !== JSON.stringify( layoutElementsInheritedProps ?? {} ) ) {
                        layoutElementsInheritedProps = newLayoutElementsInheritedProps;
                        elusiveDataToSet.layoutElementsInheritedProps = newLayoutElementsInheritedProps;
                    }
                    if ( JSON.stringify( newRepeatedDataObjects ?? {} ) !== JSON.stringify( repeatedDataObjects ?? {} ) ) {
                        repeatedDataObjects = newRepeatedDataObjects;
                        elusiveDataToSet.repeatedDataObjects = newRepeatedDataObjects;
                    }
                }

                // fieldCache
                if (
                    recompute
                    || fieldCache !== lastParams.current?.fieldCache
                ) {
                    elusiveDataToSet.fieldCache = fieldCache;
                }

                if ( Object.keys( elusiveDataToSet ).length ) {
                    dispatch( setElusiveData( uid, elusiveDataToSet ) );
                    if ( debug ) {
                        additionalRenderTime.current = innerStartTime - performance.now();
                    }
                }

                lastParams.current = {
                    activeTabContainerIndexes,
                    configDataContext,
                    configRootItemId,
                    dataContext,
                    dataDescriptors,
                    dataDescriptorsData,
                    dataDescriptorsEditValue,
                    dataDescriptorsMetaModel,
                    dataDescriptorsValue,
                    dataSchema,
                    dataStructure,
                    fieldCache,
                    formItem,
                    globalConfigDataVersions,
                    globalDataVersions,
                    hasHiddenLayoutElements,
                    hoverLayoutElementId,
                    layoutElements,
                    layoutElementsInheritedProps,
                    layoutElementsProp,
                    layoutStructure,
                    messageCatalogTimestamp,
                    metaModelTimestamp,
                    onMarkFieldsForAlias,
                    onSelectLayoutElement,
                    repeatedDataObjects,
                    rootItemId,
                    rootItemKey,
                    rootItemType,
                    selectedLayoutElementId,
                    uid,
                    user
                };
            }
        }
    );


    // TODO:
    //  refactor to one listener
    //  - removing all markings first by using document.querySelectorAll( '.<className1>, .<className2>, ...' )
    //  - then set the appropriate classes
    //  ----------------------------------------------------------
    //  listeners
    //  - get formGridUnitWidth and remWidth (on update)
    //  - mark/unmark dropped layoutElement (on update)
    //  - mark/unmark dragged layoutElement (on update)
    //  - mark/unmark selected layoutElement (on update)


    // get formGridUnitWidth and remWidth (on update)
    useLayoutEffect(
        () => {
            const formElement    = document.getElementById( `${uid}_form` ),
                  computedStyles = formElement ? window.getComputedStyle( formElement ) : undefined;

            if ( computedStyles?.fontSize ) {
                const width    = Number( computedStyles.width.substring( 0, computedStyles.width.length - 2 ) ),
                      fontSize = Number( computedStyles.fontSize.substring( 0, computedStyles.fontSize.length - 2 ) ),
                      gridSize = layoutStructure && layoutElementsProp ? layoutElementsProp[ layoutStructure.layoutElementId ]?.gridSize ?? 0 : 0;

                if ( gridSize > 0 && width / gridSize !== formGridUnitWidth ) {
                    setFormGridUnitWidth( width / gridSize );
                }

                if ( fontSize !== remWidth ) {
                    setRemWidth( fontSize );
                }
            }
        }
    );

    // mark/unmark dropped layoutElement (on update)
    useLayoutEffect(
        () => {
            if ( droppedLayoutElementId.current ) {
                const element = document.getElementById( `leid_${droppedLayoutElementId.current}_fieldWrapper` ) ?? document.getElementById( `leid_${droppedLayoutElementId.current}` );

                if ( element ) {
                    const tabElement = document.getElementById( `leid_${droppedLayoutElementId.current}_tab` );

                    element.classList.add( 'yn-formdesigner-dropped' );

                    if ( tabElement ) {
                        tabElement.classList.add( 'yn-formdesigner-dropped' );
                    }

                    if ( droppedStyleTimeout.current ) {
                        clearTimeout( droppedStyleTimeout.current );
                    }

                    droppedStyleTimeout.current = setTimeout(
                        () => {
                            droppedLayoutElementId.current = undefined;
                            element.classList.remove( 'yn-formdesigner-dropped' );

                            if ( tabElement ) {
                                tabElement.classList.remove( 'yn-formdesigner-dropped' );
                            }
                        },
                        1600
                    );
                }
            }
        }
    );

    // mark/unmark dragged layoutElement (on update)
    useLayoutEffect(
        () => {
            // remove current
            const elements : HTMLCollectionOf<Element> | null | undefined = document.getElementsByClassName( 'yn-formdesigner-dragged' );

            let element : Element | null | undefined;

            if ( elements?.length ) {
                while ( elements[ 0 ] ) {
                    elements[ 0 ].classList.remove( 'yn-formdesigner-dragged' );
                }
            }

            // set current
            if ( dragInfo.current?.dragLayoutElementId ) {
                element = document.getElementById( `leid_${dragInfo.current.dragLayoutElementId}_fieldWrapper` ) ?? document.getElementById( `leid_${dragInfo.current.dragLayoutElementId}` );

                if ( element ) {
                    element.classList.add( 'yn-formdesigner-dragged' );
                }

                element = document.getElementById( `leid_${dragInfo.current.dragLayoutElementId}_tab` );

                if ( element ) {
                    element.classList.add( 'yn-formdesigner-dragged' );
                }

                // TODO: insertPositionMarker enable if required
                /*const targetElement    = document.getElementById( `leid_${dragInfo.current.lastDragOverLayoutElementId}_fieldWrapper` ) ?? document.getElementById( `leid_${dragInfo.current.lastDragOverLayoutElementId}` ) ?? document.getElementById( `leid_${dragInfo.current.lastDragOverLayoutElementId}_tab` ),
                              targetElementBox = targetElement?.getBoundingClientRect();

                        if ( targetElementBox ) {
                            switch ( dragInfo.current.position ) {
                                case 'after':
                                    dragInfo.current.insertMarkerElement.style.height = `${targetElementBox.height}px`;
                                    dragInfo.current.insertMarkerElement.style.top = `${targetElementBox.y}px`;
                                    dragInfo.current.insertMarkerElement.style.right = `calc( 100% - ${targetElementBox.x + targetElementBox.width}px )`;
                                    break;

                                case 'before':
                                    dragInfo.current.insertMarkerElement.style.height = `${targetElementBox.height}px`;
                                    dragInfo.current.insertMarkerElement.style.top = `${targetElementBox.y}px`;
                                    dragInfo.current.insertMarkerElement.style.left = `${targetElementBox.x}px`;
                                    break;

                                case 'insideAfter':
                                    dragInfo.current.insertMarkerElement.style.bottom = `calc( 100% - ${targetElementBox.y + targetElementBox.height}px )`;
                                    dragInfo.current.insertMarkerElement.style.right = `calc( 100% - ${targetElementBox.x + targetElementBox.width}px )`;
                                    break;

                                case 'insideBefore':
                                    dragInfo.current.insertMarkerElement.style.top = `${targetElementBox.y}px`;
                                    dragInfo.current.insertMarkerElement.style.left = `${targetElementBox.x}px`;
                                    break;
                            }
                        }*/
            }
        }
    );

    // mark/unmark selected layoutElement (onUpdate)
    useLayoutEffect(
        () => {
            // remove current
            const elements : HTMLCollectionOf<Element> | null | undefined = document.getElementsByClassName( 'yn-formdesigner-selected' );

            let element : Element | null | undefined;

            if ( elements?.length ) {
                while ( elements[ 0 ] ) {
                    elements[ 0 ].classList.remove( 'yn-formdesigner-selected' );
                }
            }

            // set current
            if ( selectedLayoutElementId ) {
                element = document.getElementById( `leid_${selectedLayoutElementId}_fieldWrapper` ) ?? document.getElementById( `leid_${selectedLayoutElementId}` );

                if ( element ) {
                    element.classList.add( 'yn-formdesigner-selected' );

                    element = document.getElementById( `leid_${selectedLayoutElementId}_tab` );

                    if ( element ) {
                        element.classList.add( 'yn-formdesigner-selected' );
                    }
                }
            }
        }
    );

    // mark/unmark aliasToMark
    useLayoutEffect(
        () => {
            // remove current
            let elements : HTMLCollectionOf<Element> | NodeListOf<Element> | null | undefined = document.querySelectorAll( `.yn-form-designer#${uid}_form .yn-field-wrapper.yn-formdesigner-using-alias` ),
                index                                                                         = 0;

            if ( elements?.length ) {
                while ( elements[ index ] ) {
                    elements[ index++ ].classList.remove( 'yn-formdesigner-using-alias' );
                }
            }

            if ( aliasToMark ) {
                const selector : string[] = [];

                for ( const alias of aliasToMark ) {
                    selector.push( `.yn-form-designer#${uid}_form .yn-field-wrapper[data-datadescriptoralias^="${alias}"]` );
                }

                elements = document.querySelectorAll( selector.join( ', ' ) );
                index = 0;

                if ( elements?.length ) {
                    while ( elements[ index ] ) {
                        elements[ index++ ].classList.add( 'yn-formdesigner-using-alias' );
                    }
                }
            }
        },
        [
            aliasToMark
        ]
    );

    // mark/unmark hovered layoutElement
    useLayoutEffect(
        () => {
            // remove current
            let elements : HTMLCollectionOf<Element> | null | undefined = document.getElementsByClassName( 'yn-formdesigner-hover' ),
                element : Element | null | undefined,
                parentIndex : number,
                parentLayoutElement : FormLayoutStructure | ContainerLayoutStructure | TabContainerLayoutStructure | undefined;

            if ( elements?.length ) {
                while ( elements[ 0 ] ) {
                    elements[ 0 ].classList.remove( 'yn-formdesigner-hover' );
                }
            }

            parentIndex = 1;
            elements = document.getElementsByClassName( `yn-formdesigner-hover-parent-${parentIndex}` );
            while ( elements?.length ) {
                while ( elements[ 0 ] ) {
                    elements[ 0 ].classList.remove( `yn-formdesigner-hover-parent-${parentIndex}` );
                }

                parentIndex++;
                elements = document.getElementsByClassName( `yn-formdesigner-hover-parent-${parentIndex}` );
            }

            // set current
            if ( hoverLayoutElementId ) {
                element = document.getElementById( `leid_${hoverLayoutElementId}_fieldWrapper` ) ?? document.getElementById( `leid_${hoverLayoutElementId}` );


                if ( element ) {
                    element.classList.add( 'yn-formdesigner-hover' );

                    // TODO: add insert position marking if
                    //if ( dragInfo.current && dragInfo.current.lastDragOverLayoutElementId === hoverLayoutElementId && dragInfo.current.position ) {
                    //dragInfo.current.position
                    //}
                }

                element = document.getElementById( `leid_${hoverLayoutElementId}_tab` );

                if ( element ) {
                    element.classList.add( 'yn-formdesigner-hover' );
                }

                parentIndex = 1;
                parentLayoutElement = layoutStructure ? getParentLayoutStructureForLayoutElement( hoverLayoutElementId ?? 0, layoutStructure ) : undefined;

                while ( parentLayoutElement ) {
                    if ( parentLayoutElement.type !== 'FORM' ) {
                        element = document.getElementById( `leid_${parentLayoutElement.layoutElementId}` );

                        if ( element ) {
                            element.classList.add( `yn-formdesigner-hover-parent-${parentIndex}` );

                            element = document.getElementById( `leid_${parentLayoutElement}_tab` );

                            if ( element ) {
                                element.classList.add( `yn-formdesigner-hover-parent-${parentIndex}` );
                            }
                        }
                    }

                    parentIndex++;
                    parentLayoutElement = layoutStructure ? getParentLayoutStructureForLayoutElement( parentLayoutElement.layoutElementId, layoutStructure ) : undefined;
                }
            }
        },
        [
            hoverLayoutElementId,
            layoutStructure
        ]
    );

    if ( debug ) {
        console.log( `%crender - ${Math.round( 1000 * ( performance.now() - ( startTime + additionalRenderTime.current ) ) ) / 1000} ms%c`.asLogLine( 104 ), `color: #${debugColorScheme.render}; font-weight: bold;`, 'font-weight: unset;' );
        additionalRenderTime.current = 0;
    }

    return content;
} );

FormDesigner.defaultProps = defaultProps;
FormDesigner.displayName = 'FormDesigner';

export default FormDesigner;
